'use client';

import React, { useCallback, useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { EmailArchivePreviewProvider } from '@/components/email-viewer/EmailArchivePreviewProvider';
import { TimePilotI18nProviders } from '@/i18n/TimePilotI18nProviders';
import { TimePilotLoadingScreen } from '@/i18n/TimePilotLoadingScreen';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import { translateTimePilotMessage } from '@/i18n/timepilotCatalogs';
import { DEFAULT_APP_LOCALE, responseLanguageForLocale, type AppLocale } from '@/i18n/locales';
import { readStoredLocale, SyncLocale } from '@/i18n/LocaleProvider';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import NavigationBarHorizontal, { TimelineDropdownItem } from '@/components/navigation-bar-horizontal';
import Image from 'next/image';
import { Chat } from '@/components/chat';
import Account from '@/components/account';
import { Card, CardHeader, CardBody, CardFooter } from '@heroui/card';
import { Spinner } from '@heroui/spinner';
import {
  captureError,
  captureWarning,
  CheckBuildVersionViaApi,
  FetchWithVersionCheck,
  getClientTimeFromStr,
  getDateFromDateStr,
  extractPathAfterBulk,
  getMediaType,
  resolvePreferredMediaDisplayForSource,
  recordStat,
  filterSources,
  filterMedia,
  getTokenInfo,
  getUserEmailForHeaders,
  getUsername,
  getCaseDisplayName,
  getCaseInternalName,
  toProvenanceLookupPath,
  resolveTimelineMediaPersistUrl,
  isInlineJsonCitationDisplayable,
  isArchiveMemberSearchablePath,
  trackEvent,
  getFileInfoFromServer,
  getFileInfoBatchFromServer,
} from '@/shared/functions';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import { gatewayFetch, getGatewaySession } from '@/lib/gateway-fetch';
import { describeErrorForOps, reportOpsError, reportOpsWarn } from '@/lib/ops-log';
import { secureRandomIdSuffix } from '@/lib/secure-random';
import { edgeLambdaKeys, fetchIntercomToken, postThreatReport } from '@/shared/cors-edge-lambda-api';
import { fetchProvenanceFilesPage, type CaseSummary } from '@/shared/cors-provenance-api';
import {
  caseFolderNameOf,
  findCaseSummary,
  isAllCasesSelection,
  resolveCaseFolderName,
} from '@/shared/case-summary';
import {
  caseFolderNameBody,
  caseTimelineTag,
  composeChatKey,
} from '@/hooks/useCaseFolderName';
import { canShowCaseManagementNav } from '@/shared/case-management-nav-access';
import {
  mergeThreatReportSourceLists,
  partitionThreatReportSources,
  sourceModifiedAtFromGetSourcesResponse,
  threatReportSourceListsFromGetSourcesResponse,
  type ThreatReportSourceLists,
  type ThreatReportSourcesError,
} from '@/shared/threat-report-sources';
import {
  createExtractionJob,
  deleteExtractionJob,
  extractEvents,
  getExtractionJobStatus,
  queryExtractionJobs,
} from '@/shared/cors-entity-api';
import { dedupeExtractionJobs, upsertExtractionJob } from '@/shared/extraction-jobs';
import { casesListQueryOptions } from '@/hooks/useCasesList';
import { getTenantIdFromToken } from '@/shared/csm-utils';
import {
  AssistantApiError,
  fetchChatHistory,
  fetchChatSessionsFromArchive,
  postAssistantChat,
  swapChatSession,
} from '@/shared/cors-assistant-api';
import {
  archiveHasExpectedTurn,
  sortChatSessionsByLatestQuery,
  type ChatSessionRow,
  type RefreshChatHistorySessionsOptions,
} from '@/shared/chat-archive-sessions';
import { buildAssistantChatScopeFields } from '@/shared/build-assistant-chat-scope-fields';
import {
  createTimeline,
  createTimelineEvent,
  deleteTimeline as deleteTimelineDirect,
  deleteTimelineEvent,
  getTimelineWriteCount,
  patchTimeline as patchTimelineDirect,
  queryTimelines,
  reorderTimelineEvents,
  updateTimelineEvent,
} from '@/shared/cors-timeline-api';
import { mapTimelineEventRecordToSlide } from '@/shared/timelineEventSlide';
import { maximizedViewerConfirmsCapture } from '@/shared/captureRegion';
import {
  getLocallyHiddenTimelineEventIds,
  isTimelineCurationEnabled,
  resolveHideFromTimeline,
  setLocalTimelineEventHidden,
} from '@/shared/timeline-visibility';
import {
  SpinnerGapIcon
} from '@phosphor-icons/react';
import {
  useWindowSize,
  dedupeSources,
  getDisplayDateAndTime,
  getSortedSlides,
  stringifyLocation,
  getDescriptionFromAnswerText,
  dedupeMediaSources,
  entityEventsToSlides,
  applyChatPlayableMediaToSlides,
  autogeneratedTimelineEventsToSlides,
  isAutogeneratedTimelineAnswer,
  parseAutogeneratedTimelineEvents,
  resolveAutogeneratedTimelineSlides,
  resolveTenantId,
  resolveCognitoUsername,
  legacyCognitoLoginFromClaimsSub,
} from '@/shared/clientFunctions';
import {
  resolveMediaModeration,
  resolveTimelineMediaModeration,
} from '@/shared/timelineMediaModeration';
import {
  TINY_VIEWPORT_WIDTH,
  THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED,
  SMALL_VIEWPORT_WIDTH,
  ENRICHMENT_POLL_INTERVAL_MS,
  ENRICHMENT_POLL_TIMEOUT_MS,
  isEnrichmentHistoryPending,
  isDisplayableMediaType,
  TIMELINE_TITLE_MAX_LENGTH,
  THREAT_REPORT_CREATORS_FLAG_CAMEL,
} from '@/shared/constants';
import { isJsonMediaEnabled } from '@/shared/media-player-transition';
import {
  applyActiveFeatureFlags,
  isSimonSaysEnabled,
  selectFeatureFlagSource,
  useTaiFeatureFlags,
} from '@/lib/tai-feature-flags';
import {
  LDMultiKindContext,
  useLDClient,
  useFlags
} from 'launchdarkly-react-client-sdk';
import {
  AuthContext,
  AuthContextType,
  CsamCredentialsContext,
  FeaturesContext,
  FeaturesContextType,
  StatsContextType,
  ToastContext,
  StatsContext,
  FullScreenContext,
  WalkthruContext,
} from './contexts';
import type { FullscreenObfuscationState, FullscreenMediaEditingState } from './contexts';
import { FullscreenTransformProvider } from '@/components/fullscreen-transform-provider';
import { useStableCallback } from '@/hooks/useStableCallback';
import { useTimelineThumbnailBackfill } from '@/hooks/useTimelineThumbnailBackfill';
import { ToastLevel, TpToastHost, ToastProvider, TpToastProps } from '@/components/ui/toast';
import { isWafBlockError, WAF_BLOCK_MESSAGE, WAF_BLOCK_TITLE } from '@/shared/waf-block';
import StatusBar from '@/components/status-bar';
import TPTable from '@/components/tp-table';
import TPPresentation from '@/components/tp-presentation';
import TPCourtroom from '@/components/tp-courtroom';
import TPHorizontalTimeline from '@/components/tp-horizontal-timeline';
import WorkProductIndex from '@/components/work-product-index';
import WorkProductNavBar from '@/components/work-product-nav-bar';
import '@/components/secondary-nav.css';
import { isAdHocCase, useAdHocCaseFileUpload } from '@/hooks/useAdHocCaseFileUpload';
import { useCaseFileManagementUpload } from '@/hooks/useCaseFileManagementUpload';
import { applyAdHocCaseListPolicy } from '@/shared/ad-hoc-evidence';
import {
  personalFilesCaseIdFromClaims,
  setCurrentPersonalFilesCaseId,
} from '@/shared/ad-hoc-case';
import ThreatReport from '@/components/threat-report';
import CsamReport from '@/components/csam-report';
import {
  EventFromChat
} from '@/components/answer-item';
import type {
  AnswerTimelineGenerationStatus,
  GenerateTimelineFromAnswerArgs,
} from '@/components/answer-item';
import {
  MediaSource,
  MediaType,
  TimelinePatch,
  View,
  Slide,
  Source,
  FeatureFlagTypes,
  RemoteStorage,
  isWorkProductView,
  ThreatReportKey,
  ScopesObject,
} from '@/shared/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { deleteCookie } from 'cookies-next';
import { Sandbox } from '@/components/sandbox';
import { Button } from '@tranquility-ai/theme-components/button';
import { SkipLinks } from '@tranquility-ai/theme-components/skip-links';
import { GLOW_CTA_BUTTON_CLASS, glowCtaButtonProps } from '@/components/glow-cta-button';
import Select, { SingleValue } from "react-select";
import { isTimelineCreateAutoGenerateEnabled } from '@/components/timeline-create/timeline-create-auto-generate';
import { TimelineCreateModal } from '@/components/timeline-create/timeline-create-modal';
import { AnswerTimelineTargetModal } from '@/components/timeline-create/answer-timeline-target-modal';
import { emptyScopesNew } from '@/components/chat-evidence-locker-context-new';
import { SelectStyles } from '@/shared/styleDefs';
import { Answer } from '../components/answer-item';
import { WalkthruItem, WalkthruState } from '@/shared/walkthru';
import { Progress } from '@heroui/progress';
import MediaDisplay from '@/components/ui/mediaDisplay';
import { usePathname } from 'next/navigation';
import { ToolsDrawerProvider, ToolsDrawerChrome } from '@/components/tools-drawer';
import { SessionHistoryProvider } from '@/components/session-history-context';
import { TimelineEventsProvider } from '@/components/timeline-events-context';
import { OverlayVariantProvider } from '@tranquility-ai/theme-components';
import { DismissibleBlurOverlay } from '@/components/dismissible-blur-overlay';
import {
  computeEnrichmentFromApiResponse,
  getEventSourceUrlsForScope,
  mergeEnrichmentMedia,
  type EnrichEventOptions,
  type EventSaveResult,
} from '@/components/eventModal';
import Intercom, { hide as hideIntercom, showNewMessage, shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk';
import { useIntercomTicketForm } from './useIntercomTicketForm';
import {
  readFlagOverridesFromSession,
  writeFlagOverridesToSession,
  type FlagOverrides,
} from '@/lib/feature-flag-overrides';
import {
  CookieDisclaimer,
  useAllowsNonEssentialCookies,
} from '@/components/ui/cookie-disclaimer';
import {
  APP_NAV_HISTORY_COALESCE_MS,
  appNavStatesEqual,
  discardStaleAppNavUrl,
  getOrCreateAppNavSessionId,
  isCurrentAppNavHistoryEntry,
  isSignificantNavChange,
  needsAppNavRestore,
  normalizeAppNavState,
  parseAppNavFromSearchParams,
  resetAppNavSession,
  resolveAppNavRestoreState,
  isTimelineNavView,
  persistAppNavState,
  sanitizeRestoredView,
  viewAfterCaseChange,
  startAppNavSession,
  writeAppNavToSessionStorage,
  rememberViewBeforeOverlay,
  consumeViewBeforeOverlay,
  isOverlayNavView,
  type AppNavState,
} from '@/lib/app-nav-state';
import { csamCredentialsFromUser } from '@/lib/csam-credentials';
import { resolveCsamAccess } from '@/lib/csam-access';
import {
  clearPresentationViewState,
  type PresentationViewStateScope,
} from '@/lib/presentation-view-state';

const ENTITY_SERVICE_POLL_INTERVAL_MS = 5000;
const ENTITY_SERVICE_POLL_ATTEMPTS = 120;
// Timeline-builder runs can legitimately outlive the generic enrichment timeout.
const TIMELINE_BUILD_POLL_TIMEOUT_MS = 1000 * 60 * 20;
const TIMELINE_ERROR_RECONCILIATION_INTERVAL_MS = 3000;
const TIMELINE_ERROR_RECONCILIATION_ATTEMPTS = 10;
const TIMELINE_PENDING_STATUS_EXPIRY_MS = 1000 * 60 * 5;

type AnswerTimelineTargetChoice = 'existing' | 'new';

type PendingAnswerTimelineBuild = {
  prompt: string;
  scopesAndFiles: ScopesObject;
  epochTimeSecs: number;
};

type FetchNewTimelineOptions = {
  preserveTimelineList?: boolean;
  preserveCurrentTimelineOnError?: boolean;
  onlyUpdateIfCurrentTimelineId?: string;
  onlyUpdateIfCurrentCaseId?: string;
  /** Keep the current table/slides mounted instead of the full-screen spinner. */
  skipLoadingIndicator?: boolean;
};

type TimelineGenerationResult = boolean | 'pending';

function timelineCaseLabel(caseId: string | undefined, t: TranslateFn): string {
  const id = (caseId ?? '').trim();
  if (!id || id === '*' || id.toLowerCase() === 'all cases') return t('nav.allCases');
  return getCaseDisplayName(id);
}

const getDefaultTimelineTitleForCase = (caseId: string | undefined, t: TranslateFn) => {
  const title = timelineCaseLabel(caseId, t).trim() || t('timeline.defaultTitle');
  return title.length > TIMELINE_TITLE_MAX_LENGTH
    ? title.slice(0, TIMELINE_TITLE_MAX_LENGTH).trimEnd()
    : title;
};

/** Sync read of URL + sessionStorage for first-paint nav seeding (client only). */
function readInitialAppNavState(): AppNavState {
  if (typeof window === 'undefined') return {};
  // Drop URL params left on prior-login history entries (Back after re-auth).
  discardStaleAppNavUrl(window.location.pathname);
  return resolveAppNavRestoreState(
    new URLSearchParams(window.location.search),
    sessionStorage,
    window.history.state,
  );
}

const MainAppModals = dynamic(
  () => import('@/components/main-app-modals').then((m) => m.MainAppModals),
  { ssr: false, loading: () => null },
);
const TimelinesTutorialModal = dynamic(
  () => import('@/components/timelines-tutorial-modal').then((m) => m.TimelinesTutorialModal),
  { ssr: false, loading: () => null },
);
const EventModal = dynamic(() => import('@/components/eventModal'), {
  ssr: false,
  loading: () => null,
});
const TitleSlideModal = dynamic(() => import('@/components/titleSlideModal'), {
  ssr: false,
  loading: () => null,
});
const AdminPanel = dynamic(
  () => import('./admin/AdminPanel').then((m) => m.AdminPanel),
  { ssr: false, loading: () => null },
);
const SupportFallbackModal = dynamic(
  () => import('@/components/support-fallback-modal'),
  { ssr: false, loading: () => null },
);
const AdHocFileDropLayer = dynamic(() => import('@/components/ad-hoc-file-drop-layer'), {
  ssr: false,
  loading: () => null,
});
const CaseFileManagement = dynamic(() => import('@/components/caseFileManagement'), {
  ssr: false,
  loading: () => null,
});

dayjs.extend(utc);
dayjs.extend(timezone);

type AppAuthState = {
  isAuthenticated?: boolean;
  idToken?: {
    issuer?: string;
    clientId?: string;
    expiresAt?: number;
    idToken: string;
    authorizeUrl?: unknown;
    scopes?: unknown;
    claims: Record<string, any>;
  };
  accessToken?: {
    accessToken: string;
    expiresAt?: number;
    claims: Record<string, any>;
    tokenType?: string;
    userinfoUrl?: unknown;
    authorizeUrl?: unknown;
    scopes?: unknown;
  };
};

/** Read JWT `exp` for client-side refresh scheduling only (not cryptographically verified). */
function intercomJwtExpiryMs(token: string): number | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = JSON.parse(atob(b64)) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

const INTERCOM_JWT_REFRESH_BEFORE_EXP_MS = 5 * 60 * 1000;
const INTERCOM_JWT_REFRESH_MIN_DELAY_MS = 60 * 1000;
const INTERCOM_JWT_REFRESH_FALLBACK_MS = 11 * 60 * 60 * 1000;

function areFeatureValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;

  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!areFeatureValuesEqual(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key],
    )) {
      return false;
    }
  }
  return true;
}

/** Per-key equality so we can skip no-op feature-flag state updates. */
function equalFeatureRecord(
  a: { [key: string]: unknown },
  b: { [key: string]: unknown },
): boolean {
  if (a === b) return true;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!areFeatureValuesEqual(a[key], b[key])) return false;
  }
  return true;
}

function stableFeatureInputs(features: { [key: string]: FeatureFlagTypes | null }): { [key: string]: FeatureFlagTypes | null } {
  const stableFeatures = { ...features };
  delete stableFeatures.tokens;
  delete stableFeatures.lastActivityAt;
  // Routing for the Simon Says client, not flag values.
  delete stableFeatures.simonSaysFlagsUrl;
  return stableFeatures;
}

type MainProps = {
  features: { [key: string]: FeatureFlagTypes | null };
  onStartupReady?: () => void;
};

/** Stable select value. The visible label is `timeline.select.createNew`. */
const CREATE_NEW_TIMELINE_ID = 'Create New Timeline';

function MainSkipLinks() {
  const t = useTimePilotT();
  return (
    <SkipLinks
      links={[
        { href: '#main-content', label: t('a11y.skipToMain') },
        { href: '#main-navigation', label: t('a11y.skipToNavigation') },
      ]}
    />
  );
}

const Main = ({ features, onStartupReady }: MainProps) => {
  // The locale provider is a child of this component, so the hook would stay
  // on English. SyncLocale copies the active locale up once the provider mounts.
  const [uiLocale, setUiLocale] = useState<AppLocale>(DEFAULT_APP_LOCALE);
  const t = useCallback<TranslateFn>(
    (key, values) => translateTimePilotMessage(uiLocale, key, values),
    [uiLocale],
  );
  const { formatDate, formatSavedDuration } = useAppFormatters();
  // Capture restore target once so the first authenticated paint already matches
  // URL/sessionStorage (avoids chat → restored-view flash after LD/auth settle).
  const initialAppNavRef = useRef<AppNavState | null>(null);
  if (initialAppNavRef.current === null) {
    initialAppNavRef.current = readInitialAppNavState();
  }
  const initialAppNav = initialAppNavRef.current;
  const initialHasAppNav = needsAppNavRestore(initialAppNav);
  const initialView: View = initialAppNav.view ?? 'chat';
  const initialTimelineId = initialAppNav.timelineId;
  const initialNeedsTimelineLoad =
    !!initialTimelineId && isTimelineNavView(initialView);

  const [authState, setAuthState] = useState<AppAuthState | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [realAccessToken, setRealAccessToken] = useState<string | null>(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [caseId, setCaseId] = useState<string | undefined>(() => initialAppNav.caseId);
  const [selectedSecondOption, setSelectedSecondOption] = useState<string | undefined>(
    () => initialTimelineId,
  );
  const [selectedView, setSelectedView] = useState<View>(() => initialView);
  const [newTimelineIsAvailable, setNewTimelineIsAvailable] = useState(false);
  const [multipleTimelinesAvailable, setMultipleTimelinesAvailable] = useState<TimelineDropdownItem[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(() => initialNeedsTimelineLoad);
  const [loadingWorkProducts, setLoadingWorkProducts] = useState(() => !!initialAppNav.caseId);
  const [addEvent, setAddEvent] = useState<Slide>();
  const [flagOverrides, setFlagOverridesState] = useState<FlagOverrides>(
    () => readFlagOverridesFromSession(),
  );
  const [featureFlags, setFeatureFlags] = useState<{ [key: string]: FeatureFlagTypes }>(
    () => ({
      ...(stableFeatureInputs(features) as { [key: string]: FeatureFlagTypes }),
      ...readFlagOverridesFromSession(),
    }),
  );
  const [timeSaved, setTimeSaved] = useState<number | null>(null);
  const [loadingTimeSaved, setLoadingTimeSaved] = useState(false);
  const [timeSavedLocal, setTimeSavedLocal] = useState<number | null>(null);
  const [timelineCreated, setTimelineCreated] = useState(false);
  const [isEditableTimeline, setIsEditableTimeline] = useState(false);
  const [creatingTimeline, setCreatingTimeline] = useState(false);
  const [sourcesFromChat, setSourcesFromChat] = useState<MediaSource[]>([]);
  const [timelineServiceAppearsOperational, setTimelineServiceAppearsOperational] = useState(false);
  const [allSourcesFromChat, setAllSourcesFromChat] = useState<{ allSources: MediaSource[], mediaSources: MediaSource[] } | undefined>({ allSources: [], mediaSources: [] });
  const [eventIndex, setEventIndex] = useState<number>(
    () => initialAppNav.eventIndex ?? 0,
  );
  const [currentColumn, setCurrentColumn] = useState<string | null>(null);
  // Open only after timeline content loads in applyAppNavState (avoids empty modal flash).
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [timelinePatch, setTimelinePatch] = useState<TimelinePatch | undefined>(undefined);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [courtroomTitle, setCourtroomTitle] = useState<string | undefined>(undefined);
  const [courtroomDescription, setCourtroomDescription] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [media, setMedia] = useState<string | undefined>(undefined);
  const [currentTimelineId, setCurrentTimelineId] = useState<string | undefined>(
    () => initialTimelineId,
  );
  const currentTimelineIdRef = useRef<string | undefined>(initialTimelineId);
  const [allSlides, setAllSlides] = useState<Slide[] | undefined>(undefined);
  const allSlidesRef = useRef<Slide[] | undefined>(undefined);
  const eventWriteChainsRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const [timeline, setTimeline] = useState<any>(undefined);
  const [sortByDateTime, setSortByDateTime] = useState(false);
  const [pleaseSelectTimeline, setPleaseSelectTimeline] = useState<boolean>(false);
  const [modalSelectedTimeline, setModalSelectedTimeline] = useState<string | undefined>(undefined);
  const [answerTimelineTargetModalOpen, setAnswerTimelineTargetModalOpen] = useState(false);
  const [answerTimelineTargetChoice, setAnswerTimelineTargetChoice] = useState<AnswerTimelineTargetChoice>(
    () => (initialTimelineId ? 'existing' : 'new'),
  );
  const [answerTimelineSelectedTimeline, setAnswerTimelineSelectedTimeline] = useState<string | undefined>(undefined);
  const [answerTimelineNewTitle, setAnswerTimelineNewTitle] = useState('');
  const [answerTimelineTargetSubmitting, setAnswerTimelineTargetSubmitting] = useState(false);
  const [pendingAnswerTimelineBuild, setPendingAnswerTimelineBuild] = useState<PendingAnswerTimelineBuild | null>(null);
  const pendingAnswerTimelineCaseIdRef = useRef<string | undefined>(undefined);
  const [chatEventToAdd, setChatEventToAdd] = useState<EventFromChat | undefined>(undefined);
  const pendingChatEventRef = useRef<EventFromChat | undefined>(undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Main owns FeaturesContext.Provider, so useReactiveFeatureFlagValue cannot
  // read merged flags here (ctx is null above the provider). Use local state.
  const adHocEnabled = featureFlags.adHoc === true;
  const caseFileManagementEnabled = featureFlags.caseFileManagement === true;
  const threatReportCreatorsEnabled =
    featureFlags?.[THREAT_REPORT_CREATORS_FLAG_CAMEL] === true;
  const decodedIdToken = (
    features?.tokens as { decodedIdToken?: Record<string, unknown> } | undefined
  )?.decodedIdToken;
  const personalFilesCaseId = useMemo(
    () =>
      personalFilesCaseIdFromClaims(decodedIdToken) ??
      personalFilesCaseIdFromClaims(
        authState?.idToken?.claims as Record<string, unknown> | undefined,
      ),
    [decodedIdToken, authState?.idToken?.claims],
  );
  if (personalFilesCaseId) {
    setCurrentPersonalFilesCaseId(personalFilesCaseId);
  }
  const showAdHocCaseFeatures = adHocEnabled && isAdHocCase(caseId, personalFilesCaseId);
  // Keep chat-view Ad Hoc / Desktop Files DnD even when caseFileManagement is
  // on — CFM folder drops only apply on the case-file-management view.
  const adHocCaseFileUpload = useAdHocCaseFileUpload({
    enableDocumentDrop:
      showAdHocCaseFeatures &&
      selectedView === 'chat' &&
      !isFullscreen,
  });
  const [fullscreenMediaItem, setFullscreenMediaItem] = useState<MediaSource | null>(null);
  const [fullscreenObfuscation, setFullscreenObfuscation] = useState<FullscreenObfuscationState | null>(null);
  const [fullscreenMediaEditing, setFullscreenMediaEditing] = useState<FullscreenMediaEditingState | null>(null);
  const [linkedMediaItem, setLinkedMediaItem] = useState<MediaSource | null>(null);
  const [captureData, setCaptureData] = useState<string | undefined>(undefined);
  const [timelineTags, setTimelineTags] = useState<string[]>([]);
  const [allWalkthrus, setAllWalkthrus] = useState<Record<string, WalkthruState>>(
    localStorage.getItem('walkthrus') ? JSON.parse(localStorage.getItem('walkthrus') || '') : []
  );
  const [parsedEventsFromChat, setParsedEventsFromChat] = useState<Slide[] | undefined>(undefined);
  const autogeneratedTimelineAppliedKeysRef = useRef<Set<string>>(new Set());
  const pendingAutogenAnswerEpochRef = useRef<number | null>(null);
  const [autogeneratedTimelineAppliedTick, setAutogeneratedTimelineAppliedTick] = useState(0);
  const [autogenTimelineApplyEpochInFlight, setAutogenTimelineApplyEpochInFlight] = useState<number | null>(null);
  const [useEnrichment, setUseEnrichment] = useState<boolean>(false);
  const [entityServiceJob, setEntityServiceJob] = useState<{ jobId: string | undefined, caseId: string | undefined, count: number }>({ jobId: undefined, caseId: undefined, count: 0 });
  const [entityExtractionJobs, setEntityExtractionJobs] = useState<any[]>([]);
  const [entityExtractionJobsWithQuestions, setEntityExtractionJobsWithQuestions] = useState<any[]>([]);
  const [chatSessionId, setChatSessionId] = useState<string | null>(
    () => initialAppNav.sessionId ?? null,
  );
  const [chatHistorySessions, setChatHistorySessions] = useState<any[] | null>(null);
  const [newAnswer, setNewAnswer] = useState<Answer | null>(null);
  const [entityJobId, setEntityJobId] = useState<string | undefined>(undefined);
  const [jobsBeingDeleted, setJobsBeingDeleted] = useState<string[]>([]);
  const [isCognitoUser, setIsCognitoUser] = useState(false);
  const [cognitoClientId, setCognitoClientId] = useState<string | undefined>(undefined);
  const [tenantName, setTenantName] = useState<string | undefined | null>(undefined);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isTimelineNameModalOpen, setIsTimelineNameModalOpen] = useState<boolean>(false);
  const [defaultTimelineName, setDefaultTimelineName] = useState<string>('');
  const [pendingTimelineCreation, setPendingTimelineCreation] = useState<{ creatingEventFromChat?: boolean } | null>(null);
  const [isThreatReportNameModalOpen, setIsThreatReportNameModalOpen] = useState<boolean>(false);
  const [isThreatReportProcessing, setIsThreatReportProcessing] = useState<boolean>(false);
  /** True when ThreatReport has a completed body safe to export as DOCX. */
  const [isThreatReportReady, setIsThreatReportReady] = useState(false);
  // Case id(s) with an in-flight AI timeline build (TR-1117); toolbar shows
  // "Generating…" only when the selected case is in this set.
  const [generatingTimelineCaseIds, setGeneratingTimelineCaseIds] = useState<string[]>([]);
  const [answerTimelineGenerationStatuses, setAnswerTimelineGenerationStatuses] = useState<
    Record<string, AnswerTimelineGenerationStatus>
  >({});
  const [newThreatReportId, setNewThreatReportId] = useState<string | null>(null);
  const [threatReportKey, setThreatReportKey] = useState<ThreatReportKey | undefined>(
    () => (initialView === 'threat-report' ? initialAppNav.threatReportKey : undefined),
  );
  /** Last version picked in the nav bar. The viewer leaves it alone until the language changes. */
  const [pickedThreatReportVersion, setPickedThreatReportVersion] = useState<ThreatReportKey | undefined>();
  const pickThreatReportVersion = useCallback((key: ThreatReportKey) => {
    setPickedThreatReportVersion(key);
    setThreatReportKey(key);
  }, []);
  const [isUserAdmin, setIsUserAdmin] = useState<boolean>(false);
  const [csamLimitedUser, setCsamLimitedUser] = useState(false);
  const [csamVerifier, setCsamVerifier] = useState(false);
  // False until the permission-service user fetch settles (success or fail-closed).
  // Nav restore must wait on this so ``csam-report`` is not bounced while
  // clearance is still its initial false.
  const [csamCredentialsReady, setCsamCredentialsReady] = useState(false);
  const csamCredentialsReadyRef = useRef(csamCredentialsReady);
  csamCredentialsReadyRef.current = csamCredentialsReady;
  const [isPermissionServiceAvailable, setIsPermissionServiceAvailable] = useState<boolean>(false);
  const showCaseManagementNav = useMemo(
    () =>
      canShowCaseManagementNav({
        isPermissionServiceAvailable,
        isUserAdmin,
        caseManagementDashboard: featureFlags?.caseManagementDashboard === true,
      }),
    [isPermissionServiceAvailable, isUserAdmin, featureFlags?.caseManagementDashboard],
  );
  const [entityServicePollAllJobsCount, setEntityServicePollAllJobsCount] = useState<number>(0);
  const [remoteStorageLink, setRemoteStorageLink] = useState<RemoteStorage | null>(null);
  const [remoteStorageLinkLocal, setRemoteStorageLinkLocal] = useState<RemoteStorage | null>(null);
  const [lastWorkProductView, setLastWorkProductView] = useState<View | null>(null);
  const [lastWorkProductTimelineId, setLastWorkProductTimelineId] = useState<string | undefined>(undefined);
  const [lastWorkProductCaseId, setLastWorkProductCaseId] = useState<string | undefined>(undefined);
  const [workProductViewMode, setWorkProductViewMode] = useState<'grid' | 'list'>(
    () => initialAppNav.wpMode ?? 'grid',
  );
  const [threatReportSources, setThreatReportSources] = useState<ThreatReportSourceLists>({
    fiveCastFiles: [],
    magnetFiles: [],
  });
  /** S3 upload time by export URL, for titles whose filename has no creation stamp. */
  const [threatReportSourceModifiedAt, setThreatReportSourceModifiedAt] = useState<
    Record<string, string>
  >({});
  const [threatReportSourcesLoading, setThreatReportSourcesLoading] = useState(false);
  /**
   * Why the source lists could not be built, so the modal does not report a
   * lookup that never ran as "no suitable data source" (TR-359 turned the
   * unresolved-case path into a silent return with no request to inspect).
   */
  const [threatReportSourcesError, setThreatReportSourcesError] =
    useState<ThreatReportSourcesError>(null);
  /** Storage case folder name for threat-report edge actions (getSources / getSelectors). */
  const [threatReportApiCaseId, setThreatReportApiCaseId] = useState<string | undefined>(undefined);
  const [selectedFivecastSource, setSelectedFivecastSource] = useState<string | undefined>(undefined);
  const [selectedMagnetSource, setSelectedMagnetSource] = useState<string | undefined>(undefined);
  const threatReportSourcesAbortRef = useRef<AbortController | null>(null);
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  const realAccessTokenRef = useRef(realAccessToken);
  realAccessTokenRef.current = realAccessToken;
  allSlidesRef.current = allSlides;
  /**
   * The newest ``fetchNewTimeline`` that has committed to loading: a counter, so
   * an attempt resuming after an await can tell it was superseded, plus the
   * timeline that attempt asked for, so being superseded by a *reload of the same
   * timeline* can be told apart from a switch to a different one.
   */
  const timelineLoadRef = useRef<{ generation: number; requestedId: string }>({
    generation: 0,
    requestedId: '',
  });
  /**
   * Plain reads of a timeline that are still in flight, keyed by case + timeline.
   * A second identical read joins the first instead of sending another list GET
   * and another round of file-info lookups (TR-3920: one session loaded the same
   * timeline about 44 times in 30 seconds and tripped the edge WAF rate limit).
   */
  const inFlightTimelineReadsRef = useRef<
    Map<string, { load: Promise<boolean>; generation: number; writeCount: number }>
  >(new Map());
  /** True when the latest timeline load failed on an edge WAF block (TR-3920). */
  const lastTimelineLoadWafBlockedRef = useRef(false);
  const fetchNewTimelineRef = useRef<
    (
      documentIdentifier: string | undefined,
      caseId: string | undefined,
      isUpdating: boolean,
      creatingEventFromChat?: boolean,
    ) => Promise<boolean>
  >(async () => false);
  const fetchWorkProductsForCaseRef = useRef<
    (caseId: string) => Promise<TimelineDropdownItem[]>
  >(async () => []);
  const [isBannerModalOpen, setIsBannerModalOpen] = useState<boolean>(false);
  const [banners, setBanners] = useState<string[] | undefined>(undefined);
  const [isTitleSlideModalOpen, setIsTitleSlideModalOpen] = useState<boolean>(false);
  const [intercomJwt, setIntercomJwt] = useState<string | null>(null);
  const allowsNonEssentialCookies = useAllowsNonEssentialCookies();
  const intercomAuthHeadersRef = useRef({ access: '', idToken: '' });
  intercomAuthHeadersRef.current = {
    access: authState?.accessToken?.accessToken ?? '',
    idToken: authState?.idToken?.idToken ?? '',
  };

  const bannerModalAcceptedRef = useRef<boolean>(false);
  const entityServiceJobPollRef = useRef<NodeJS.Timeout | null>(null);
  // Processed chat citations for an in-flight legacy wand job. The status poll
  // does not resend them, and entity events come back naming sourceFilePath.
  const legacyWandSourcesByJobIdRef = useRef<Map<string, MediaSource[]>>(new Map());
  const entityServicePollAllJobsRef = useRef<NodeJS.Timeout | null>(null);
  /** Captured once on mount: URL query params win, sessionStorage fills gaps. */
  const pendingNavRestoreRef = useRef<AppNavState | null>(initialAppNav);
  const navRestoreStartedRef = useRef(false);
  /** Seeded restore must skip the case-switch reset on the first caseId effect. */
  const skipForceChatOnCaseChangeRef = useRef(initialHasAppNav);
  /** True while applying URL/history restore — blocks toast + history push. */
  const suppressNavSideEffectsRef = useRef(false);
  const suppressNavSideEffectsDepthRef = useRef(0);
  const navApplyGenerationRef = useRef(0);
  const isApplyingHistoryNavRef = useRef(false);
  const lastPersistedNavRef = useRef<AppNavState | null>(
    initialHasAppNav ? null : initialAppNav,
  );
  /** After refresh/Back/Forward apply, force replaceState once (avoid duplicate push). */
  const forceReplaceNavUrlRef = useRef(!initialHasAppNav);
  /** Last state committed as a Back/Forward history point (not every replace). */
  const lastHistoryCommitRef = useRef<AppNavState | null>(
    initialHasAppNav ? null : initialAppNav,
  );
  /** While true, further significant updates replace the entry just pushed. */
  const historyCoalesceActiveRef = useRef(false);
  const historyCoalesceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const appNavSessionIdRef = useRef<string | null>(null);
  /** Last observed tenant — used to clear nav/case state on in-session tenant switch. */
  const prevTenantIdRef = useRef<string | null>(null);
  /**
   * Ref (not state) so apply/persist locking does not re-render the whole shell.
   * Unlock bumps `navPersistWake` so the persist effect re-runs after Back/Forward
   * even when apply setStates were no-ops or already flushed while locked.
   */
  const historyApplyLockRef = useRef(false);
  /** Bumped when a history apply unlocks — wakes URL/sessionStorage persist. */
  const [navPersistWake, setNavPersistWake] = useState(0);
  /** undefined = no nav restore pending; null = explicit clear; string = activate session. */
  const [seedChatSessionId, setSeedChatSessionId] = useState<string | null | undefined>(
    () => initialAppNav.sessionId,
  );
  const [navRestoreComplete, setNavRestoreComplete] = useState(() => !initialHasAppNav);
  const navRestoreCompleteRef = useRef(navRestoreComplete);
  navRestoreCompleteRef.current = navRestoreComplete;
  // True only after identified LaunchDarkly values are copied into
  // ``featureFlags``. Declared above the startup layout effect: a later
  // ``const`` is still in the temporal dead zone when that effect's
  // dependency list is evaluated.
  const [flagsReady, setFlagsReady] = useState<boolean>(false);

  // page.tsx keeps its original loading screen mounted while Main initializes
  // behind it. Signal from a layout effect so the first visible Main paint
  // already contains the fully resolved auth + navigation state.
  useLayoutEffect(() => {
    if (isAuthInitialized && navRestoreComplete && flagsReady) {
      onStartupReady?.();
    }
  }, [isAuthInitialized, navRestoreComplete, flagsReady, onStartupReady]);

  const windowSize = useWindowSize();
  const isSmallViewport = windowSize.width && windowSize.width < SMALL_VIEWPORT_WIDTH;
  const logoWidth = isSmallViewport ? 300 : 733;
  const logoHeight = isSmallViewport ? (300 * 233.7) / 733 : 233.7; // Maintain aspect ratio

  // Cases are read through the query cache rather than ``useCasesList``: that
  // hook reads AuthContext / FeaturesContext, which Main provides and therefore
  // cannot read itself (see the FeaturesContext note above), so here it would
  // key a permanently disabled query on a null cellId. Main passes its own
  // tokens and flags to the shared options so it shares the nav's cache entry.
  const queryClient = useQueryClient();

  /**
   * Resolve a case's S3 FOLDER NAME: its ``folder_token`` (a UUID)
   * when it has one, else its display name. ``null`` for All Cases, for an
   * unresolvable selection, and for a cold+failed cache.
   *
   * This is the one identifier that does not move when a case is renamed — in the
   * UI or at the SharePoint/Drive folder — and everything that has to be
   * retrievable across a rename keys on it:
   *
   *   - ``caseFolderName`` on a chat request → ``case:<folderName.lower()>``
   *     retrieval filter (matching what ingest stamped) and the ``chat_key``
   *     partition. Without it assistant-service derives the filter from the
   *     display name it was sent, which matches nothing for a UUID-folder case:
   *     every question fails with zero retrieval.
   *   - ``CASE:<folderName>`` timeline and entity-extraction-job tags, matched
   *     byte-exactly and checked against the caller's ``case:<folderName>`` access
   *     tags.
   *   - ``{folderName}-{userId}`` chat-history keys.
   *
   * The bare folder name is sent rather than a finished tag because the server needs it in
   * two casings and lowercasing is not reversible. ``selectedCase`` stays the
   * display name alongside it: the RAG system prompt, report titles, and the
   * mobile-forensics intake lookup all want a human name.
   *
   * The component equivalent is ``useCaseFolderNameResolver``; Main cannot use it
   * (it provides the contexts that hook reads), so it goes through the shared
   * query options and applies the same ad-hoc list policy — without which the
   * synthetic My Files case resolves to no folder name.
   *
   * Every consumer falls back to the display name when this returns ``null``,
   * which is today's behavior and the correct answer for a legacy case — never a
   * widened read.
   */
  const resolveCaseFolderNameFor = useCallback(
    async (selected: string | null | undefined): Promise<string | null> => {
      const trimmed = selected?.trim() ?? '';
      if (!trimmed || isAllCasesSelection(trimmed)) return null;
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return null;

      const fromPermissionService = featureFlags?.casesFromPermissionService === true;
      const tenantId = getTenantIdFromToken(authState?.idToken?.idToken || '');
      // The permission-service branch needs a tenant to resolve the caller's
      // cases; without one there is nothing to await, and asking would poison a
      // dead cache key with an error. Degrade rather than fetch — matching the
      // precondition ``useCasesList`` puts on ``enabled``, which ``fetchQuery``
      // does not honor on its own.
      if (fromPermissionService && !tenantId) return null;

      const casesOptions = casesListQueryOptions({
        cellId,
        accessToken: realAccessToken,
        fromPermissionService,
        tenantId,
      });
      let fetched: CaseSummary[] | undefined;
      try {
        fetched = await queryClient.fetchQuery(casesOptions);
      } catch {
        // A refresh of a stale entry rejects while the cache still holds the list
        // the nav is showing; resolving against those beats giving up.
        fetched = queryClient.getQueryData<CaseSummary[]>(casesOptions.queryKey);
      }
      return resolveCaseFolderName(
        fetched ? applyAdHocCaseListPolicy(fetched, adHocEnabled, personalFilesCaseId) : undefined,
        trimmed,
      );
    },
    [
      queryClient,
      authState?.idToken?.idToken,
      realAccessToken,
      featureFlags?.casesFromPermissionService,
      adHocEnabled,
      personalFilesCaseId,
    ],
  );

  // Below the resolver on purpose. This hook runs inside Main, above the
  // providers ``useAwaitedCaseFolderNameResolver`` reads, so it takes Main's
  // resolver instead of calling that hook.
  const caseFileManagementUpload = useCaseFileManagementUpload({
    caseId,
    resolveStorageCaseId: resolveCaseFolderNameFor,
  });

  /** Spreadable ``caseFolderName`` fragment for a ``POST /chat`` body. */
  const caseFolderNameFields = useCallback(
    async (selected: string | null | undefined): Promise<{ caseFolderName?: string }> =>
      caseFolderNameBody(await resolveCaseFolderNameFor(selected)),
    [resolveCaseFolderNameFor],
  );

  /** ``CASE:<folderName>`` tag for timeline reads/writes and extraction jobs. */
  const caseTimelineTagFor = useCallback(
    async (selected: string | null | undefined): Promise<string> =>
      caseTimelineTag(await resolveCaseFolderNameFor(selected), selected),
    [resolveCaseFolderNameFor],
  );

  const [ldFlagsSet, setLdFlagsSet] = useState<boolean>(false);
  const ldClient = useLDClient();
  const ldFlags = useFlags();
  const pathname = usePathname();
  // LaunchDarkly simonSays only starts the fetch. The live set is the Simon
  // Says map when that map's own simonSays flag is also true; either flag
  // turning off selects LaunchDarkly again.
  const ldSimonSays = isSimonSaysEnabled(ldFlags);
  const sessionTokens = features?.tokens as {
    decodedAccessToken?: Record<string, unknown>;
    decodedIdToken?: Record<string, unknown>;
  } | undefined;
  const sessionClaim = (key: string): string | undefined => {
    for (const source of [
      sessionTokens?.decodedAccessToken,
      authState?.accessToken?.claims,
      sessionTokens?.decodedIdToken,
      authState?.idToken?.claims,
    ]) {
      const value = source?.[key];
      if (typeof value === 'string' && value.trim() !== '') return value.trim();
    }
    return undefined;
  };
  const taiFeatureFlags = useTaiFeatureFlags(
    ldSimonSays,
    {
      username: sessionClaim('username')
        ?? sessionClaim('cognito:username')
        ?? (authState?.idToken?.claims?.['cognito:username'] as string | undefined)
        ?? (authState?.idToken?.claims?.username as string | undefined),
      email: sessionClaim('email')
        ?? (typeof features?.email === 'string' ? features.email : undefined),
      userId: sessionClaim('custom:user_id'),
      tenantId: sessionClaim('custom:tenant_id')
        ?? (features as { tenantId?: string } | undefined)?.tenantId
        ?? (features?.orgId as string | undefined),
      tenantName: sessionClaim('custom:tenant_name'),
      cellId: sessionClaim('custom:cell_id')
        ?? (features?.cellId as string | undefined),
      partition: sessionClaim('custom:partition'),
      environment: sessionClaim('custom:env')
        ?? (features?.systemStage as string | undefined),
    },
    {
      flagsUrl: typeof features?.simonSaysFlagsUrl === 'string' ? features.simonSaysFlagsUrl : undefined,
      accessToken: realAccessToken,
    },
  );
  const serviceSimonSays = isSimonSaysEnabled(taiFeatureFlags);
  // LaunchDarkly's React SDK merges each stream into the full flag set, but a
  // single update can still omit a key. Keep the keys seen this session in a
  // snapshot that never contains Simon Says-only keys, and swap the whole
  // live map when either simonSays flag changes which source is selected.
  const ldSnapshotRef = useRef<Record<string, FeatureFlagTypes>>({});
  const seenLdFlagsRef = useRef<object | null>(null);
  if (
    seenLdFlagsRef.current !== ldFlags
    || isSimonSaysEnabled(ldSnapshotRef.current) !== ldSimonSays
  ) {
    seenLdFlagsRef.current = ldFlags;
    ldSnapshotRef.current = { ...ldSnapshotRef.current, ...ldFlags };
  }
  const usingSimonSays = ldSimonSays && serviceSimonSays;
  const flagsForMerge = useMemo(
    () => selectFeatureFlagSource(ldSnapshotRef.current, ldSimonSays ? taiFeatureFlags : null),
    [ldFlags, ldSimonSays, serviceSimonSays, taiFeatureFlags],
  );
  // Persist Internal Tools overrides for the browser session. The merge effect
  // below folds them into `featureFlags` so every FeaturesContext consumer
  // (and `useReactiveFeatureFlag*` subscriber) updates without a reload.
  useEffect(() => {
    writeFlagOverridesToSession(flagOverrides);
  }, [flagOverrides]);

  // Ref-backed, stable context values.
  //
  // The `featureFlags`/`ldFlags` and auth-token values change frequently (LD
  // streams flag updates; Cognito periodically refreshes tokens). If we passed
  // those values directly through context, the provider object's identity would
  // change on every update and React would re-render *every* consumer of these
  // contexts. Instead we mirror the latest values into refs (updated each
  // render, matching the `intercomAuthHeadersRef` pattern above) and expose a
  // single memoized context object whose identity never changes. Consumers read
  // the latest value lazily via getters, so flag/token updates no longer force
  // context-driven re-renders.
  const featuresRef = useRef<{
    flagsFromLD: { [key: string]: FeatureFlagTypes };
    flagsFromService: { [key: string]: FeatureFlagTypes } | null;
    featureFlags: { [key: string]: FeatureFlagTypes };
    flagOverrides: { [key: string]: FeatureFlagTypes };
  }>({ flagsFromLD: ldFlags, flagsFromService: taiFeatureFlags, featureFlags, flagOverrides });
  featuresRef.current = {
    flagsFromLD: ldFlags,
    flagsFromService: taiFeatureFlags,
    featureFlags,
    flagOverrides,
  };

  const authTokensRef = useRef<{
    idToken: string;
    accessToken: string;
    realAccessToken: string | null;
    tenantName: string;
    orgName: string;
  }>({
    idToken: authState?.idToken?.idToken || '',
    accessToken: authState?.accessToken?.accessToken || '',
    realAccessToken: realAccessToken || null,
    tenantName: tenantName || '',
    orgName: orgName || '',
  });
  authTokensRef.current = {
    idToken: authState?.idToken?.idToken || '',
    accessToken: authState?.accessToken?.accessToken || '',
    realAccessToken: realAccessToken || null,
    tenantName: tenantName || '',
    orgName: orgName || '',
  };

  // Feature-flag change subscription. Lets `React.memo`'d consumers re-render
  // when flags actually change (via `useReactiveFeatureFlags`) without being
  // re-rendered by unrelated `Main` renders or token refreshes. `subscribeFlags`
  // is stable so the context object identity never changes.
  const flagListenersRef = useRef<Set<() => void>>(new Set());
  const subscribeFlags = useCallback((listener: () => void) => {
    flagListenersRef.current.add(listener);
    return () => {
      flagListenersRef.current.delete(listener);
    };
  }, []);

  // Token change subscription (see `subscribeFlags`). Lets `React.memo`'d
  // consumers (e.g. Chat) re-render on a Cognito token refresh via
  // `useReactiveAuthTokens` while still skipping unrelated `Main` renders.
  const authListenersRef = useRef<Set<() => void>>(new Set());
  const subscribeAuth = useCallback((listener: () => void) => {
    authListenersRef.current.add(listener);
    return () => {
      authListenersRef.current.delete(listener);
    };
  }, []);

  const featuresContextValue = useMemo<FeaturesContextType>(
    () => ({
      get flagsFromLD() {
        return featuresRef.current.flagsFromLD;
      },
      get flagsFromService() {
        return featuresRef.current.flagsFromService;
      },
      get featureFlags() {
        return featuresRef.current.featureFlags;
      },
      get flagOverrides() {
        return featuresRef.current.flagOverrides;
      },
      setFlagOverrides: setFlagOverridesState,
      getFeatureFlags: () => featuresRef.current.featureFlags,
      getFlagsFromLD: () => featuresRef.current.flagsFromLD,
      getFlagsFromService: () => featuresRef.current.flagsFromService,
      getFlagOverrides: () => featuresRef.current.flagOverrides,
      subscribe: subscribeFlags,
    }),
    [subscribeFlags],
  );

  const authContextValue = useMemo<AuthContextType>(
    () => ({
      get idToken() {
        return authTokensRef.current.idToken;
      },
      get accessToken() {
        return authTokensRef.current.accessToken;
      },
      get realAccessToken() {
        return authTokensRef.current.realAccessToken;
      },
      get tenantName() {
        return authTokensRef.current.tenantName;
      },
      get orgName() {
        return authTokensRef.current.orgName;
      },
      getIdToken: () => authTokensRef.current.idToken,
      getAccessToken: () => authTokensRef.current.accessToken,
      getRealAccessToken: () => authTokensRef.current.realAccessToken,
      getTenantName: () => authTokensRef.current.tenantName,
      getOrgName: () => authTokensRef.current.orgName,
      subscribe: subscribeAuth,
    }),
    [subscribeAuth],
  );

  const statsContextValue = useMemo<StatsContextType>(
    () => ({
      recordStat,
      userInfo: {
        page: pathname || (typeof window !== 'undefined' ? window.location.pathname : ''),
        userId: authState?.idToken?.claims.email ?? '',
        sessionId: chatSessionId ?? '',
        caseId: caseId ?? '',
        query: '',
      },
    }),
    [authState?.idToken?.claims.email, caseId, chatSessionId, pathname],
  );

  // Notify feature-flag subscribers when the live flags change identity, when
  // a session override changes, when the raw LaunchDarkly map changes, and
  // when the simon-says map changes.
  // `setFeatureFlags` is guarded by a shallow-equality check, so while Simon
  // Says is the live source a LaunchDarkly update does not change
  // `featureFlags`. Internal Tools still has to refresh the LaunchDarkly
  // column. Readers of the live flags bail out when their snapshot is the
  // same object.
  useEffect(() => {
    flagListenersRef.current.forEach((listener) => listener());
  }, [featureFlags, flagOverrides, ldFlags, taiFeatureFlags]);

  // Notify token subscribers when any auth value changes (e.g. Cognito refresh).
  useEffect(() => {
    authListenersRef.current.forEach((listener) => listener());
  }, [authState, realAccessToken, accessToken, tenantName, orgName]);

  const [toast, setToastRaw] = useState<TpToastProps>();

  // Block toasts while restoring from refresh / Back / Forward / LD soft-refresh
  // so read-only navigation cannot resurface create/update/delete notifications.
  const setToast = useCallback((value: TpToastProps | undefined | ((prev: TpToastProps | undefined) => TpToastProps | undefined)) => {
    if (suppressNavSideEffectsRef.current) {
      if (value === undefined) {
        setToastRaw(undefined);
        return;
      }
      if (typeof value === 'function') {
        setToastRaw((prev) => {
          const next = value(prev);
          return next === undefined ? undefined : prev;
        });
        return;
      }
      return;
    }
    setToastRaw(value);
  }, []);

  // Edge WAF block notice (TR-3920). It goes around the restore suppression in
  // `setToast` on purpose: the block is not a create/update/delete notice, and
  // the loads it breaks are often the restore-time reads that suppression is on
  // for. One notice a minute, because every blocked call in a burst lands here.
  const lastWafBlockNoticeAtRef = useRef(0);
  const notifyWafBlock = useCallback(() => {
    const now = Date.now();
    if (now - lastWafBlockNoticeAtRef.current < 60_000) return;
    lastWafBlockNoticeAtRef.current = now;
    setToastRaw({
      setOpen: (open: boolean) => { if (!open) setToastRaw(undefined); },
      open: true,
      location: 'bottom-right',
      time: now,
      duration: 10000,
      title: WAF_BLOCK_TITLE,
      description: WAF_BLOCK_MESSAGE,
      level: ToastLevel.ERROR,
    });
  }, []);

  // Memoized so the FullScreen context object only changes identity when one of
  // its *values* changes (entering/leaving fullscreen, linking media, capture
  // data) — not on every unrelated `Main` render. Setters are referentially
  // stable, so consumers (e.g. memoized `MediaDisplay`) still re-render exactly
  // when the fullscreen state they care about changes.
  const fullScreenContextValue = useMemo(
    () => ({
      isFullscreen,
      setIsFullscreen,
      fullscreenMediaItem,
      setFullscreenMediaItem,
      linkedMediaItem,
      setLinkedMediaItem,
      fullScreenCaptureData: captureData,
      setFullScreenCaptureData: setCaptureData,
      fullscreenObfuscation,
      setFullscreenObfuscation,
      fullscreenMediaEditing,
      setFullscreenMediaEditing,
    }),
    [isFullscreen, fullscreenMediaItem, linkedMediaItem, captureData, fullscreenObfuscation, fullscreenMediaEditing],
  );


  const setToastOpen = (open: boolean) => {
    if (!open) {
      setToastRaw(undefined);
      return;
    }
    if (toast) setToast({ ...toast, open });
  }

  // Stable ToastContext value so memoized consumers (e.g. EventModal) aren't
  // re-rendered on every unrelated `Main` render. `setToast` is a stable state
  // setter; `setToastOpen` is wrapped to keep a stable identity while reading
  // the latest `toast`.
  const stableSetToastOpen = useStableCallback(setToastOpen);
  const toastContextValue = useMemo(
    () => ({ setToast, setToastOpen: stableSetToastOpen }),
    [setToast, stableSetToastOpen],
  );

  const safeShowIntercom = () => {
    try {
      showNewMessage('');
    } catch (error) {
    }
  }

  const safeHideIntercom = () => {
    try {
      hideIntercom();
    } catch (error) {
    }
  }

  const safeShutdownIntercom = () => {
    try {
      shutdownIntercom();
    } catch (error) {
    }
  }

  useEffect(() => {
    if (
      !allowsNonEssentialCookies ||
      !authState?.isAuthenticated ||
      !authState?.accessToken?.accessToken?.trim() ||
      !authState?.idToken?.idToken?.trim()
    ) {
      setIntercomJwt(null);
      return;
    }

    const ac = new AbortController();
    const getIntercomJwt = async () => {
      try {
        const session = await getGatewaySession();
        if (!session || ac.signal.aborted) return;
        const data = await fetchIntercomToken({
          cellId: session.cellId,
          accessToken: session.accessToken,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        if (typeof data?.token === 'string' && data.token) {
          setIntercomJwt(data.token);
        } else {
          captureWarning('Intercom JWT success response missing token');
          setIntercomJwt(null);
        }
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        setIntercomJwt(null);
      }
    };
    void getIntercomJwt();
    return () => ac.abort();
  }, [
    allowsNonEssentialCookies,
    authState?.isAuthenticated,
    authState?.idToken?.idToken,
    authState?.accessToken?.accessToken,
  ]);

  /** Re-fetch before server JWT expiry (12h) so Intercom identity stays valid without an access-token rotation. */
  useEffect(() => {
    if (!intercomJwt?.trim()) return;

    const expMs = intercomJwtExpiryMs(intercomJwt);
    const delay =
      expMs != null
        ? Math.max(
            expMs - Date.now() - INTERCOM_JWT_REFRESH_BEFORE_EXP_MS,
            INTERCOM_JWT_REFRESH_MIN_DELAY_MS,
          )
        : INTERCOM_JWT_REFRESH_FALLBACK_MS;

    const id = window.setTimeout(() => {
      void (async () => {
        try {
          const session = await getGatewaySession();
          if (!session) { setIntercomJwt(null); return; }
          const data = await fetchIntercomToken({
            cellId: session.cellId,
            accessToken: session.accessToken,
          });
          if (typeof data?.token === 'string' && data.token) {
            setIntercomJwt(data.token);
          } else {
            captureWarning('Intercom JWT refresh success response missing token');
            setIntercomJwt(null);
          }
        } catch {
          setIntercomJwt(null);
        }
      })();
    }, delay);

    return () => clearTimeout(id);
  }, [intercomJwt]);

  useEffect(() => {
    const ldIdentify = async () => {
      const email = authState?.idToken?.claims.email;
      if (!authState || !email || email === '') {
        return;
      }
      // Name is optional — missing name must not block identify / nav restore.
      const name =
        (authState.idToken?.claims.name as string | undefined)?.trim() || email;
      const tenantId = authState?.accessToken?.claims?.['custom:tenant_id'];
      const initFriendly = (features as Record<string, unknown> | undefined)?.tenantFriendlyName;
      const tenantNameFromInit =
        typeof initFriendly === 'string' && initFriendly.trim() !== '' ? initFriendly.trim() : undefined;
      const tenantNameFromToken = authState?.accessToken?.claims?.['custom:tenant_name'] as string | undefined;
      const tenantName = tenantNameFromInit ?? tenantNameFromToken;
      const baseEmail = email.includes('+') && email.includes('@') ? `${email.split('+')[0]}@${email.split('@')[1]}` : email;
      const ldContext: LDMultiKindContext = {
        kind: 'multi',
        user: {
          key: email,
          name,
          email,
          baseEmail,
        },
        organization: {
          key: features?.orgId ? `${features?.orgId}` : undefined,
          systemStage: authState?.accessToken?.claims?.['custom:env'] ?? features?.systemStage ?? '',
        }
      };
      // add tenant id to organization context if it exists
      if (tenantId) {
        (ldContext.organization as any).tenant = {
          key: tenantId ?? '',
        };
      }
      if (tenantName) {
        setTenantName(tenantName as string);
        (ldContext.organization as any).tenantName = {
          key: tenantName ?? '',
        };
      } else setTenantName(undefined);
      // Always mark flags ready after the attempt. A failed/blocked identify
      // must not leave bookmark restore stuck on "Loading TimePilot…".
      try {
        await ldClient?.identify(ldContext);
      } catch (error) {
        console.error('LaunchDarkly identify failed:', error);
        // WARN, not ERROR: flags fall back to defaults and the app still works,
        // but a spike means users are seeing the wrong feature set.
        reportOpsWarn('FEATURE_FLAGS_IDENTIFY_FAILED', describeErrorForOps(error));
      } finally {
        setLdFlagsSet(true);
      }
    };
    void ldIdentify();
  }, [ldClient, features?.orgId, features?.systemStage, (features as Record<string, unknown> | undefined)?.tenantFriendlyName, authState?.idToken?.claims.email, authState?.idToken?.claims.name, authState?.accessToken?.claims?.['custom:tenant_id'], authState?.accessToken?.claims?.['custom:tenant_name'], authState?.accessToken?.claims?.['custom:env']]);

  // Nav restore's 8s fallback only arms while restore is still pending. A visit
  // with no saved nav already has ``navRestoreComplete`` set, so if identify
  // never starts or the promise never settles, ``ldFlagsSet`` stays false,
  // ``flagsReady`` never flips, and the shell stays on the loading screen.
  // Same ceiling HelioTrace and IdentifiedFlagsGate use on their own.
  useEffect(() => {
    if (ldFlagsSet) return;
    const timer = window.setTimeout(() => setLdFlagsSet(true), 8000);
    return () => window.clearTimeout(timer);
  }, [ldFlagsSet]);

  useEffect(() => {
    if (banners && banners.length > 0) {
      if (banners.includes('cjis')) {
        setIsBannerModalOpen(true);
      }
    }
  }, [banners]);

  useEffect(() => {
    if (!features) return;
    const init = features as Record<string, unknown>;
    const rawBanners = init.banners;
    if (Array.isArray(rawBanners)) {
      setBanners(rawBanners.filter((x): x is string => typeof x === 'string'));
    }
    const friendly = init.tenantFriendlyName;
    if (typeof friendly === 'string' && friendly.trim() !== '') {
      setTenantName(friendly.trim());
    }
    const rs = init.remoteStorageFromTenant as { type?: string; url?: string } | undefined;
    if (rs && typeof rs.url === 'string' && rs.url.trim() !== '') {
      setRemoteStorageLink({
        type: rs.type && rs.type.trim() !== '' ? rs.type : 'SharePoint',
        url: rs.url.trim(),
      });
    }
  }, [features]);

  // Keep authState in sync with tokens from /api/init (initial load and periodic refresh for tenant + CSM).
  // When the 50-min refresh returns new tokens, features.tokens updates; we must push them into authState
  // so API calls and AuthContext use the refreshed token.
  useEffect(() => {
    if (!features || !features.tokens) return;
    const cognitoId = (features.tokens as any)?.decodedIdToken;
    const cognitoAccess = (features.tokens as any)?.decodedAccessToken;

    const realAccessToken = (features.tokens as any)?.rawAccessToken;
    const idToken = (features.tokens as any)?.rawIdToken || (features.tokens as any)?.rawIdTokenData;
    setCognitoClientId(cognitoAccess?.client_id);

    const cognitoUsername = getUsername(cognitoId);
    const name = cognitoId?.name || cognitoUsername;
    if (!(cognitoId?.email && cognitoUsername && idToken && idToken.trim() !== '')) {
      return;
    }
    const authStateToSet = {
      idToken: {
        issuer: cognitoId.iss,
        clientId: cognitoId.aud,
        expiresAt: cognitoId.exp,
        idToken: idToken as any,
        authorizeUrl: cognitoId.authorizeUrl,
        scopes: cognitoId.scopes,
        claims: {
          email: cognitoId.email,
          name: name,
          aud: 'timepilot-cognito',
          auth_time: cognitoId.auth_time,
          exp: cognitoId.exp,
          iss: cognitoId.iss,
          jti: cognitoId.jti,
          iat: cognitoId.iat,
          email_verified: true,
          family_name: cognitoId.family_name ?? '',
          given_name: cognitoId.given_name ?? '',
          'cognito:username': cognitoUsername,
          sub: cognitoId.sub ?? cognitoUsername,
        },
      },
      accessToken: {
        // TODO: use (realAccessToken || idToken) once all consumers (e.g. cases route, getTokenInfoFromHeadersOrCookies) use id token for tenant
        accessToken: idToken as any,
        expiresAt: cognitoAccess?.exp ?? cognitoId.exp,
        claims: cognitoAccess ?? cognitoId,
        tokenType: 'Bearer',
        userinfoUrl: cognitoId.userinfoUrl,
        authorizeUrl: cognitoId.authorizeUrl,
        scopes: cognitoId.scopes,
      },
      isAuthenticated: true,
    }
    setTenantId(
      resolveTenantId(
        cognitoId['custom:tenant_id'] as string | undefined,
        cognitoId['custom:tenant'] as string | undefined,
        (features as { tenantId?: string })?.tenantId,
        features?.orgId as string | undefined,
      ),
    );
    // TODO: use (realAccessToken || idToken) once consumers use id token for tenant; for now idToken so cookie has tenant claims
    setAccessToken(idToken as string);
    setRealAccessToken(realAccessToken as string);
    setAuthState(authStateToSet);
    setIsCognitoUser(true);
    setIsAuthInitialized(true);
    // Depend on token content so authState updates when periodic alb-info refresh returns new tokens
  }, [
    features?.isCognitoClient,
    features?.tokens,
    (features?.tokens as any)?.rawIdToken,
    (features?.tokens as any)?.rawAccessToken,
    (features?.tokens as any)?.decodedIdToken?.exp,
    (features?.tokens as any)?.decodedAccessToken?.exp,
  ]);


  useEffect(() => {
    // The config-service–derived link (new cell architecture) is authoritative
    // when present. Show it regardless of whether the tenant friendly name has
    // loaded — link derivation does not depend on it, so gating on tenantName
    // could hide a valid SharePoint/Google Drive link. Fall back to the legacy
    // ENV-derived org link only when no derived link is available.
    if (!!remoteStorageLink?.url && remoteStorageLink?.url.trim() !== '') {
      setRemoteStorageLinkLocal({
        type: remoteStorageLink?.type ?? 'SharePoint',
        url: remoteStorageLink?.url,
      });
    } else if (!!featureFlags?.sharepointLink && (featureFlags?.sharepointLink as string).trim() !== '') {
      // not a real flag, comes from ENV variables for the organization
      setRemoteStorageLinkLocal({
        type: (featureFlags?.sharepointLink as string).toLowerCase().includes('google') ? 'Google Drive' : 'SharePoint',
        url: featureFlags?.sharepointLink as string,
      });
    } else {
      setRemoteStorageLinkLocal(null);
    }
  }, [featureFlags?.sharepointLink, remoteStorageLink]);

  // Tracks the last case+timeline the merge effect auto-refetched, so repeated
  // runs (e.g. from frequent LaunchDarkly flag streams) don't refetch — and
  // reset `allSlides`/`title`/`media` — for a timeline that's already loaded.
  const lastTimelineRefetchKeyRef = useRef<string | null>(null);
  // Apply the selected flag set before paint. Either simonSays flag flipping
  // changes `usingSimonSays` / `flagsForMerge`, and replace drops the other
  // list's keys on that same turn.
  useLayoutEffect(() => {
    const writeFlags = () => {
      setFeatureFlags((prev) => {
        const next = applyActiveFeatureFlags(
          prev,
          stableFeatureInputs(features),
          flagsForMerge,
          flagOverrides,
          // A source change must drop the other list's keys. `usingSimonSays`
          // is a full snapshot. LaunchDarkly replaces too, from the session
          // snapshot above, so a stream that omitted a key does not clear it
          // and a service-only key does not linger.
          true,
        );
        return equalFeatureRecord(prev, next) ? prev : next;
      });
    };
    const initFeatureFlags = () => {
      if (
        !!ldClient
        && features?.ldClientSideId
        && features?.orgId
        && !!ldFlags
        && !!ldFlagsSet
      ) {
        writeFlags();
      }
    }
    if (
      !!ldClient &&
      (authState
        && authState.idToken?.claims.email
        && authState.idToken?.claims.email !== ''
        && authState.idToken?.claims.name
        && authState.idToken?.claims.name !== ''
      )
      && features?.ldClientSideId
      && features?.orgId
      && !!flagsForMerge
    ) {
      initFeatureFlags();
    } else {
      writeFlags();
    }
    if (ldFlagsSet) {
      setFlagsReady(true);
    }
    // The timeline fetch below closes over ``featureFlags``. On the render that
    // first applies identified flags, that state is still the previous one, so
    // a fetch here would run before ``casesFromPermissionService`` is visible.
    if (!flagsReady || !caseId) return;
    if (caseId) {
      (async () => {
        if (currentTimelineId && currentTimelineId !== '') {
          // This effect re-runs on every LaunchDarkly stream update (it depends
          // on `flagsForMerge`, which changes identity each stream). `fetchNewTimeline`
          // calls `setAllSlides`/`setTitle`/`setMedia` with fresh references, so
          // refetching here on every flag update would reset those values and
          // force memoized consumers (e.g. `TPPresentation`) to re-render. Dedupe
          // by case + timeline so the auto-refetch happens once per timeline
          // rather than on every flag stream.
          const refetchKey = `${caseId}::${currentTimelineId}`;
          if (lastTimelineRefetchKeyRef.current === refetchKey) return;
          // Mark in-flight *before* awaiting to prevent rapid LD streams from
          // kicking off concurrent duplicate fetches, but only keep the key if
          // the fetch actually succeeded. If it fails or returns early (e.g.
          // token not ready yet, network error, no timeline), reset the key so a
          // later effect run retries instead of leaving slides empty/stale until
          // a full reload or manual timeline change.
          lastTimelineRefetchKeyRef.current = refetchKey;
          const didLoad = await fetchNewTimeline(currentTimelineId, caseId, false);
          if (!didLoad) {
            // Keep the key after a WAF block. A retry on the next flag change
            // only adds to the rate count that caused the block (TR-3920).
            if (lastTimelineLoadWafBlockedRef.current) return;
            if (lastTimelineRefetchKeyRef.current === refetchKey) {
              lastTimelineRefetchKeyRef.current = null;
            }
            return;
          }
          setSelectedSecondOption(currentTimelineId);
          setCurrentTimelineId(currentTimelineId);
        }
      })();
    }
  }, [ authState, features, flagsForMerge, usingSimonSays, ldFlags, ldClient, ldFlagsSet, flagsReady, flagOverrides]);


  // Fetch user admin status from permission service once on mount.
  // Main only renders after /api/init confirms authentication, so no auth guard needed here.
  useEffect(() => {
    (async () => {
      try {
        const gwSession = await getGatewaySession();
        if (!gwSession) {
          setIsUserAdmin(false);
          setCsamLimitedUser(false);
          setCsamVerifier(false);
          setIsPermissionServiceAvailable(false);
          return;
        }
        const { tenantId, accessToken } = gwSession;
        if (!tenantId) {
          console.error('fetchUserAdminStatus: custom:tenant_id claim is missing from access token — cannot build permission URL');
          // Deliberately NOT reported to ops: the ops-log route itself requires a
          // tenant claim (`require_tenant`), so this event can never be delivered
          // — and the resulting 401 would look like a stale token to gatewayFetch's
          // auth recovery, which can escalate to a sign-out redirect. Covering
          // auth-layer failures needs a route that does not itself require auth.
          setIsUserAdmin(false);
          setCsamLimitedUser(false);
          setCsamVerifier(false);
          setIsPermissionServiceAvailable(false);
          return;
        }
        const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const cognitoGroups: string[] = Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [];
        const isCSM = cognitoGroups.some((g: string) => ['csm', 'admin'].includes(g));

        const response = await gatewayFetch(
          isCSM ? '/permission-service/v1/csm' : '/permission-service/v1',
          `/tenants/${tenantId}/user`,
          { method: 'GET' },
          gwSession,
        );

        if (response.ok) {
          const user = await response.json();
          setIsUserAdmin(user?.is_admin || false);
          const csam = csamCredentialsFromUser(user);
          setCsamLimitedUser(csam.csamLimitedUser);
          setCsamVerifier(csam.csamVerifier);
          setIsPermissionServiceAvailable(true);
        } else {
          await response.text().catch(() => '');
          // Status only — a permission-service body can name the tenant.
          reportOpsError('PERMISSION_SERVICE_UNAVAILABLE', `HTTP ${response.status}`);
          setIsUserAdmin(false);
          setCsamLimitedUser(false);
          setCsamVerifier(false);
          setIsPermissionServiceAvailable(false);
        }
      } catch (error) {
        console.error('Failed to fetch user admin status (permission service):', error);
        reportOpsError('PERMISSION_SERVICE_UNAVAILABLE', describeErrorForOps(error));
        setIsUserAdmin(false);
        setCsamLimitedUser(false);
        setCsamVerifier(false);
        setIsPermissionServiceAvailable(false);
      } finally {
        setCsamCredentialsReady(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!flagsReady) return;
    if (featureFlags?.supportFallbackForm || !allowsNonEssentialCookies) {
      safeHideIntercom();
      safeShutdownIntercom();
      return;
    }

    const intercomOn =
      authState?.isAuthenticated && !!authState?.idToken;
    const intercomUserReady =
      !!authState?.idToken?.claims.email &&
      authState?.idToken?.claims.email !== '' &&
      !!authState?.idToken?.claims.name &&
      authState?.idToken?.claims.name !== '';

    if (!intercomOn || !intercomUserReady || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal') {
      safeHideIntercom();
      safeShutdownIntercom();
      return;
    }

    if (intercomJwt) {
      const intercomInit = {
        intercom_user_jwt: intercomJwt,
        app_id: 'bab3jdz3',
        user_id: authState?.idToken?.claims.email,
        name: authState?.idToken?.claims.name,
        email: authState?.idToken?.claims.email,
      };
      Intercom(intercomInit);
    } else {
      // JWT still loading or refresh in flight — do not shutdown (avoids widget flicker).
      safeHideIntercom();
    }
  }, [
    flagsReady,
    featureFlags?.supportFallbackForm,
    allowsNonEssentialCookies,
    intercomJwt,
    authState?.isAuthenticated,
    authState?.idToken,
    authState?.idToken?.claims.email,
    authState?.idToken?.claims.name,
    selectedView,
  ]);

  const firstNonEmptyString = (...values: unknown[]) =>
    values.find((value): value is string => typeof value === 'string' && value.trim() !== '')?.trim() ?? '';

  const initFeatures = features as {
    tenantId?: string;
    tenantFriendlyName?: string;
    cellId?: string;
  } | undefined;
  const userIdentity = {
    userId: (authState?.idToken?.claims?.sub as string | undefined) ?? '',
    email: (authState?.idToken?.claims?.email as string | undefined) ?? '',
    userName: (authState?.idToken?.claims?.name as string | undefined) ?? '',
    tenantId: firstNonEmptyString(
      initFeatures?.tenantId,
      features?.orgId,
      authState?.accessToken?.claims?.['custom:tenant_id'],
    ),
    tenantName: firstNonEmptyString(
      initFeatures?.tenantFriendlyName,
      authState?.accessToken?.claims?.['custom:tenant_name'],
    ),
    cellId: firstNonEmptyString(
      initFeatures?.cellId,
      authState?.accessToken?.claims?.['custom:cell_id'],
    ),
    orgId: (features?.orgId as string | undefined) ?? '',
    partition: (authState?.accessToken?.claims?.['custom:partition'] as string | undefined) ?? '',
    systemStage: firstNonEmptyString(
      features?.systemStage,
      authState?.accessToken?.claims?.['custom:env'],
    ),
  };

  useIntercomTicketForm({
    enabled: flagsReady && !!authState?.isAuthenticated && !featureFlags?.supportFallbackForm,
    caseId: caseId ?? '',
    selectedView: selectedView ?? '',
    chatSessionId: chatSessionId ?? '',
    identity: userIdentity,
  });

  useEffect(() => {
    const detectCognitoUser = () => {
      try {
        const tokenIssuer = authState?.idToken?.issuer || authState?.idToken?.claims?.iss;

        if (tokenIssuer && typeof tokenIssuer === 'string') {
          const isCognito = tokenIssuer.includes('cognito');

          if (isCognito && !isCognitoUser) {
            setIsCognitoUser(true);
          } else if (!isCognito && isCognitoUser) {
            setIsCognitoUser(false);
          }
        }
      } catch {
        /* ignore issuer parse / state edge cases */
      }
    };

    // Only run detection if we have an authenticated user
    if (authState?.isAuthenticated) {
      detectCognitoUser();
    }
  }, [authState?.isAuthenticated, authState?.idToken, isCognitoUser]);

  useEffect(() => {
    const resolveJobEpochTimeSecs = (job: any): number =>
      Number(job?.epochTimeSecs ?? job?.epoch_time_secs);

    const resolveQuestionForJob = (epochTimeSecs: number): string => {
      if (!Number.isFinite(epochTimeSecs)) {
        return '';
      }
      const newAnswerEpochTimeSecs = Number(newAnswer?.epochTimeSecs);
      if (newAnswerEpochTimeSecs === epochTimeSecs && newAnswer?.question) {
        return newAnswer.question;
      }
      if (chatHistorySessions && chatHistorySessions.length > 0) {
        for (const session of chatHistorySessions) {
          for (const question of session.questions) {
            if (Number(question.epochTime) === epochTimeSecs) {
              return question.question;
            }
          }
        }
      }
      return '';
    };

    const entityExtractionJobsWithQuestions = entityExtractionJobs.map((job: any) => {
      const epochTimeSecs = resolveJobEpochTimeSecs(job);
      return {
        ...job,
        ...(Number.isFinite(epochTimeSecs) ? { epochTimeSecs } : {}),
        question: resolveQuestionForJob(epochTimeSecs),
      };
    });

    if (entityServicePollAllJobsCount > 0 && entityExtractionJobsWithQuestions.length > 0) {
      if (!entityExtractionJobsWithQuestions.some((job: any) => job.status === 'pending')) {
        setEntityServicePollAllJobsCount(0);
      }
    }

    setEntityExtractionJobsWithQuestions(entityExtractionJobsWithQuestions);
  }, [entityExtractionJobs, chatHistorySessions, newAnswer]);

  const setMainChatSessionId = (sessionId: string | null) => {
    setChatSessionId(sessionId);
  };

  const refreshChatHistorySessions = useCallback(
    async (options?: RefreshChatHistorySessionsOptions) => {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      const userId = authState?.idToken?.claims.email ?? '';
      if (!cellId || !realAccessToken || !userId) {
        if (options?.showLoading) setChatHistorySessions([]);
        return;
      }
      if (options?.showLoading) {
        setChatHistorySessions(null);
      }
      const caseFolderName = await resolveCaseFolderNameFor(caseId);
      const fetchOnce = async (): Promise<ChatSessionRow[]> => {
        const data = await fetchChatSessionsFromArchive({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          body: {
            caseId: caseId ?? '',
            userId,
            ...caseFolderNameBody(caseFolderName),
          },
        });
        const sessions = data.sessions
          ? (Object.values(data.sessions) as ChatSessionRow[])
          : [];
        return sortChatSessionsByLatestQuery(sessions);
      };
      try {
        let sorted = await fetchOnce();
        setChatHistorySessions(sorted);
        const turnMissing = () =>
          !archiveHasExpectedTurn(sorted, {
            expectedSessionId: options?.expectedSessionId,
            expectedQuestionEpoch: options?.expectedQuestionEpoch,
          });
        if (turnMissing()) {
          for (const delayMs of [400, 1200]) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            sorted = await fetchOnce();
            setChatHistorySessions(sorted);
            if (!turnMissing()) break;
          }
        }
      } catch (error) {
        captureError('Could not load chat sessions.', error as Error);
        setChatHistorySessions((prev) => prev ?? []);
      }
    },
    [
      caseId,
      authState?.idToken?.claims.email,
      authState?.idToken?.idToken,
      realAccessToken,
      resolveCaseFolderNameFor,
    ],
  );

  useEffect(() => {
    if (!flagsReady) return;
    if (caseId && caseId !== '' && authState?.idToken?.claims.email && authState?.idToken?.claims.email !== '') {
      void refreshChatHistorySessions({ showLoading: true });
    }
  }, [flagsReady, caseId, authState?.idToken?.claims.email, authState?.idToken?.idToken, realAccessToken, refreshChatHistorySessions]);

  const swapSessionId = async (sessionId: string | null, newSessionId: string): Promise<string | null> => {
    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return null;
      const historyResponseJson = await swapChatSession({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        body: {
          userId: authState?.idToken?.claims.email ?? '',
          caseId: caseId ?? '',
          ...caseFolderNameBody(await resolveCaseFolderNameFor(caseId)),
          sessionId: newSessionId,
          deleteSessionId: sessionId ?? null,
        },
      });
      return historyResponseJson.sessionId ?? null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const allSources = sourcesFromChat;
    const jsonMediaDisplayEnabled = isJsonMediaEnabled(featureFlags);
    const mediaSources = sourcesFromChat.flatMap((source: MediaSource): MediaSource[] => {
      const preferredDisplay = resolvePreferredMediaDisplayForSource(
        source,
        source.fileInfo,
      );
      const preferredUrl = preferredDisplay.mediaUrl;
      const hasDisplayableUrl =
        !!preferredUrl &&
        !isArchiveMemberSearchablePath(preferredUrl) &&
        isDisplayableMediaType(preferredDisplay.mediaType, {
          jsonMediaDisplay: jsonMediaDisplayEnabled,
        });
      const contentOnlyJson =
        !hasDisplayableUrl &&
        isInlineJsonCitationDisplayable(
          source,
          preferredDisplay.mediaType,
          jsonMediaDisplayEnabled,
        );
      if (!hasDisplayableUrl && !contentOnlyJson) {
        return [];
      }
      const mediaType = (contentOnlyJson ? 'json' : preferredDisplay.mediaType) as MediaType;
      const mediaUrlToUse = hasDisplayableUrl ? preferredUrl! : '';
      const mediaObjectFromSource: MediaSource = {
        ...source,
        mediaType,
        ...(mediaUrlToUse
          ? { mediaSrc: mediaUrlToUse, mediaUrl: mediaUrlToUse }
          : { mediaSrc: undefined, mediaUrl: undefined }),
        content: source.content,
        mediaContext: source.sourceContext ? { ...source.sourceContext } : {},
      };
      if (source.pageNumber) {
        mediaObjectFromSource.mediaContext!.pageNumber = source.pageNumber;
      }
      if (mediaUrlToUse && source.sourceUrl && mediaUrlToUse !== source.sourceUrl) {
        mediaObjectFromSource.mediaContext!.previewUrl = mediaUrlToUse;
      }
      return [mediaObjectFromSource];
    });
    const dedupedSources = dedupeSources(allSources);
    const dedupedMediaSources = dedupeMediaSources(mediaSources);
    setAllSourcesFromChat({ allSources: dedupedSources, mediaSources: dedupedMediaSources });
  }, [sourcesFromChat, featureFlags?.jsonMediaDisplay, featureFlags?.mediaPlayerTransition, featureFlags?.['media-player-transition']]);

  useEffect(() => {
    if (addEvent?.id && addEvent?.id.split('_')[0] === 'NEW') {
      setEventIndex(-1);
      handleOpenEventModal();
    }
  }, [addEvent]);

  const openTitleSlideModal = () => {
    setIsTitleSlideModalOpen(true);
  }
  const closeTitleSlideModal = () => {
    setIsTitleSlideModalOpen(false);
  }

  const openModalFromTable = (event: Slide, column?: string | null) => {
    setCurrentColumn(column || null);
    const isNewEventFromChat = event && event.id && event.id.includes('NEW_EVENT_FROM_CHAT');
    const isNewEventFromTable = column === 'create' || (event && event.id && event.id.includes('NEW_EVENT_FROM_TABLE'));
    const isDuplicateEventFromTable = column === 'duplicate' || (event && event.id && event.id.includes('NEW_DUPLICATE_FROM_TABLE'));
    const isNewEvent = isNewEventFromChat || isNewEventFromTable || isDuplicateEventFromTable;
    if(column !== 'timelineTitle' && !isNewEvent) {
      setEventIndex(event.displayOrder ? event.displayOrder - 1 : -1);
    }
    if(isDuplicateEventFromTable) {
      setCurrentColumn('duplicate');
    } else if(isNewEventFromChat) {
      setCurrentColumn('newEventFromChat');
    } else if(isNewEventFromTable) {
      setCurrentColumn('create');
    }
    setIsEventModalOpen(true);
  }

  const openModalFromPresentation = (slide: Slide, column: string | null) => {
    setCurrentColumn(column);
    setEventIndex(slide.displayOrder ? slide.displayOrder - 1 : -1);
    setIsEventModalOpen(true);
  }

  const handleOpenEventModal = () => {
    setIsEventModalOpen(true);
  };

  const clearPendingChatEvent = () => {
    pendingChatEventRef.current = undefined;
    setChatEventToAdd(undefined);
  };

  const getActiveChatEventToAdd = (): EventFromChat | undefined => {
    return pendingChatEventRef.current ?? chatEventToAdd;
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    // If user came from timeline selection flow, dismiss that modal too
    if (pleaseSelectTimeline || chatEventToAdd || pendingChatEventRef.current) {
      setPleaseSelectTimeline(false);
      clearPendingChatEvent();
      setModalSelectedTimeline(undefined);
    }
  };

  useEffect(() => {
    currentTimelineIdRef.current = currentTimelineId;
  }, [currentTimelineId]);

  const fetchNewTimeline = (
    documentIdentifier: string | undefined,
    caseId: string | undefined,
    isUpdating: boolean,
    creatingEventFromChat?: boolean,
    options: FetchNewTimelineOptions = {},
  ): Promise<boolean> => {
    // Only plain reads are shared. An update, a chat-driven load, or a load with
    // options must run on its own, because the caller depends on its side effects.
    const isPlainRead =
      !isUpdating && !creatingEventFromChat && Object.keys(options).length === 0;
    const readKey = `${(caseId ?? '').trim()}::${(documentIdentifier ?? '').trim()}`;
    // Join only while the shared load is still the newest one and no timeline
    // write started after it. After a switch away and back, the old load drops
    // its result as superseded, so a join would never apply the selection. After
    // a write (a delete refetches as a plain read), the old GET can return the
    // state from before the write.
    const inFlight = isPlainRead ? inFlightTimelineReadsRef.current.get(readKey) : undefined;
    if (
      inFlight &&
      inFlight.generation === timelineLoadRef.current.generation &&
      inFlight.writeCount === getTimelineWriteCount()
    ) {
      return inFlight.load;
    }
    const writeCount = getTimelineWriteCount();
    const load = loadTimeline(documentIdentifier, caseId, isUpdating, creatingEventFromChat, options);
    if (isPlainRead) {
      // loadTimeline claims its generation before its first await, so the ref
      // already holds this load's generation here.
      inFlightTimelineReadsRef.current.set(readKey, {
        load,
        generation: timelineLoadRef.current.generation,
        writeCount,
      });
      const release = () => {
        if (inFlightTimelineReadsRef.current.get(readKey)?.load === load) {
          inFlightTimelineReadsRef.current.delete(readKey);
        }
      };
      load.then(release, release);
    }
    return load;
  };

  const loadTimeline = async (
    documentIdentifier: string | undefined,
    caseId: string | undefined,
    isUpdating: boolean,
    creatingEventFromChat: boolean | undefined,
    options: FetchNewTimelineOptions,
  ): Promise<boolean> => {
    lastTimelineLoadWafBlockedRef.current = false;
    if (!options.preserveCurrentTimelineOnError) {
      setTimelineTags([]);
    }
    setPleaseSelectTimeline(false);

    if (
      (!documentIdentifier || documentIdentifier.trim() === '') &&
      (!caseId || caseId.trim() === '')
    ) {
      setLoadingTimeline(false);
      return false;
    }

    // Bail before clearing selection when auth isn't ready so restore/retry can
    // keep the intended timeline id and try again once tokens arrive.
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) {
      return false;
    }

    // Claimed after the bail-outs above, which must not invalidate a load already
    // in flight, and before the first state write, so any attempt that starts
    // from here on supersedes this one.
    const loadGeneration = timelineLoadRef.current.generation + 1;
    timelineLoadRef.current = {
      generation: loadGeneration,
      requestedId: (documentIdentifier ?? '').trim(),
    };
    if (!options.skipLoadingIndicator) {
      setLoadingTimeline(true);
    }
    if (!isUpdating) {
      setNewTimelineIsAvailable(false);
      setAllSlides(undefined);
      setCurrentTimelineId(undefined);
    }

    try {
      const response: any = await queryTimelines({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        docId: documentIdentifier,
        tags: await caseTimelineTagFor(caseId),
      });
      const noTimelinesFound = response && response.error && response.error.includes('No timelines found');
      if (response && (noTimelinesFound || response.timeline || response.timelineList)) {
        setTimelineServiceAppearsOperational(true);
      } else {
        setTimelineServiceAppearsOperational(false);
      }

      if (
        options.onlyUpdateIfCurrentCaseId &&
        caseIdRef.current !== options.onlyUpdateIfCurrentCaseId
      ) {
        setLoadingTimeline(false);
        return false;
      }

      if (
        options.onlyUpdateIfCurrentTimelineId &&
        currentTimelineIdRef.current !== options.onlyUpdateIfCurrentTimelineId
      ) {
        setLoadingTimeline(false);
        return true;
      }

      if (response && ((response.timelineList && response.timelineList.length > 0) || response.timeline)) {
        const timelineList = response.timelineList || [ response.timeline ];
        if (!options.preserveTimelineList) {
          setMultipleTimelinesAvailable(timelineList);
        }
        if (response?.timeline?.id) {
          setSelectedSecondOption(response?.timeline?.id);
          setLoadingTimeline(false);
        }
      } else {
        if (!options.preserveCurrentTimelineOnError) {
          setNewTimelineIsAvailable(false);
        }
        setLoadingTimeline(false);
        return false;
      }

      const timeline = response?.timeline;
      if (!timeline) {
        setLoadingTimeline(false);
        return false;
      }
      const tags = timeline?.tags ?? [];
      setTimelineTags(tags);
      const events = response?.timeline?.events;
      setTimeline(timeline);
      let newEvents = 0;
      const unsortedSlides = [];
      // Resolve per-event horizontal-timeline visibility: prefer the server
      // field when present, else fall back to the browser-local override.
      const timelineIdForVisibility = response?.timeline?.id;
      const locallyHiddenFromTimeline = getLocallyHiddenTimelineEventIds(timelineIdForVisibility);
      let useLocalDisplayOrder = true;
      if (events[0] && events[0].displayOrder !== undefined) {
        useLocalDisplayOrder = false;
      }
      setSortByDateTime(useLocalDisplayOrder);
      let localDisplayOrder = 0;
      for (const event of events) {
        if(event.deleted) continue;
        localDisplayOrder += 1;
        newEvents += 1;
        const mappedSlide = mapTimelineEventRecordToSlide(
          {
            ...(event as Record<string, unknown>),
            displayOrder: useLocalDisplayOrder
              ? localDisplayOrder
              : (event as { displayOrder?: number }).displayOrder,
          },
          localDisplayOrder,
          { fallbackId: `NEW_${newEvents}` },
        );
        mappedSlide.hideFromTimeline = resolveHideFromTimeline({
          serverValue: event.hiddenFromTimeline,
          eventId: event.id,
          locallyHidden: locallyHiddenFromTimeline,
        });
        unsortedSlides.push(mappedSlide);
      }

      if (response.timeline.title) {
        setTitle(response.timeline.title);
      } else {
        setTitle(undefined);
      }
      if (response.timeline.description) {
        setDescription(response.timeline.description);
      } else {
        setDescription(undefined);
      }
      if (response.timeline.courtroomTitle) {
        setCourtroomTitle(response.timeline.courtroomTitle);
      } else {
        setCourtroomTitle(undefined);
      }
      if (response.timeline.courtroomDescription) {
        setCourtroomDescription(response.timeline.courtroomDescription);
      } else {
        setCourtroomDescription(undefined);
      }
      if (response.timeline.media) {
        setMedia(response.timeline.media);
      } else {
        setMedia(undefined);
      }
      setIsEditableTimeline(true);
      setCurrentTimelineId(response.timeline.id);
      const sortedSlides = getSortedSlides(unsortedSlides, useLocalDisplayOrder ? 'datetime' : 'displayOrder');
      // A saved media row stores the rendition its file resolved to when the
      // event was saved, so a review that happened since then leaves it showing
      // the wrong one. Re-resolve content-moderated stills against provenance
      // before the rows reach the screen, uncached — the cached summary is held
      // for 15 minutes, long enough to outlive a review (TR-2153).
      // fetchFileInfoBatch is required. The per-URL getFileInfoFromServer is
      // only the fallback when POST /files/file-info is absent — do not remove
      // the batch option or this list becomes one GET per file.
      const resolvedSlides = await resolveTimelineMediaModeration(
        sortedSlides,
        (url) => getFileInfoFromServer(url, cellId, realAccessToken),
        {
          fetchFileInfoBatch: (urls) =>
            getFileInfoBatchFromServer(urls, cellId, realAccessToken),
        },
      );
      // Provenance answering is the second slow gap in this function, and unlike
      // the fetch above it sits between the id checks and the writes below. A
      // switch to a different timeline during it would otherwise land this load's
      // events on top of the newer one's, with the spinner already cleared at the
      // `setSelectedSecondOption` branch, so they read as finished loading.
      //
      // Only a switch *away* is a reason to drop the work. A newer load of the
      // same timeline (this function is called from several effects, and the
      // dedupe at the LaunchDarkly refetch only covers its own call site) clears
      // `allSlides` on entry and can then fail, and dropping resolved events for
      // the timeline still on screen would leave it blank with the spinner off.
      // Committing is safe there because both loads describe the same timeline:
      // if the newer one succeeds it overwrites this with equivalent events, and
      // if it fails the view keeps the events this load already resolved.
      //
      // Nothing is reported as failure either way. Callers raise a warning toast
      // on false, and being superseded is not this load's failure — the newer
      // one owns the outcome and its own caller handles it.
      const supersededByAnotherTimeline =
        loadGeneration !== timelineLoadRef.current.generation &&
        timelineLoadRef.current.requestedId !== String(response.timeline.id ?? '').trim();
      if (supersededByAnotherTimeline) {
        return true;
      }
      setAllSlides(resolvedSlides);
      syncTimelineEventCountInList(response.timeline.id, unsortedSlides.length);
      setTimelinePatch({
        tags,
        title: response.timeline.title,
        description: response.timeline.description,
        courtroomTitle: response.timeline.courtroomTitle ?? undefined,
        courtroomDescription: response.timeline.courtroomDescription ?? undefined,
        media: response.timeline.media,
      });
      setNewTimelineIsAvailable(true);
      if (!creatingEventFromChat) {
        setLoadingTimeline(false);
      } else if (creatingEventFromChat && parsedEventsFromChat) {
        await createEventsFromParsedEvents(parsedEventsFromChat, useEnrichment, false, response.timeline.id);
        clearPendingChatEvent();
      } else if (creatingEventFromChat && getActiveChatEventToAdd()) {
        proceedWithChatEvent(response.timeline.id);
        const activeChatEvent = getActiveChatEventToAdd();
        if (!activeChatEvent?.autogen || !creatingEventFromChat) {
          setLoadingTimeline(false);
        }
      } else {
        setLoadingTimeline(false);
      }
      return true;
    } catch (error) {
      if (!options.preserveCurrentTimelineOnError) {
        setNewTimelineIsAvailable(false);
      }
      setLoadingTimeline(false);
      if (isWafBlockError(error)) {
        lastTimelineLoadWafBlockedRef.current = true;
        notifyWafBlock();
      }
      const errorMessage = 'Error fetching new timeline.';
      captureError(errorMessage, error as Error);
      return false;
    }
  };
  fetchNewTimelineRef.current = fetchNewTimeline;

  // Fetch work products (timelines list) for a specific case - lightweight version
  const fetchWorkProductsForCase = async (caseId: string): Promise<TimelineDropdownItem[]> => {
    if (!caseId || caseId.trim() === '' || !accessToken || !authState?.idToken?.idToken) {
      return [];
    }

    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return [];
      const response: any = await queryTimelines({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        docId: undefined,
        tags: await caseTimelineTagFor(caseId),
      });

      if (response && response.timelineList && response.timelineList.length > 0) {
        return response.timelineList;
      } else if (response && response.timeline) {
        return [response.timeline];
      }
      return [];
    } catch (error) {
      // Silently fail for pre-fetching - don't spam errors
      console.warn(`Error fetching work products for case ${caseId}:`, error);
      // Except a WAF block: the empty list would read as "this case has no
      // timelines" (TR-3920).
      if (isWafBlockError(error)) notifyWafBlock();
      return [];
    }
  };
  fetchWorkProductsForCaseRef.current = fetchWorkProductsForCase;

  const handleEditEvent = async (editedEvent: Slide): Promise<EventSaveResult> => {
    // check for time/date change, if so, refetch timeline
    let shouldRefetchTimeline = true;
    const currentEvent = allSlides?.find(event => event.id === editedEvent.id);
    const currentEventDate = currentEvent?.date ? getDateFromDateStr(currentEvent.date)?.toString() : null;
    const editedEventDate = editedEvent.date ? getDateFromDateStr(editedEvent.date)?.toString() : null;
    const currentEventTime = currentEvent?.time ? getClientTimeFromStr(currentEvent.time)?.toString() : null;
    const editedEventTime = editedEvent.time ? getClientTimeFromStr(editedEvent.time)?.toString() : null;
    if (currentEventTime !== editedEventTime) {
      shouldRefetchTimeline = true;
    } else if (currentEventDate !== editedEventDate) {
      shouldRefetchTimeline = true;
    } else {
      // shouldRefetchTimeline = false; should we ever not refectch
    }
    editedEvent.sources = editedEvent.sources ? editedEvent.sources.map((source: MediaSource) => {
      return {
        ...source,
        sourceType: Array.isArray(source.sourceType) ? source.sourceType[0] : source.sourceType,
      };
    }) : undefined;
    editedEvent.media = editedEvent.media ? editedEvent.media.map((media: MediaSource) => {
      return {
        ...media,
        mediaType: Array.isArray(media.mediaType) ? media.mediaType[0] : media.mediaType,
      };
    }) : undefined;
    if (!currentTimelineId) return { success: false };
    setLoadingTimeline(shouldRefetchTimeline);
    return updateEvent(editedEvent, shouldRefetchTimeline);
  }

  const patchTimeline = async (currentTimelineId: string, timelinePatchIn: TimelinePatch) => {
    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return;
      const timelinePatchToSend = {
        ...timelinePatchIn,
        tags: timelineTags?.length ? timelineTags : timelinePatchIn.tags,
      };
      const titleToSend = timelinePatchToSend.title || undefined;
      if (typeof titleToSend === 'string' && titleToSend.length > TIMELINE_TITLE_MAX_LENGTH) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.titleTooLong'),
          description: t('main.titleTooLongSave', { max: TIMELINE_TITLE_MAX_LENGTH }),
          level: ToastLevel.ERROR,
        });
        return;
      }

      const patchSources = (timelinePatchIn as TimelinePatch & { sources?: MediaSource[] }).sources;
      const patchMedia = timelinePatchToSend.media;
      let mediaForPatch: string | undefined;

      if (Array.isArray(patchMedia) && patchMedia.length > 0) {
        mediaForPatch =
          (await resolveTimelineMediaPersistUrl(patchMedia[0], patchSources?.[0], cellId, realAccessToken)) ||
          undefined;
      } else if (typeof patchMedia === 'string' && patchMedia.trim() !== '') {
        mediaForPatch =
          (await resolveTimelineMediaPersistUrl(patchMedia, patchSources?.[0], cellId, realAccessToken)) ||
          patchMedia;
      }

      if (!timelinePatchToSend.tags || timelinePatchToSend.tags.length < 1) {
        return;
      }
      await patchTimelineDirect({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        timelineId: currentTimelineId,
        patch: {
          tags: timelinePatchToSend.tags,
          title: titleToSend,
          description: timelinePatchToSend.description || undefined,
          courtroomTitle: timelinePatchToSend.courtroomTitle ?? '',
          courtroomDescription: timelinePatchToSend.courtroomDescription ?? '',
          media: mediaForPatch || undefined,
        },
      });
      await fetchNewTimeline(currentTimelineId, caseId, true);
    } catch (error) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.titleSlideUpdateFailed'),
        description: t('main.titleSlideUpdateFailedDescription'),
        level: ToastLevel.ERROR,
      });
    }
  }

  const onUpdateTimeline = async (action: 'delete' | 'update' | 'order', rows: Slide[]) => {
    if (rows && rows.length > 0 && timeline && action === 'delete') {
      let deletedEvents = 0;
      try {
        for (const row of rows) {
          if (await deleteEvent(row, false)) {
            deletedEvents += 1;
          }
        }
      } catch (error) {
        console.error('Error deleting events:', error);
      }
      // Sole error toast for batch delete: deleteEvent(row, false) skips per-event toasts.
      if (deletedEvents !== rows.length) {
        const plural = rows.length > 1;
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: plural ? 'Some Events Were Not Deleted' : 'Event Deletion Failed',
          description: plural
            ? 'Delete failed for some events. Please refresh the page and try again.'
            : 'Event deletion failed. Please try again.',
          level: ToastLevel.ERROR,
        });
      }
      await fetchNewTimeline(currentTimelineId, caseId, true);
    } else if (!sortByDateTime && rows && rows.length > 0 && timeline && action === 'order') {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken || !currentTimelineId) return;
      const order = rows.map(row => row.id).filter((id): id is string => Boolean(id));
      await reorderTimelineEvents({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        timelineId: currentTimelineId,
        order,
      });
      await fetchNewTimeline(currentTimelineId, caseId, true);
    }
  }

  const deleteTimeline = async (currentTimelineId: string) => {
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) {
      setLoadingTimeline(false);
      return;
    }
    setLoadingTimeline(true);
    try {
      await deleteTimelineDirect({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        timelineId: currentTimelineId,
      });
      setLoadingTimeline(false);

      setAllSlides(undefined);
      setCurrentTimelineId(undefined);
      setNewTimelineIsAvailable(false);
      setMultipleTimelinesAvailable([]);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.timelineDeleted'),
        description: t('main.timelineDeletedDescription'),
        level: ToastLevel.SUCCESS,
      });
      // Route to work products index when a case is selected, otherwise route to chat
      if (caseId) {
        setSelectedView('work-product-index');
        sessionStorage.setItem('view', 'work-product-index');
        // Refresh work products after deletion
        fetchWorkProductsForCase(caseId).then((workProducts) => {
          setMultipleTimelinesAvailable(workProducts);
        });
      } else {
        setSelectedView('chat');
      }
      await fetchNewTimeline(undefined, caseId, true);
    } catch (error) {
      console.error('Error Deleting timeline:', error);
      setLoadingTimeline(false);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.timelineDeletionFailed'),
        description: t('main.timelineDeletionFailedDescription'),
        level: ToastLevel.ERROR,
      });
    }
  }

  const handleDeleteTimeline = async () => {
    if(!currentTimelineId) return;
    setAllSlides(undefined);
    setSelectedSecondOption(undefined);
    setLoadingTimeline(true);
    deleteTimeline(currentTimelineId);
  };

  const handlePatchTimeline = async(timelinePatch: TimelinePatch) => {
    if(!currentTimelineId) return;
    patchTimeline(currentTimelineId, timelinePatch);
  };

  const createNewTimeline = async (timelineTitle: string, creatingEventFromChatIn?: boolean) => {
    setTimelineCreated(false);
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken || !caseId) return undefined;
    try {
      setCreatingTimeline(true);
      const response: any = await createTimeline({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        title: timelineTitle || caseId,
        description: t('main.newTimelineFor', { case: timelineCaseLabel(caseId, t) }),
        caseId,
        caseFolderName: await resolveCaseFolderNameFor(caseId),
      });
      setCreatingTimeline(false);
      if (response.timeline && response.timeline.title === (timelineTitle || caseId)) {
        setCurrentTimelineId(response.timeline.id);
        setTimelineCreated(true);
        if (creatingEventFromChatIn && !chatEventToAdd?.autogen) {
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        } else if (creatingEventFromChatIn && chatEventToAdd?.autogen) {
          // await createEventFromChat(chatEventToAdd, response.timeline.id);
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        } else {
          await fetchNewTimeline(response.timeline.id, caseId, false, !!creatingEventFromChatIn);
        }
        if(!creatingEventFromChatIn && !chatEventToAdd?.autogen) {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 5000,
            title: t('main.timelineCreated'),
            description: t('main.timelineCreatedDescription'),
            level: ToastLevel.SUCCESS,
            actionButtonText: t('main.goToTimelineDetails'),
            actionButtonOnClick: () => {
              setSelectedView('table');
            },
          });
        }
        return response?.timeline;
      } else {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: t('main.createTimelineError'),
          description: t('main.couldNotCreateTimelineDescription'),
          level: ToastLevel.ERROR,
        });
      }
      return response;
    } catch (error) {
      setCreatingTimeline(false);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.createTimelineError'),
        description: t('main.couldNotCreateTimelineDescription'),
        level: ToastLevel.ERROR,
      });
      return undefined;
    }
  }

  const applySlideToLocalTimeline = (savedSlide: Slide) => {
    const slides = allSlidesRef.current;
    if (!slides) return;
    const nextSlides = slides.map((event) =>
      event.id === savedSlide.id ? { ...event, ...savedSlide } : event,
    );
    allSlidesRef.current = nextSlides;
    setAllSlides(nextSlides);
  };

  const enqueueEventPersist = (eventId: string, task: () => Promise<unknown>) => {
    const key = eventId.trim();
    if (!key) return Promise.resolve();
    const previous = eventWriteChainsRef.current.get(key) ?? Promise.resolve();
    const next = previous.catch(() => {}).then(() => task());
    eventWriteChainsRef.current.set(key, next);
    void next.finally(() => {
      if (eventWriteChainsRef.current.get(key) === next) {
        eventWriteChainsRef.current.delete(key);
      }
    });
    return next;
  };

  const updateEvent = async (
    updatedEvent: Slide,
    shouldRefetchTimeline: boolean = true,
  ): Promise<EventSaveResult> => {
    if (!currentTimelineId || !updatedEvent.id) return { success: false };
    if (shouldRefetchTimeline) {
      setLoadingTimeline(true);
    }
    try {
      return (await enqueueEventPersist(String(updatedEvent.id), async () => {
        const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
        if (!cellId || !realAccessToken) {
          if (shouldRefetchTimeline) {
            setLoadingTimeline(false);
          }
          return { success: false };
        }
        const { event: apiEventRaw } = await updateTimelineEvent({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          docId: currentTimelineId,
          event: updatedEvent as unknown as Record<string, unknown>,
        });
        let savedSlide: Slide = updatedEvent;
        if (apiEventRaw && typeof apiEventRaw === 'object') {
          savedSlide = mapTimelineEventRecordToSlide(
            apiEventRaw as Record<string, unknown>,
            updatedEvent.displayOrder ?? 0,
            { mergeWith: updatedEvent },
          );
        }
        if (shouldRefetchTimeline) {
          await fetchNewTimeline(currentTimelineId, caseId, true);
        } else {
          applySlideToLocalTimeline(savedSlide);
        }
        return {
          success: true,
          event: savedSlide,
          ...(apiEventRaw && typeof apiEventRaw === 'object'
            ? { serverRecord: apiEventRaw as Record<string, unknown> }
            : {}),
        };
      })) as EventSaveResult;
    } catch (error) {
      if (shouldRefetchTimeline) {
        setLoadingTimeline(false);
      }
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.eventUpdateFailed'),
        description: t('main.eventUpdateFailedDescription'),
        level: ToastLevel.ERROR,
      });
      const errorMessage = 'Could not update event.';
      captureError(errorMessage, error as Error);
      return { success: false };
    }
  }

  const { pdfCaptureHost } = useTimelineThumbnailBackfill({
    slides: allSlides,
    timelineId: currentTimelineId,
    isEditableTimeline,
    loadingTimeline,
    caseId,
    cellId: resolveCellIdFromIdToken(authState?.idToken?.idToken || '') || null,
    accessToken: realAccessToken,
    tenantFriendlyName:
      typeof featureFlags?.tenantFriendlyName === 'string'
        ? featureFlags.tenantFriendlyName
        : undefined,
    updateEvent,
  });

  // Toggle whether a single event is shown in the horizontal timeline view.
  // Optimistic local state + localStorage fallback, with best-effort server
  // persistence (no refetch; failures are swallowed so a backend that doesn't
  // yet support `hiddenFromTimeline` cannot disrupt the toggle).
  const toggleTimelineVisibility = async (slide: Slide) => {
    if (!slide?.id) return;

    const current = allSlidesRef.current?.find((s) => s.id === slide.id) ?? slide;
    const nextHidden = !current.hideFromTimeline;
    const nextSlide: Slide = { ...current, hideFromTimeline: nextHidden };

    setAllSlides((prev) => {
      const nextSlides = prev?.map((s) => (s.id === slide.id ? nextSlide : s));
      allSlidesRef.current = nextSlides;
      return nextSlides;
    });

    setLocalTimelineEventHidden(currentTimelineId, slide.id, nextHidden);

    const eventId = slide.id;
    void enqueueEventPersist(eventId, async () => {
      if (!currentTimelineId) return;
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return;
      const latest = allSlidesRef.current?.find((s) => s.id === eventId);
      if (!latest) return;
      try {
        await updateTimelineEvent({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          docId: currentTimelineId,
          event: latest as unknown as Record<string, unknown>,
        });
      } catch {
        // Server may not support `hiddenFromTimeline` yet (or a transient failure);
        // the localStorage fallback already holds the state for this browser.
      }
    });
  };

  const createEvent = async (
    newEvent: Slide,
    timelineId: string | null,
    shouldRefetchTimeline: boolean = true,
  ): Promise<EventSaveResult> => {
    if(!currentTimelineId && !timelineId) return { success: false };
    // `shouldRefetchTimeline: false` means the caller owns `loadingTimeline`
    // (batch create keeps the spinner up across events; batch enrich keeps the
    // table mounted). Do not set or clear it on this path.
    if (shouldRefetchTimeline) {
      setLoadingTimeline(true);
    }
    const timelineIdToUse = currentTimelineId || timelineId;
    newEvent.sources = newEvent.sources ? newEvent.sources.map((source: MediaSource) => {
      return {
        ...source,
        sourceType: Array.isArray(source.sourceType) ? source.sourceType[0] : source.sourceType,
      };
    }) : undefined;
    newEvent.media = newEvent.media ? newEvent.media.map((media: MediaSource) => {
      return {
        ...media,
        mediaType: Array.isArray(media.mediaType) ? media.mediaType[0] : media.mediaType,
      };
    }) : undefined;
    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken || !timelineIdToUse) {
        if (shouldRefetchTimeline) {
          setLoadingTimeline(false);
        }
        return { success: false };
      }
      await createTimelineEvent({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        docId: timelineIdToUse,
        event: newEvent as unknown as Record<string, unknown>,
      });
      bumpTimelineEventCount(timelineIdToUse, 1);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.eventCreated'),
        description: t('main.eventCreatedDescription'),
        level: ToastLevel.SUCCESS,
      });
      if (shouldRefetchTimeline) {
        await fetchNewTimeline(timelineIdToUse || undefined, caseId, true);
      }
      return { success: true, event: newEvent };

    } catch (error) {
      if (shouldRefetchTimeline) {
        setLoadingTimeline(false);
      }
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.eventCreationFailed'),
        description: t('main.eventCreationFailedDescription'),
        level: ToastLevel.ERROR,
      });

      const errorMessage = 'Could not create event.';
      captureError(errorMessage, error as Error);
      return { success: false };
    }
  }

  const enrichEvent = async (formData: Slide, options: EnrichEventOptions = {}) => {
    const { useEventSourcesAsScope = false, keepCurrentMedia = false } = options;
    let query = '';
    if (featureFlags?.enrichEventPrompt && (featureFlags?.enrichEventPrompt as string).trim() !== '') {
      const title = formData?.slideTitle || undefined;
      const description = formData?.slideDescription || '';
      query = featureFlags?.enrichEventPrompt as string;
      if (title && title.trim() !== '') {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '$1');
        query = query.replace('{title}', title);
      } else {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '');
      }
      if (description && description.trim() !== '') {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '$1');
        query = query.replace('{description}', description);
      } else {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '');
      }
    } else {
      query = 'Tell me more about';
      let useAnd = false;
      if (formData?.slideTitle && formData?.slideTitle.trim() !== '') {
        query += ` "${formData?.slideTitle} as it relates to this case"`;
        useAnd = true;
      }
      if (formData?.slideDescription && formData?.slideDescription.trim() !== '') {
        query += `${useAnd ? ' and' : ''} "${formData?.slideDescription} as it relates to this case"`;
        useAnd = true;
      }
    }
    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) return formData as Slide;
      const idTokenInfo = getTokenInfo(authState?.idToken?.idToken || '');
      const accessTokenInfo = getTokenInfo(realAccessToken || '');
      const userId = getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
      let body: any = {
        enrichEvent: true,
        deepDive: false,
        threatReport: false,
        query: query,
        selectedCase: caseId,
        ...(await caseFolderNameFields(caseId)),
        files: useEventSourcesAsScope ? getEventSourceUrlsForScope(formData.sources) : [],
        scopes: [],
        excludedFiles: [],
        excludedScopes: [],
        userId: userId,
        sessionId: null,
      };
      const caseApiName = getCaseInternalName(caseId).trim();
      if (caseApiName && caseApiName !== '*') {
        body.caseName = caseApiName;
      }
      const displayUser = String(authState?.idToken?.claims?.name ?? '').trim();
      if (displayUser) {
        body.userName = displayUser;
      }
      const responseLanguage = responseLanguageForLocale(readStoredLocale());
      if (responseLanguage) body.responseLanguage = responseLanguage;
      let result = await postAssistantChat({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        body,
      });
      body = {
        enrichEvent: result?.enrich_event ?? false,
        sessionId: result?.sessionId,
        epochTimeSecs: result?.epoch_time_secs,
        chatKey: result?.chat_key,
        requestType: result?.request_type,
      }
      const startTime = Date.now();
      while (isEnrichmentHistoryPending(result?.status) && Date.now() - startTime < ENRICHMENT_POLL_TIMEOUT_MS) {
        await new Promise(resolve => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
        result = await fetchChatHistory({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          body,
        }) as Record<string, unknown>;
      }
      if (result?.status === 'error') {
        return formData as Slide;
      }
      const answer = JSON.parse(result?.answer as string);
      const enrichment = computeEnrichmentFromApiResponse(
        answer,
        isJsonMediaEnabled(featureFlags),
      );
      if (enrichment) {
        const enrichedSources = enrichment.sources ?? [];
        const enrichedMedia = enrichment.media ?? [];
        const newSources = dedupeSources([...enrichedSources]);
        const newMedia = mergeEnrichmentMedia(formData.media ?? [], enrichedMedia, keepCurrentMedia);
        const currentEvent = formData;
        const newFormData = {
          id: currentEvent?.id || null,
          displayOrder: currentEvent?.displayOrder || -1,
          date: currentEvent?.date ? currentEvent.date : null,
          displayDate: currentEvent?.displayDate ? currentEvent.displayDate : currentEvent?.date || null,
          time: currentEvent?.time ? currentEvent.time : null,
          isTimeValid: currentEvent?.isTimeValid === true ? true : false,
          eventLocalTimezone: currentEvent?.eventLocalTimezone || null,
          slideTitle: currentEvent?.slideTitle || '',
          slideSubTitle: currentEvent?.slideSubTitle || undefined,
          slideDescription: enrichment.slideDescription || currentEvent?.slideDescription || '',
          eventType: currentEvent?.eventType || '',
          sources: newSources,
          media: newMedia,
          show: currentEvent?.show || false,
          hideFromTimeline: currentEvent?.hideFromTimeline === true,
        };
        return newFormData as Slide;
      }
      return formData as Slide;
    } catch {
      return formData as Slide;
    }
  }

  const autogeneratedTimelineAppliedKey = (timelineId: string, epochTimeSecs: number) =>
    `${timelineId}:${epochTimeSecs}`;

  const isAutogeneratedTimelineAppliedForAnswer = useCallback(
    (epochTimeSecs: number) => {
      void autogeneratedTimelineAppliedTick;
      const timelineId = currentTimelineId?.trim();
      if (!timelineId) {
        return false;
      }
      return autogeneratedTimelineAppliedKeysRef.current.has(
        autogeneratedTimelineAppliedKey(timelineId, epochTimeSecs),
      );
    },
    [currentTimelineId, autogeneratedTimelineAppliedTick],
  );

  const isAutogeneratedTimelineApplyingForAnswer = useCallback(
    (epochTimeSecs: number) => autogenTimelineApplyEpochInFlight === epochTimeSecs,
    [autogenTimelineApplyEpochInFlight],
  );

  const markAutogeneratedTimelineApplied = (timelineId: string, epochTimeSecs: number) => {
    autogeneratedTimelineAppliedKeysRef.current.add(
      autogeneratedTimelineAppliedKey(timelineId, epochTimeSecs),
    );
    setAutogeneratedTimelineAppliedTick((n) => n + 1);
  };

  const clearPendingAutogeneratedTimelineApply = () => {
    pendingAutogenAnswerEpochRef.current = null;
    setAutogenTimelineApplyEpochInFlight(null);
  };

  const slidesFromAutogeneratedChatAnswer = async (answerText: string): Promise<Slide[] | null> => {
    const rawEvents = parseAutogeneratedTimelineEvents(answerText);
    if (!rawEvents?.length) {
      return null;
    }
    const slides = autogeneratedTimelineEventsToSlides(rawEvents).sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    );
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) {
      return slides;
    }
    return resolveAutogeneratedTimelineSlides(slides, cellId, realAccessToken);
  };

  const queueParsedEventsForTimelineSelection = (parsedEvents: Slide[], enriching = false) => {
    if (!caseId || caseId.trim() === '') {
      return;
    }
    if (!parsedEvents || parsedEvents.length === 0) {
      return;
    }
    clearPendingChatEvent();
    setUseEnrichment(enriching);
    setParsedEventsFromChat(parsedEvents);
    setModalSelectedTimeline(undefined);
    setPleaseSelectTimeline(true);
  };

  /** Parse ``AUTOGENERATED_TIMELINE:`` JSON from chat and create timeline events (no entity extract API). */
  const applyAutogeneratedTimelineFromAnswer = async (answerText: string, epochTimeSecs: number) => {
    if (!caseId || caseId.trim() === '') {
      return;
    }
    const timelineId = currentTimelineId?.trim();
    if (timelineId && isAutogeneratedTimelineAppliedForAnswer(epochTimeSecs)) {
      return;
    }
    if (autogenTimelineApplyEpochInFlight === epochTimeSecs) {
      return;
    }
    setAutogenTimelineApplyEpochInFlight(epochTimeSecs);
    pendingAutogenAnswerEpochRef.current = epochTimeSecs;
    try {
      const slides = await slidesFromAutogeneratedChatAnswer(answerText);
      if (!slides?.length) {
        clearPendingAutogeneratedTimelineApply();
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.couldNotCreateTimeline'),
          description: t('main.noAutogeneratedEvents'),
          level: ToastLevel.INFO,
        });
        return;
      }
      if (timelineId) {
        await createEventsFromParsedEvents(slides, false, false, timelineId);
        return;
      }
      queueParsedEventsForTimelineSelection(slides, false);
    } catch {
      clearPendingAutogeneratedTimelineApply();
    }
  };

  const createEventsFromParsedEvents = async (parsedEvents: Slide[], enriching: boolean, edit: boolean, timelineIdIn?: string, enrichOptions: EnrichEventOptions = {}) => {
    if(!caseId || caseId.trim() === '') {
      return;
    }
    if(!parsedEvents || parsedEvents.length === 0) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.parseFailed'),
        description: t('main.parseFailedDescription'),
        level: ToastLevel.INFO,
      });
      return;
    }
    let timelineId = timelineIdIn || currentTimelineId || null;
    if(!timelineId && parsedEvents.length > 0) { // force timeline selection
      setUseEnrichment(enriching);
      setParsedEventsFromChat(parsedEvents);
      setPleaseSelectTimeline(true);
      return;
    }
    setParsedEventsFromChat(undefined);
    setUseEnrichment(false);
    // Enriching selected events must keep the current table mounted. Setting
    // `loadingTimeline` here (and again after each persist) unmounted TPTable
    // onto the full-screen "Loading Timeline..." spinner after every event —
    // N blanks for N selected events, plus another in `fetchNewTimeline`.
    // Persist in place, then refresh the timeline once without unmounting.
    // Creating events still uses the spinner: those rows are not on the table yet.
    if (!edit) {
      setLoadingTimeline(true);
    }
    try {
      const sortedParsedEvents = edit ? parsedEvents : parsedEvents.sort((a, b) => {
        const aOrder = a.displayOrder;
        const bOrder = b.displayOrder;
        if (typeof aOrder === 'number' && typeof bOrder === 'number') {
          return aOrder - bOrder;
        }
        if (!a.date) {
          return -1;
        }
        if (!b.date) {
          return 1;
        }
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        return aDate.getTime() - bDate.getTime();
      });
      const count = sortedParsedEvents.length;
      const timer = enriching ? 120000 : 10000;
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: count * timer,
        title: `${edit ? 'Enriching' : 'Creating'} Event(s)`,
        description: `${edit ? 'Enriching' : 'Creating'} event(s), this may take a minute...`,
        level: ToastLevel.INFO,
        child: (
          <div className="flex flex-col gap-2 items-center justify-center">
            <Spinner size="sm" color="primary" />
          </div>
        ),
      });
      for (let i = 0; i < count; i++) {
        let newEvent: Slide = {
          ...sortedParsedEvents[i],
          show: sortedParsedEvents[i].show !== undefined ? sortedParsedEvents[i].show : true,
        }
        if (!edit) {
          if (sortedParsedEvents[i].slideTitle) {
            newEvent.slideTitle = sortedParsedEvents[i].slideTitle;
          }
          if (sortedParsedEvents[i].slideDescription) {
            newEvent.slideDescription = sortedParsedEvents[i].slideDescription;
          }
          if (sortedParsedEvents[i].date) {
            newEvent.date = sortedParsedEvents[i].date;
          }
        }
        if ((featureFlags?.['autoEnrichEvents'] || edit ) && enriching) {
          const enrichedEvent = await enrichEvent(newEvent, enrichOptions);
          if (enrichedEvent) {
            if (edit) {
              // Paint the enriched row immediately so progress is visible in the
              // table; persist without refetching. The batch still reloads once
              // after every selected event has been saved.
              applySlideToLocalTimeline(enrichedEvent as Slide);
              await updateEvent(enrichedEvent as Slide, false);
            } else {
              await createEvent(enrichedEvent as Slide, timelineId, false);
            }
          }
        } else {
          await createEvent(newEvent, timelineId, false);
        }
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: i === count - 1 ? 5000 : (i + 1) * timer,
          title: `${edit ? 'Enriched' : 'Created'} Event ${i + 1} of ${count}`,
          description: `${edit ? 'Enriched' : 'Created'} event ${i + 1} of ${count}, waiting for the rest...`,
          level: ToastLevel.SUCCESS,
          child: (
            <div className="flex flex-col gap-2 items-center justify-center">
              <Progress
                value={((i + 1) / count) * 100}
                size="lg"
                color="success"
                className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]"
              />
              <div className="text-md text-gray-800">
                {`${edit ? 'Enriched' : 'Created'} event ${i + 1} of ${count}`}
              </div>
            </div>
          ),
        });
      }
      try {
        if (entityJobId) {
          await getEventsFromEntityService('DELETE_JOB', timelineId || null, caseId || '', '', [], 0, entityJobId, false, 'user');
        }
      } catch (error) {
        // ignore
      }
      await fetchNewTimeline(timelineId || undefined, caseId, true, false, {
        skipLoadingIndicator: edit,
      });
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: `${edit ? 'Enriched' : 'Created'} Event(s)`,
        description: `${edit ? 'Enriched' : 'Created'} event(s) successfully.`,
        actionButtonText: selectedView === 'chat' ? t('main.goToTimelineDetails') : t('main.goToTimelineTable'),
        actionButtonOnClick: selectedView === 'chat' ? () => {
          setSelectedView('table');
        } : undefined,
        level: ToastLevel.SUCCESS,
      });
      const autogenEpoch = pendingAutogenAnswerEpochRef.current;
      if (autogenEpoch != null && timelineId) {
        markAutogeneratedTimelineApplied(timelineId, autogenEpoch);
      }
      clearPendingAutogeneratedTimelineApply();
    } catch (error) {
      setLoadingTimeline(false);
      clearPendingAutogeneratedTimelineApply();
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: `Event ${edit ? 'Enrichment' : 'Creation'} Failed`,
        description: `Event ${edit ? 'Enrichment' : 'Creation'} failed. Please try again.`,
        level: ToastLevel.ERROR,
      });
    }
  }

  const deleteEvent = async (eventToDelete: Slide, shouldRefetchTimeline: boolean = true): Promise<boolean> => {
    if(!currentTimelineId) return false;
    setLoadingTimeline(true);
    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken || !eventToDelete.id) {
        setLoadingTimeline(false);
        return false;
      }
      await deleteTimelineEvent({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        docId: currentTimelineId,
        eventId: String(eventToDelete.id),
      });
      if (shouldRefetchTimeline) {
        await fetchNewTimeline(currentTimelineId, caseId, false);
      } else {
        setLoadingTimeline(false);
      }
      return true;
    } catch (error) {
      setLoadingTimeline(false);
      // Batch deletes (`onUpdateTimeline`) pass `shouldRefetchTimeline: false` and show
      // "Some Events Were Not Deleted" once; skip per-event toasts to avoid stacking.
      if (shouldRefetchTimeline) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: t('main.eventDeletionFailed'),
          description: t('main.eventDeletionFailedDescription'),
          level: ToastLevel.ERROR,
        });
      }
      const errorMessage = 'Could not delete event.';
      captureError(errorMessage, error as Error);
      return false;
    }
  }

  // poll specific job status waiting for the job to complete and apply the results to the timeline
  useEffect(() => {
    if (entityServiceJob.count > 0 && entityServiceJob.count < ENTITY_SERVICE_POLL_ATTEMPTS && !!caseId && entityServiceJob.caseId === caseId && !!entityServiceJob.jobId && entityServiceJob.jobId.trim() !== '') {
      if (entityServiceJobPollRef.current) {
        clearTimeout(entityServiceJobPollRef.current);
        entityServiceJobPollRef.current = null;
      }
      entityServiceJobPollRef.current = setTimeout(() => {
        setEntityServiceJob({ ...entityServiceJob, count: entityServiceJob.count + 1 });
        getEventsFromEntityService('GET_JOB_STATUS', currentTimelineId || null, caseId || '', '', [], 0, entityServiceJob.jobId || '', false, 'system');
      }, ENTITY_SERVICE_POLL_INTERVAL_MS);
    } else if (entityServiceJob.caseId !== undefined && entityServiceJob.caseId !== undefined) {
      if (entityServiceJobPollRef.current) {
        clearTimeout(entityServiceJobPollRef.current);
        entityServiceJobPollRef.current = null;
      }
      setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
    }
  }, [ entityServiceJob.jobId, entityServiceJob.caseId, entityServiceJob.count, caseId ]);

  // poll all jobs waiting for the job to complete so they can be used in the table view
  useEffect(() => {
    if (entityServicePollAllJobsCount > 0 && entityServicePollAllJobsCount < ENTITY_SERVICE_POLL_ATTEMPTS && !!caseId && caseId.trim() !== '') {
      // A live single-job poll is what applies completed events to the timeline.
      // The all-jobs poll (including a menu refresh that arms it) must not cancel that.
      const singleJobPolling = !!entityServiceJob.jobId && entityServiceJob.jobId.trim() !== '' && entityServiceJob.count > 0;
      if (!singleJobPolling) {
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
      }
      if (entityServicePollAllJobsRef.current) {
        clearTimeout(entityServicePollAllJobsRef.current);
        entityServicePollAllJobsRef.current = null;
      }
      entityServicePollAllJobsRef.current = setTimeout(() => {
        setEntityServicePollAllJobsCount(entityServicePollAllJobsCount + 1);
        getEventsFromEntityService('GET_ALL_JOBS', currentTimelineId || null, caseId || '', '', [], 0, null, false, 'system');
      }, ENTITY_SERVICE_POLL_INTERVAL_MS);
    } else {
      if (entityServicePollAllJobsRef.current) {
        clearTimeout(entityServicePollAllJobsRef.current);
        entityServicePollAllJobsRef.current = null;
      }
      setEntityServicePollAllJobsCount(0);
    }
  }, [ caseId, entityServicePollAllJobsCount ]);

  const handleCancelPleaseSelectTimeline = () => {
    setPleaseSelectTimeline(false);
    setParsedEventsFromChat(undefined);
    clearPendingChatEvent();
    clearPendingAutogeneratedTimelineApply();
    setModalSelectedTimeline(undefined);
    setIsEditableTimeline(false);
  }

  const handleConfirmPleaseSelectTimeline = () => {
    if (!modalSelectedTimeline) return;
    setPleaseSelectTimeline(false);
    setSelectedSecondOption(modalSelectedTimeline);
    setCurrentTimelineId(modalSelectedTimeline);
    fetchNewTimeline(modalSelectedTimeline, caseId, false, true);
  }

  const getTimelineEventCount = (timeline: TimelineDropdownItem): number => {
    return timeline.eventCount ?? 0;
  };

  const bumpTimelineEventCount = (timelineId: string, delta: number) => {
    if (!timelineId || delta === 0) return;
    setMultipleTimelinesAvailable((prev) =>
      prev.map((t) =>
        t.id === timelineId
          ? { ...t, eventCount: Math.max(0, getTimelineEventCount(t) + delta) }
          : t,
      ),
    );
  };

  const syncTimelineEventCountInList = (timelineId: string, eventCount: number) => {
    if (!timelineId) return;
    setMultipleTimelinesAvailable((prev) =>
      prev.map((t) =>
        t.id === timelineId ? { ...t, eventCount } : t,
      ),
    );
  };

  const formatTimelineSelectLabel = (timeline: TimelineDropdownItem) => {
    const createdBy = timeline.createdBy ? timeline.createdBy.split('@')[0] : '';
    const createdAt = timeline.createdAt
      ? formatDate(timeline.createdAt, { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '';
    const title = timeline.title ? timeline.title : '';
    const count = getTimelineEventCount(timeline);
    const countLabel = t(
      count === 1 ? 'workProducts.eventCountOne' : 'workProducts.eventCountMany',
      { count },
    );
    return `${createdBy} | ${timeline.id} | ${createdAt} | ${title} | ${countLabel}`;
  }

  const pendingParsedEventCount = parsedEventsFromChat?.length ?? 0;

  const pleaseSelectTimelineModalContent = (
    <div className="fixed inset-0 z-[30100] flex items-center justify-center bg-gray-900/95">
      <Card className="box-border backdrop-blur-md h-[500px] w-[min(420px,calc(100vw-2rem))] bg-gray-800 opacity-100">
        <CardHeader className="box-border top-0 left-0 bg-gray-900 w-full h-[50px] flex flex-row items-center justify-center border-b border-solid border-white/10">
          <div className="text-lg text-white font-semibold">
            {currentTimelineId ? t('timeline.select.confirmTitle') : t('timeline.select.pleaseSelect')}
          </div>
        </CardHeader>
        <CardBody className="box-border flex flex-col w-full px-4 text-white">
          <div className="flex flex-row items-center justify-center p-2 text-center">
            <div>
              {pendingParsedEventCount > 0
                ? t(
                    pendingParsedEventCount === 1
                      ? 'timeline.select.pendingOne'
                      : 'timeline.select.pendingMany',
                    { count: pendingParsedEventCount },
                  )
                : currentTimelineId
                  ? t('timeline.select.willAdd')
                  : t('timeline.select.required')}
            </div>
          </div>
          <div className="box-border w-full mb-1 pb-1">
            <Select
              aria-label={t('timeline.select.aria')}
              styles={SelectStyles}
              className="react-select-container font-inter z-[9999] w-full custom-scrollbar2"
              value={modalSelectedTimeline
                ? {
                    value: modalSelectedTimeline,
                    label: (() => {
                      const timeline = multipleTimelinesAvailable.find(t => t.id === modalSelectedTimeline);
                      return timeline ? formatTimelineSelectLabel(timeline) : modalSelectedTimeline;
                    })(),
                  }
                : null}
              onChange={(newValue) => {
                const value = (newValue as SingleValue<{ value: string; label: string }>)?.value;
                if (value === CREATE_NEW_TIMELINE_ID) {
                  setPleaseSelectTimeline(false);
                  handleSecondOptionChange(value, true);
                } else {
                  setModalSelectedTimeline(value);
                }
              }}
              options={
                multipleTimelinesAvailable && multipleTimelinesAvailable.length > 0
                  ? [
                      { value: CREATE_NEW_TIMELINE_ID, label: t('timeline.select.createNew') },
                      ...multipleTimelinesAvailable.map(item => ({
                        value: item.id,
                        label: formatTimelineSelectLabel(item),
                      })),
                    ]
                  : [
                      { value: CREATE_NEW_TIMELINE_ID, label: t('timeline.select.createNew') },
                      { value: '', label: t('timeline.select.none'), isDisabled: true },
                    ]
              }
              placeholder={t('eventModal.selectTimeline')}
              noOptionsMessage={() => t('timeline.select.noCases')}
            />
          </div>
        </CardBody>
        <CardFooter className="box-border bg-gray-900 flex w-full flex-row items-center justify-between gap-2 border-t border-solid border-white/10">
          <Button
            type="button"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('timeline.dialog.cancel')}
            onClick={() => handleCancelPleaseSelectTimeline()}
          >
            {t('timeline.dialog.cancel')}
          </Button>
          <Button
            type="button"
            style="solid"
            color="accent"
            background="inverse"
            aria-label={pendingParsedEventCount > 1 ? t('eventModal.addEvents') : t('timeline.table.addEvent')}
            disabled={!modalSelectedTimeline}
            onClick={handleConfirmPleaseSelectTimeline}
          >
            {pendingParsedEventCount > 1 ? t('eventModal.addEvents') : t('timeline.table.addEvent')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );

  const pleaseSelectTimelineModal =
    pleaseSelectTimeline && typeof document !== 'undefined'
      ? createPortal(pleaseSelectTimelineModalContent, document.body)
      : null;

  // Legacy wand events (better-wand off) name the cited object, not the
  // transcode the presentation player streams. Resolve that preview before
  // the slides are saved or queued.
  const resolveLegacyGeneratedSlides = async (slides: Slide[]): Promise<Slide[]> => {
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) {
      return slides;
    }
    return resolveAutogeneratedTimelineSlides(slides, cellId, realAccessToken);
  };

  // Prefer the citations captured when the job started. Those have the
  // playable mediaUrl from the chat answer. The status poll passes ``[]``,
  // and ``allSourcesFromChat`` is only the raw source path.
  const chatSourcesForLegacyJob = (
    jobId: string | null | undefined,
    sources: Source[],
  ): MediaSource[] => {
    if (jobId) {
      const saved = legacyWandSourcesByJobIdRef.current.get(jobId);
      if (saved?.length) return saved;
    }
    return (sources || []) as MediaSource[];
  };

  const withChatPlayableMedia = (
    slides: Slide[],
    jobId: string | null | undefined,
    sources: Source[],
  ): Slide[] => applyChatPlayableMediaToSlides(slides, chatSourcesForLegacyJob(jobId, sources));

  const createEventsFromEntityEvents = async (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string, initiator: 'user' | 'system' = 'user') => {
    if (!caseId || caseId.trim() === '') {
      return;
    }
    if ((!timelineIdIn || timelineIdIn.trim() === '') && (!currentTimelineId || currentTimelineId.trim() === '')) {
      const newEvents = withChatPlayableMedia(
        await resolveLegacyGeneratedSlides(
          entityEventsToSlides(events, allSourcesFromChat?.allSources || [], allSourcesFromChat),
        ),
        jobId,
        [],
      );
      setEntityJobId(jobId);
      queueParsedEventsForTimelineSelection(newEvents, enriching);
      return;
    }
    const newEvents = withChatPlayableMedia(
      await resolveLegacyGeneratedSlides(
        entityEventsToSlides(events, allSourcesFromChat?.allSources || [], allSourcesFromChat),
      ),
      jobId,
      [],
    );
    await createEventsFromParsedEvents(newEvents, enriching, false, timelineIdIn || currentTimelineId);
    await getEventsFromEntityService('DELETE_JOB', timelineIdIn || null, caseId || '', '', [], 0, jobId, false, initiator);
  }

  const getEventsFromEntityService = async (
    action: string,
    timelineId: string | null,
    caseId: string,
    answerItemText: string,
    sources: Source[],
    epochTimeSecs: number,
    jobId: string | null,
    cancelPolling: boolean = false,
    initiator: 'user' | 'system' = 'user',
  ) => {
    if (!realAccessToken) return;
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId) return;
    if (cancelPolling && entityServiceJobPollRef.current) {
      clearTimeout(entityServiceJobPollRef.current);
      entityServiceJobPollRef.current = null;
    }
    if (action !== 'GET_ALL_JOBS' && action !== 'DELETE_JOB') {
      if(!timelineId) return;
      // Status polls run in the background while the table is open. Marking the
      // timeline loading here unmounts TPTable on every tick (TR-4237).
      if (action !== 'GET_JOB_STATUS') {
        setLoadingTimeline(true);
      }
    }
    if (action !== 'DELETE_JOB' && action !== 'GET_JOB_STATUS' && action !== 'GET_ALL_JOBS') {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 602000,
        title: t('main.extractingEvents'),
        description: t('main.extractingEventsDescription'),
        level: ToastLevel.INFO,
        child: (
          <div className="flex flex-col gap-2 items-center justify-center">
            <Spinner size="sm" color="primary" />
          </div>
        ),
      });
    }
    try {
      const sourceUrls: string[] = (sources || [])
        .map((s: Source) => s?.sourceUrl || '')
        .filter((u: string) => u.trim() !== '');
      const tags = [await caseTimelineTagFor(caseId)];
      const signal = AbortSignal.timeout(602000);
      // Loose ``any`` so the existing downstream branches (which read
      // ``response.jobId``/``response.status``/``response.result?.events``
      // off the upstream extraction-job shape) keep working without rewrite.
      let response: any = null;

      if (action === 'CREATE_JOB') {
        response = await createExtractionJob({
          cellId,
          accessToken: realAccessToken,
          text: answerItemText,
          tags,
          sources: sourceUrls,
          epochTimeSecs,
          signal,
        });
      } else if (action === 'GET_JOB_STATUS' && jobId) {
        response = await getExtractionJobStatus({
          cellId,
          accessToken: realAccessToken,
          jobId,
          signal,
        });
      } else if (action === 'GET_ALL_JOBS') {
        const jobs = await queryExtractionJobs({
          cellId,
          accessToken: realAccessToken,
          tags,
          signal,
        });
        setJobsBeingDeleted([]);
        const entityExtractionJobs = dedupeExtractionJobs((jobs && jobs.length > 0 ? jobs : []).map((job: any) => {
          const epochTimeSecs = job?.epochTimeSecs ?? job?.epoch_time_secs;
          return Number.isFinite(Number(epochTimeSecs))
            ? { ...job, epochTimeSecs: Number(epochTimeSecs) }
            : job;
        }));
        // Arming the all-jobs poll cancels single-job polling. A menu refresh
        // while a wand job is in flight must only update the list (TR-4237).
        const singleJobPolling = !!entityServiceJob.jobId && entityServiceJob.jobId.trim() !== '' && entityServiceJob.count > 0;
        if (!singleJobPolling) {
          if (entityExtractionJobs.length === 0) {
            setEntityServicePollAllJobsCount(0);
          } else {
            const somePending = entityExtractionJobs.some((job: any) => job.status === 'pending');
            if (somePending) {
              setEntityServicePollAllJobsCount((count) => (count > 0 ? count : 1));
            } else {
              setEntityServicePollAllJobsCount(0);
            }
          }
        }
        setEntityExtractionJobs(entityExtractionJobs);
        return;
      } else if (action === 'DELETE_JOB' && jobId) {
        setJobsBeingDeleted([...jobsBeingDeleted, jobId]);
        await deleteExtractionJob({
          cellId,
          accessToken: realAccessToken,
          jobId,
          signal,
        });
        // get all jobs after deleting a job to update the list of jobs
        await getEventsFromEntityService('GET_ALL_JOBS', timelineId || null, caseId || '', '', [], 0, null, false, initiator);
        setJobsBeingDeleted([...jobsBeingDeleted.filter((job: any) => job.jobId !== jobId)]);
        return;
      } else if (action === 'GET_EVENTS') {
        const eventsResponse = await extractEvents({
          cellId,
          accessToken: realAccessToken,
          text: answerItemText,
          tags,
          sources: sourceUrls,
          signal,
        });
        // The legacy BFF attached the original Source[] (not just URLs) to the
        // response so downstream consumers had full context. Preserve that
        // shape on direct CORS so call sites need no further changes.
        if (eventsResponse && typeof eventsResponse === 'object') {
          (eventsResponse as { sources?: unknown }).sources = sources || [];
        }
        response = eventsResponse;
      } else {
        throw new Error('Invalid Entity Service action from UI', { cause: { action } });
      }

      let eventsToParse: Slide[] = [];

      let processEvents = false;
      if (action === 'CREATE_JOB' && epochTimeSecs && response && response.jobId) {
        legacyWandSourcesByJobIdRef.current.set(String(response.jobId), (sources || []) as MediaSource[]);
        setEntityExtractionJobs((prev) => upsertExtractionJob(prev, {
          jobId: response.jobId,
          status: 'pending',
          result: { events: [] },
          epochTimeSecs: response.epochTimeSecs,
        }));
        // use the jobId in the response to poll for the events
        setEntityServiceJob({ jobId: response.jobId, caseId: caseId, count: 1 });
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: t('main.extractionStarted'),
          description: t('main.extractionStartedDescription'),
          level: ToastLevel.INFO,
          child: (
            <div className="flex flex-col gap-2 items-center justify-center">
              <Spinner size="sm" color="primary" />
            </div>
          ),
        });
        return;
      } else if (action === 'GET_JOB_STATUS' && jobId && response && (response.status === 'completed' || response.status === 'failed' || response.status === 'deleted')) {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        // response from polling, use the events for the correct answer
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        // parse the events from the response
        setEntityExtractionJobs((prev) => upsertExtractionJob(prev, response));
        if (response.status === 'completed') {
          processEvents = true;
          eventsToParse = response.result?.events || [];
        }
        getEventsFromEntityService('DELETE_JOB', timelineId, caseId, answerItemText, sources, epochTimeSecs, response.jobId, false, initiator);
      } else if (action === 'GET_JOB_STATUS' && response && response.status === 'pending') {
        setEntityExtractionJobs((prev) => upsertExtractionJob(prev, response));
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: t('main.extractionPending'),
          description: t('main.extractionPendingDescription'),
          descriptionClassName: 'text-sm',
          level: ToastLevel.INFO,
          child: (
            <div className="flex flex-col gap-2 items-center justify-center">
              <Spinner size="sm" color="primary" />
            </div>
          ),
          actionButtonText: t('main.notifyOnCompletion'),
          actionButtonOnClick: () => {
            setLoadingTimeline(false);
            // cancel any single job polling
            if (entityServiceJobPollRef.current) {
              clearTimeout(entityServiceJobPollRef.current);
              entityServiceJobPollRef.current = null;
            }
            setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
            // kick off polling for all jobs to check for old jobs
            setEntityServicePollAllJobsCount(1);
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 5000,
              title: t('main.pollingStopped'),
              description: t('main.pollingStoppedDescription'),
              level: ToastLevel.INFO,
            });
          },
        });
        return;
      } else if (action === 'GET_JOB_STATUS' && response && response.status === 'failed') {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        setEntityExtractionJobs((prev) => upsertExtractionJob(prev, response));
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.extractionFailed'),
          description: t('main.extractionFailedDescription'),
          level: ToastLevel.ERROR,
        });
        return;
      } else if (response && response.status && response.status !== 'completed' && response.status !== 'failed' && response.status !== 'deleted' && response.status !== 'pending') {
        // unknown status, do nothing
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityExtractionJobs((prev) => upsertExtractionJob(prev, response));
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
      }

      if (processEvents && eventsToParse && eventsToParse.length > 0) {
        const count = eventsToParse.length;
        setLoadingTimeline(true);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 602000,
          title: t('main.eventsExtracted'),
          description: t('main.eventsExtractedDescription', { count }),
          level: ToastLevel.SUCCESS,
        });
        const newEvents = withChatPlayableMedia(
          await resolveLegacyGeneratedSlides(
            entityEventsToSlides(eventsToParse, sources, allSourcesFromChat),
          ),
          jobId,
          sources,
        ).sort((a: Slide, b: Slide) => {
            if (!a.date) {
              return -1;
            }
            if (!b.date) {
              return 1;
            }
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            return aDate.getTime() - bDate.getTime();
          });
        // create the events
        const timer = 10000; // change this if enriching

        for (let i = 0; i < count; i++) {
          await createEvent(newEvents[i], timelineId, false);
          setLoadingTimeline(true);
          if (i < count - 1) {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: i === count - 1 ? 5000 : (i + 1) * timer,
              title: `'Created Event ${i + 1} of ${count}`,
              description: `Created event ${i + 1} of ${count}, waiting for the rest. Please do not change the case or timeline.`,
              level: ToastLevel.INFO,
              child: (
                <div className="flex flex-col gap-2 items-center justify-center">
                  <Progress
                    value={((i + 1) / count) * 100}
                    size="lg"
                    color="success"
                    className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]"
                  />
                  <div className="text-md text-gray-800">
                    {t('main.createdEventProgress', { current: i + 1, total: count })}
                  </div>
                </div>
              ),
            });
          } else {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: i === count - 1 ? 5000 : (i + 1) * timer,
              title: `'Created Event ${i + 1} of ${count}`,
              description: `Created event ${i + 1} of ${count}, waiting for the rest. Please do not change the case or timeline.`,
              level: ToastLevel.INFO,
              actionButtonText: selectedView === 'chat' ? t('main.goToTimelineDetails') : t('main.goToTimelineTable'),
              actionButtonOnClick: selectedView === 'chat' ? () => {
                setSelectedView('table');
              } : undefined,
              child: (
                <div className="flex flex-col gap-2 items-center justify-center">
                  <Progress
                    value={((i + 1) / count) * 100}
                    size="lg"
                    color="success"
                    className="box-border w-[calc(100%-20px)] mx-[10px] h-[30px]"
                  />
                  <div className="text-md text-gray-800">
                    {t('main.createdEventProgress', { current: i + 1, total: count })}
                  </div>
                </div>
              ),
            });
          }
        }
        if (timelineId) {
          await fetchNewTimeline(timelineId, caseId, true);
        }
      } else if (action === 'GET_JOB_STATUS' && response.status === 'completed') {
        // cancel any single job polling
        if (entityServiceJobPollRef.current) {
          clearTimeout(entityServiceJobPollRef.current);
          entityServiceJobPollRef.current = null;
        }
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        setLoadingTimeline(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.noEventsGenerated'),
          description: t('main.noEventsGeneratedDescription'),
          level: ToastLevel.INFO,
        });
      }
    } catch (error) {
      // A list refresh can fail while the wand job is still running. Clearing
      // the single-job poll here would drop completed events (TR-4237).
      const singleJobPolling = !!entityServiceJob.jobId && entityServiceJob.jobId.trim() !== '' && entityServiceJob.count > 0;
      if (!(action === 'GET_ALL_JOBS' && singleJobPolling)) {
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
      }
      setEntityServicePollAllJobsCount(0);
      setLoadingTimeline(false);
    }
  }

  const deleteEntityExtractionJob = async (jobId: string) => {
    getEventsFromEntityService('DELETE_JOB', null, '', '', [], 0, jobId, false, 'user');
  }

  /**
   * A chat event whose media rows point at the rendition provenance resolves
   * them to now, rather than the one the citation happened to name.
   *
   * Chat citations are turned into media rows in the browser, from urls that
   * name the cited object itself — for an extract inside a container that is the
   * stored original, not the content-moderation-safe preview. Without this the
   * Add Event modal previews a flagged still unobfuscated and the event is saved
   * pointing at it. Timelines run the same pass on load, so resolving here also
   * keeps what the modal shows and what the event reopens with the same thing.
   */
  const withModeratedChatMedia = async (chatEvent: EventFromChat): Promise<EventFromChat> => {
    if (!chatEvent.media?.length) return chatEvent;
    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) return chatEvent;
    // fetchFileInfoBatch is required. The per-URL getFileInfoFromServer is
    // only the fallback when POST /files/file-info is absent — do not remove
    // the batch option or this list becomes one GET per file.
    const media = await resolveMediaModeration(
      chatEvent.media,
      (url) => getFileInfoFromServer(url, cellId, realAccessToken),
      {
        fetchFileInfoBatch: (urls) =>
          getFileInfoBatchFromServer(urls, cellId, realAccessToken),
      },
    );
    return media === chatEvent.media ? chatEvent : { ...chatEvent, media };
  };

  // addToTimeline prop for chat/answerItem
  const createEventFromChat = async (chatEventToAddIn: EventFromChat) => {
    if(!caseId || caseId.trim() === '') {
      return;
    }
    setParsedEventsFromChat(undefined);
    const chatEventToUse = await withModeratedChatMedia(chatEventToAddIn);
    pendingChatEventRef.current = chatEventToUse;
    setChatEventToAdd(chatEventToUse);

    if (currentTimelineId && currentTimelineId.trim() !== '') {
      if (timelinePatch) {
        await proceedWithChatEvent(currentTimelineId);
      } else {
        await fetchNewTimeline(currentTimelineId, caseId, false, true);
      }
      return;
    }
    setModalSelectedTimeline(undefined);
    setPleaseSelectTimeline(true);
  }

  // Called after user confirms timeline selection in the modal
  const proceedWithChatEvent = async (timelineId: string) => {
    const chatEventToUse = getActiveChatEventToAdd();
    if (chatEventToUse) {
      if (
        chatEventToUse.autogen
        && isAutogeneratedTimelineAnswer(chatEventToUse.answerText)
        && caseId
        && caseId.trim() !== ''
      ) {
        const autogeneratedSlides = await slidesFromAutogeneratedChatAnswer(chatEventToUse.answerText);
        if (autogeneratedSlides?.length) {
          await createEventsFromParsedEvents(autogeneratedSlides, false, false, timelineId);
          clearPendingChatEvent();
          setParsedEventsFromChat(undefined);
          return;
        }
      }
      if (chatEventToUse.autogen && chatEventToUse.answerText && caseId && caseId.trim() !== '') {
        const action = 'CREATE_JOB';
        const createdEvents = await getEventsFromEntityService(
          action,
          timelineId,
          caseId,
          chatEventToUse.answerText,
          chatEventToUse.sources,
          chatEventToUse.epochTimeSecs,
          null,
          false,
          'user',
        );
      } else if (chatEventToUse?.answerText === 'FROM_SOURCE' && caseId && caseId.trim() !== '') {
        const newEvent = {
          id: 'NEW_EVENT_FROM_CHAT',
          slideTitle: 'FROM SOURCE',
          slideDescription: chatEventToUse.slideDescription,
          show: true,
          approval: 'approved',
          sources: chatEventToUse.sources as MediaSource[],
        }
        openModalFromTable({ ...newEvent }, 'newEventFromChat');
      } else if (chatEventToUse.answerText && caseId && caseId.trim() !== '') {
        const description = getDescriptionFromAnswerText(chatEventToUse.answerText);
        const sourcesFromChatEvent = chatEventToUse.sources.map((source: Source) => {
          return {
            sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
            sourceUrl: source.sourceFilePath,
            sourceContext: source.sourceContext,
          }
        });
        const sourcesForEvents = dedupeSources(sourcesFromChatEvent);
        // TODO: only id is currently used, modal rebuilds this event
        const newEvent = {
          id: 'NEW_EVENT_FROM_CHAT',
          slideTitle: '',
          slideDescription: description,
          show: true,
          approval: 'approved',
          sources: sourcesForEvents,
        }
        openModalFromTable({ ...newEvent }, 'newEventFromChat');
      }
    }
  }

  const handleAddEvent = async (newEvent: Slide): Promise<EventSaveResult> => {
    if(!currentTimelineId) return { success: false };
    setLoadingTimeline(true);
    return createEvent(newEvent, currentTimelineId, true);
  }

  const handleDeleteEvent = async(event: Slide) => {
    if(allSlides) {
      setLoadingTimeline(true);
      deleteEvent(event);
    }
  }

  useEffect(() => {
    // Threat-report create is scoped to the current case. Abort in-flight
    // provenance loads and close the modal only when the case changes — not
    // from unrelated state updates below.
    threatReportSourcesAbortRef.current?.abort();
    threatReportSourcesAbortRef.current = null;
    setThreatReportSourcesLoading(false);
    setIsThreatReportNameModalOpen(false);
    setThreatReportSources({ fiveCastFiles: [], magnetFiles: [] });
    setThreatReportSourceModifiedAt({});
    setThreatReportSourcesError(null);
    setSelectedFivecastSource(undefined);
    setSelectedMagnetSource(undefined);
  }, [caseId]);

  useEffect(() => {
    // applyAppNavState / restore already owns timeline + slide state; skip the
    // user case-switch reset so we don't clear currentTimelineId mid-restore.
    const skipCaseReset =
      suppressNavSideEffectsRef.current || skipForceChatOnCaseChangeRef.current;
    if (!skipCaseReset) {
      const resetForNewCase = async () => {
        setTimelineTags([]);
        setIsFullscreen(false);
        setIsEventModalOpen(false);
        setNewTimelineIsAvailable(false);
        setAllSlides(undefined);
        setEntityExtractionJobs([]);
        setChatHistorySessions(null);
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        setEntityServicePollAllJobsCount(0);
        if (currentTimelineId && currentTimelineId.trim() !== '' || caseId && caseId.trim() !== '') {
          await fetchNewTimeline(undefined, caseId, false);
        }
      };
      resetForNewCase();
    }
    // Poll in-progress extraction jobs whenever a case is selected — including
    // refresh restore where layout pre-applies caseId under skipForceChatOnCaseChangeRef.
    if (caseId && caseId.trim() !== '') {
      setEntityServicePollAllJobsCount(1);
    }
  }, [ caseId ]);

  const closeThreatReportNameModal = useCallback(() => {
    threatReportSourcesAbortRef.current?.abort();
    threatReportSourcesAbortRef.current = null;
    setThreatReportSourcesLoading(false);
    setThreatReportApiCaseId(undefined);
    setIsThreatReportNameModalOpen(false);
  }, []);

  const handleSetCaseId = (option: string | undefined) => {
    // Keep ref in sync immediately so Back/Forward apply (and any logic in the
    // same turn) sees the case the history entry references, not the prior one.
    const caseChanged = option !== caseIdRef.current;
    caseIdRef.current = option;
    setMultipleTimelinesAvailable([]);
    setSelectedSecondOption(undefined);
    if (caseChanged) {
      setCurrentTimelineId(undefined);
      setAllSlides(undefined);
      setTimeline(undefined);
      setTitle(undefined);
      setDescription(undefined);
      setMedia(undefined);
      setTimelinePatch(undefined);
      setTimelineTags([]);
    }
    setCaseId(option);
    if (option && option.trim() !== '') {
      getEventsFromEntityService('GET_ALL_JOBS', null, option || '', '', [], 0, null, false, 'user');
    }
  }

  const handleSetSelectedSecondOptionFromDelete = (option: string | undefined) => {
    setMultipleTimelinesAvailable([]);
    setSelectedSecondOption(option);
  }

  useEffect(() => {
    if (featureFlags['timeSaved'] || featureFlags['newTimeSaved']) {
      const timeSavedLocalFromStorage = localStorage.getItem(`timeSaved-${featureFlags?.orgId}`);
      if (timeSavedLocalFromStorage && Number(timeSavedLocalFromStorage) > THRESHOLD_TO_SHOW_LOCAL_TIME_SAVED) {
        setTimeSavedLocal(parseInt(timeSavedLocalFromStorage));
      }
    } else {
      setTimeSavedLocal(0);
    }
  }, [featureFlags['orgId'], featureFlags['timeSaved'], featureFlags['newTimeSaved'], timeSaved]);

  // use toast to notify user that timeline is being created, but don't block the UI
  const onCreateTimeline = async (creatingEventFromChat?: boolean) => {
    if(caseId && caseId.trim() !== '') {
      // Open modal to prompt for timeline name
      setDefaultTimelineName(getDefaultTimelineTitleForCase(caseId, t));
      setPendingTimelineCreation({ creatingEventFromChat });
      setIsTimelineNameModalOpen(true);
    }
  };

  const handleCreateTimelineWithName = async (name: string) => {
    if (!caseId || caseId.trim() === '') return;

    const nameToUse = name.trim() || getDefaultTimelineTitleForCase(caseId, t);
    if (nameToUse.length > TIMELINE_TITLE_MAX_LENGTH) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.titleTooLong'),
        description: t('main.titleTooLongCreate', { max: TIMELINE_TITLE_MAX_LENGTH }),
        level: ToastLevel.ERROR,
      });
      return;
    }
    const creatingEventFromChat = pendingTimelineCreation?.creatingEventFromChat;

    setIsTimelineNameModalOpen(false);
    setPendingTimelineCreation(null);

    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: 3000,
      title: t('main.creatingTimeline'),
      description: t('main.creatingTimelineDescription'),
      level: ToastLevel.INFO,
    });

    const timeline = await createNewTimeline(nameToUse, creatingEventFromChat);
    void trackEvent('timeline_created', {
      user: authState?.idToken?.claims.email,
      case_id: caseId,
      session_id: chatSessionId ?? undefined,
      work_product_name: nameToUse,
      success: !!timeline,
      identity: { tenantName: tenantName ?? undefined },
    });
    if (!timeline) return;
    await fetchNewTimeline(timeline.id, caseId, false, false);
    setCurrentTimelineId(timeline.id);
    setSelectedSecondOption(timeline.id);
    setIsEditableTimeline(true);
    // Refresh work products for this case
    if (caseId) {
      fetchWorkProductsForCase(caseId).then((workProducts) => {
        setMultipleTimelinesAvailable(workProducts);
      });
    }
  };

  // Display name for the signed-in user, used as the CSAM report reviewer.
  // Falls back to email when the name claims are missing.
  const csamReportCreator = useMemo(() => {
    const name = [
      authState?.idToken?.claims.given_name,
      authState?.idToken?.claims.family_name,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || (authState?.idToken?.claims.email as string) || undefined;
  }, [authState?.idToken?.claims.given_name, authState?.idToken?.claims.family_name, authState?.idToken?.claims.email]);

  const onCreateThreatReport = async () => {
    // Keep the open report until the analyst confirms a name. Canceling the
    // modal must not replace it with a loading state.
    if (!caseId || caseId.trim() === '') {
      return;
    }
    const requestedCaseId = caseId.trim();

    threatReportSourcesAbortRef.current?.abort();
    const abort = new AbortController();
    threatReportSourcesAbortRef.current = abort;

    setSelectedFivecastSource(undefined);
    setSelectedMagnetSource(undefined);
    setThreatReportSources({ fiveCastFiles: [], magnetFiles: [] });
    setThreatReportSourceModifiedAt({});
    setThreatReportSourcesError(null);
    setThreatReportApiCaseId(undefined);
    setThreatReportSourcesLoading(true);
    // Open immediately — one case-scoped provenance page fills the dropdowns.
    setIsThreatReportNameModalOpen(true);

    const isStale = () =>
      abort.signal.aborted || caseIdRef.current?.trim() !== requestedCaseId;

    // A report covers one case, and the cross-case selection has no caseTag to
    // filter provenance by, so there is nothing to look up. Say so rather than
    // let it read as a lookup that failed.
    if (isAllCasesSelection(requestedCaseId)) {
      setThreatReportSourcesError('all-cases');
      setThreatReportSourcesLoading(false);
      return;
    }

    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) {
        captureWarning(
          'Threat report sources: no provenance session (cellId or access token missing)',
        );
        if (!isStale()) {
          setThreatReportSourcesError('session');
          setThreatReportSourcesLoading(false);
        }
        return;
      }

      // A real caseTag has to come from the cases list — there is no guessed
      // ``case:<name>`` fallback any more (TR-359). ``fetchQuery`` serves the
      // list the nav already loaded while it is fresh and only fetches when the
      // cache is cold, so this neither stalls on a warm cache nor gives up on a
      // cold one. Why it is missing is the whole diagnosis when no tag
      // resolves, so keep the error rather than discarding it.
      const casesOptions = casesListQueryOptions({
        cellId,
        accessToken: realAccessToken,
        fromPermissionService: featureFlags?.casesFromPermissionService === true,
        tenantId: getTenantIdFromToken(authState?.idToken?.idToken || ''),
      });
      let fetchedCases: CaseSummary[] | undefined;
      let casesError: Error | undefined;
      try {
        fetchedCases = await queryClient.fetchQuery(casesOptions);
      } catch (error) {
        casesError = error instanceof Error ? error : new Error(String(error));
        // A refresh of a *stale* entry rejects while the cache keeps the list
        // the nav is still showing. Resolving against those cases beats failing
        // a lookup that the visible list can satisfy.
        fetchedCases = queryClient.getQueryData<CaseSummary[]>(casesOptions.queryKey);
      }
      if (isStale()) return;

      // ``useCasesList`` applies this through ``select``, which the raw cache
      // entry has not been through — and it is what injects the synthetic
      // personal-files case, so without it My Files resolves to no tag.
      const casesList = fetchedCases
        ? applyAdHocCaseListPolicy(fetchedCases, adHocEnabled, personalFilesCaseId)
        : undefined;

      const normalised = requestedCaseId.toLowerCase();
      const internal = getCaseInternalName(requestedCaseId).trim().toLowerCase();
      const matched = findCaseSummary(casesList, requestedCaseId);
      // Permission-service (and provenance) own the tag. Do not guess
      // ``case:<name>`` — UUID-folder files carry ``case:<uuid>`` and that
      // guess matches nothing (TR-359).
      const caseTag = matched?.caseTag?.trim();
      if (!caseTag) {
        // Case names are the analyst's own and stay in their console; the
        // reported message is kept free of them (see lib/ops-log).
        console.warn('Threat report sources: unresolved case', {
          requestedCaseId,
          casesListed: casesList?.map((c) => c.caseName),
          casesError,
          // Which cases source was queried, since the two fail for different reasons.
          casesFromPermissionService: featureFlags?.casesFromPermissionService === true,
        });
        captureWarning(
          casesList === undefined
            ? 'Threat report sources: cases list unavailable, cannot resolve a case tag'
            : `Threat report sources: no case tag for the selected case among ${casesList.length} case(s)`,
          casesError,
        );
        if (!isStale()) {
          setThreatReportSourcesError(casesList === undefined ? 'cases-unavailable' : 'case');
        }
        return;
      }
      // ``getSources`` lists ``data/bulk/{caseId}/{FiveCast|Magnet}/`` straight out
      // of S3, so this has to be the case's FOLDER SEGMENT and not its display
      // name: a UUID-folder case has no prefix under its name, the listing comes
      // back empty, and the failure is swallowed by the catch below — leaving the
      // create modal quietly short of its FiveCast/Magnet exports (TR-359).
      // ``getSelectors`` takes the same field but only logs it; it keys on ``source``.
      //
      // ``matched`` is non-null and its tag non-empty — the guard above returned
      // otherwise — so ``caseFolderNameOf`` always yields a segment here. No
      // fallback chain: a display-name fallback is the bug, and anything else
      // would be dead code reading as live logic.
      const apiCaseId = caseFolderNameOf(matched as CaseSummary) as string;

      if (isStale()) return;
      setThreatReportApiCaseId(apiCaseId);

      // One page is enough for the create-modal dropdown; avoid paging and the
      // previous unscoped category-wide fallback (that scanned the whole cell).
      const filesPage = await fetchProvenanceFilesPage({
        cellId,
        accessToken: realAccessToken,
        offset: 0,
        limit: 100,
        filters: {
          contentCategories: ['fivecast', 'magnet'],
          tags: [caseTag],
          tagMatch: 'any',
        },
        signal: abort.signal,
      });
      if (isStale()) return;
      const provenanceSources = partitionThreatReportSources(filesPage.items);

      let edgeSources: ThreatReportSourceLists = { fiveCastFiles: [], magnetFiles: [] };
      let sourceModifiedAt: Record<string, string> = {};
      try {
        const getSourcesResponse = await postThreatReport({
          cellId,
          accessToken: realAccessToken,
          body: { action: 'getSources', caseId: apiCaseId },
          signal: abort.signal,
        });
        if (!isStale()) {
          edgeSources = threatReportSourceListsFromGetSourcesResponse(getSourcesResponse);
          sourceModifiedAt = sourceModifiedAtFromGetSourcesResponse(getSourcesResponse);
        }
      } catch (error) {
        // Fall back to provenance-only lists when getSources is unavailable.
        captureWarning(
          'Threat report sources: getSources failed, using provenance lists only',
          error instanceof Error ? error : undefined,
        );
      }

      if (isStale()) return;
      const sources = mergeThreatReportSourceLists(edgeSources, provenanceSources);

      setThreatReportSources(sources);
      setThreatReportSourceModifiedAt(sourceModifiedAt);
    } catch (error) {
      // An aborted load is a newer open or a case switch, not a failure.
      if (isStale()) return;
      captureWarning(
        'Threat report sources: provenance lookup failed',
        error instanceof Error ? error : undefined,
      );
      setThreatReportSourcesError('failed');
    } finally {
      if (!isStale()) {
        setThreatReportSourcesLoading(false);
      }
    }
  };

  const handleCreateThreatReportWithName = async (
    name: string,
    creatorIds: string[] = [],
    options?: { includeFivecast?: boolean },
  ) => {
    setThreatReportKey(undefined);
    if (!caseId || caseId.trim() === '') return;

    const nameToUse = name.trim();
    if (!nameToUse) return;

    const fivecastServer = (featureFlags?.threatReportConfig as any)?.fivecastServer || null;
    const includeFivecast =
      options?.includeFivecast ?? Boolean(selectedFivecastSource && fivecastServer);
    const fivecastDataDirectory =
      includeFivecast &&
      threatReportSources.fiveCastFiles.find((file: any) => file === selectedFivecastSource) ||
      null;
    const magnetDataDirectory = threatReportSources.magnetFiles.find((file: any) => file === selectedMagnetSource) || null;

    if (!magnetDataDirectory && !(fivecastServer && fivecastDataDirectory)) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.threatCannotStart'),
        description: fivecastDataDirectory && !fivecastServer
            ? t('main.threatNeedsFivecastServer')
            : t('main.threatNeedsSource'),
        level: ToastLevel.ERROR,
      });
      return;
    }

    // Generate a unique threat report ID
    const threatReportId = `threat-report-${Date.now()}-${secureRandomIdSuffix()}`;

    closeThreatReportNameModal();
    setNewThreatReportId(threatReportId);

    setIsThreatReportProcessing(true);
    const body: any = {
      query: '',
      deepDive: false,
      threatReport: true,
      selectedCase: caseId,
      threatReportTitle: nameToUse,
      ...(await caseFolderNameFields(caseId)),
      files: [],
      scopes: [],
      excludedFiles: [],
      excludedScopes: [],
      userId: authState?.idToken?.claims.email || '',
    };
    if (!!fivecastServer) {
      body.fivecastServer = fivecastServer;
    }
    if (!!fivecastDataDirectory) {
      body.fivecastDataDirectory = fivecastDataDirectory;
    }
    if (!!magnetDataDirectory) {
      body.magnetDataDirectory = magnetDataDirectory;
    }
    // Absent means "every account in the export" — the pre-selection behaviour.
    if (creatorIds.length > 0) {
      body.creatorIds = creatorIds;
    }
    // English omits the field so assistant-service keeps today's English-only
    // report. Greek/Spanish/German are the legacy names that service accepts.
    const responseLanguage = responseLanguageForLocale(readStoredLocale());
    if (responseLanguage) {
      body.responseLanguage = responseLanguage;
    }
    if (!!tenantId && tenantId.trim() !== '' && tenantId !== 'null') {
      body.tenant = tenantId;
    }

    try {
      const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
      if (!cellId || !realAccessToken) {
        setIsThreatReportProcessing(false);
        const description = !cellId
          ? t('main.threatMissingCell')
          : t('main.sessionMissingToken');
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.threatFailedToStart'),
          description,
          level: ToastLevel.ERROR,
        });
        return;
      }
      const result = await postAssistantChat({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        threatReport: true,
        body,
      });
      const sessionIdRaw = result?.sessionId ?? result?.session_id;
      const sessionId = typeof sessionIdRaw === 'string' ? sessionIdRaw.trim() : '';
      const epochTimeSecs = Number(result?.epoch_time_secs);
      // The key the server wrote under, in preference order: what it told us, then
      // the folder name this request actually carried. Re-resolving here would race
      // the case list — if it lands mid-POST, a folder name that was null at send
      // time resolves to the token now, and we would poll ``{token}-{user}`` for a
      // turn written under the display name, so the report never appears (TR-361).
      const serverChatKey = typeof result?.chat_key === 'string' ? result.chat_key.trim() : '';
      const chatKey =
        serverChatKey ||
        composeChatKey(
          typeof body.caseFolderName === 'string' ? body.caseFolderName : null,
          caseId,
          authState?.idToken?.claims.email,
        );
      if (
        !sessionId ||
        !Number.isFinite(epochTimeSecs) ||
        epochTimeSecs <= 0
      ) {
        setIsThreatReportProcessing(false);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.threatFailedToStart'),
          description: t('main.threatIncompleteResponse'),
          level: ToastLevel.ERROR,
        });
        return;
      }
      const threatReportKey: ThreatReportKey = {
        chatKey,
        sessionId,
        epochTimeSecs,
        requestType:
          typeof result?.request_type === 'string' ? result.request_type : 'threat_report',
      };
      setThreatReportKey(threatReportKey);
      void queryClient.invalidateQueries({
        queryKey: [...edgeLambdaKeys.all, 'threatReportVersions'],
      });
      void trackEvent('threat_report_created', {
        user: authState?.idToken?.claims.email,
        case_id: caseId,
        session_id: sessionId,
        work_product_name: nameToUse,
        success: true,
        identity: { tenantName: tenantName ?? undefined },
      });
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 3000,
        title: t('main.creatingThreatReport'),
        description: t('main.creatingThreatReportDescription'),
        level: ToastLevel.INFO,
      });
      setTimeout(() => {
        setSelectedView('threat-report');
      }, 1000);
    } catch (error) {
      setIsThreatReportProcessing(false);
      let description = 'Could not start threat report generation. Please try again.';
      if (error instanceof AssistantApiError) {
        const raw = (error.detail ?? '').trim();
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { error?: unknown; message?: unknown };
            const extracted =
              typeof parsed.error === 'string'
                ? parsed.error
                : typeof parsed.message === 'string'
                  ? parsed.message
                  : raw;
            description = extracted.length > 280 ? `${extracted.slice(0, 277)}…` : extracted;
          } catch {
            description = raw.length > 280 ? `${raw.slice(0, 277)}…` : raw;
          }
        } else if (error.message) {
          description = error.message;
        }
      }
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.threatFailedToStart'),
        description,
        level: ToastLevel.ERROR,
      });
    }
  };


  // TR-1117: kick off an AI-generated timeline for the selected case. Posts
  // /chat with timelineBuild:true (the assistant-service returns 202 and runs
  // the timeline-builder agent in the background), polls chat history until the
  // build settles, then refreshes the case's work products so the new timeline
  // appears in the index. Best-effort and non-blocking: the user can keep
  // working while it runs.
  const notifyTimelineGenerating = () => {
    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: 4000,
      title: t('main.generatingTimeline'),
      description: t('main.generatingTimelineDescription'),
      level: ToastLevel.INFO,
    });
  };

  const handleGenerateTimeline = async (
    timelineScopesAndFiles?: ScopesObject,
    timelineTitle?: string,
    timelinePrompt?: string,
    targetTimelineId?: string,
    caseIdOverride?: string,
  ): Promise<TimelineGenerationResult> => {
    if (!featureFlags?.chatAutogenTimeline) {
      return false;
    }
    const buildForCase = caseIdOverride ?? caseId;
    if (
      !buildForCase ||
      buildForCase.trim() === '' ||
      buildForCase === '*' ||
      buildForCase === 'All Cases'
    ) {
      return false;
    }
    if (generatingTimelineCaseIds.includes(buildForCase)) {
      return false;
    }

    const cellId = resolveCellIdFromIdToken(authState?.idToken?.idToken || '');
    if (!cellId || !realAccessToken) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.timelineGenerationFailedToStart'),
        description: !cellId
          ? t('main.timelineMissingCell')
          : t('main.sessionMissingToken'),
        level: ToastLevel.ERROR,
      });
      return false;
    }

    const idTokenInfo = getTokenInfo(authState?.idToken?.idToken || '');
    const accessTokenInfo = getTokenInfo(realAccessToken || '');
    const userId = getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
    const requestedTimelinePrompt = (timelinePrompt ?? '').trim();
    const requestedTargetTimelineId = (targetTimelineId ?? '').trim();
    const requestedTimelineTitle = requestedTargetTimelineId
      ? (timelineTitle ?? '').trim()
      : (timelineTitle ?? '').trim() || getDefaultTimelineTitleForCase(buildForCase, t);
    if (requestedTimelineTitle && requestedTimelineTitle.length > TIMELINE_TITLE_MAX_LENGTH) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.titleTooLong'),
        description: t('main.titleTooLongGenerate', { max: TIMELINE_TITLE_MAX_LENGTH }),
        level: ToastLevel.ERROR,
      });
      return false;
    }
    const requestedScopesAndFiles = timelineScopesAndFiles ?? {
      scopes: [],
      files: [],
      excludedScopes: [],
      excludedFiles: [],
      contentCategories: [],
      excludedContentCategories: [],
      evidenceCategoryChips: [],
      evidenceCategoryScopes: [],
      excludedEvidenceCategoryScopes: [],
      evidenceAllFileTypes: false,
    };
    const { bodyFields: timelineScopeBodyFields } = buildAssistantChatScopeFields(
      requestedScopesAndFiles,
      { contentCategoriesEnabled: !!featureFlags?.contentCategories },
    );
    const timelineBuildStartedAt = Date.now();
    const timelineIdsBeforeBuild = new Set(multipleTimelinesAvailable.map((item) => item.id));
    let targetEventCountBeforeBuild: number | null = null;

    const fetchTimelineSnapshot = async (
      timelineId: string,
    ): Promise<{ eventCount: number; hasRecentlyCreatedEvent: boolean } | null> => {
      try {
        const response: any = await queryTimelines({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          docId: timelineId,
          tags: await caseTimelineTagFor(buildForCase),
        });
        const events = response?.timeline?.events;
        if (!Array.isArray(events)) return null;
        const activeEvents = events.filter((event: { deleted?: boolean }) => !event.deleted);
        return {
          eventCount: activeEvents.length,
          hasRecentlyCreatedEvent: activeEvents.some((event: { createdAt?: unknown }) => {
            if (typeof event.createdAt !== 'string') return false;
            const createdAt = Date.parse(event.createdAt);
            return Number.isFinite(createdAt) && createdAt >= timelineBuildStartedAt - 10_000;
          }),
        };
      } catch {
        return null;
      }
    };

    const reconcileTimelineAfterAssistantError = async (): Promise<TimelineDropdownItem[] | null> => {
      for (let attempt = 0; attempt < TIMELINE_ERROR_RECONCILIATION_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, TIMELINE_ERROR_RECONCILIATION_INTERVAL_MS),
        );

        const workProducts = await fetchWorkProductsForCase(buildForCase);
        if (requestedTargetTimelineId) {
          const currentSnapshot = await fetchTimelineSnapshot(requestedTargetTimelineId);
          if (
            currentSnapshot &&
            (
              (
                targetEventCountBeforeBuild !== null &&
                currentSnapshot.eventCount > targetEventCountBeforeBuild
              ) ||
              (
                targetEventCountBeforeBuild === null &&
                currentSnapshot.hasRecentlyCreatedEvent
              )
            )
          ) {
            return workProducts;
          }
          continue;
        }

        const createdTimeline = workProducts.find((item) => {
          if (timelineIdsBeforeBuild.has(item.id) || item.title !== requestedTimelineTitle) {
            return false;
          }
          const createdAt = Date.parse(item.createdAt);
          return Number.isFinite(createdAt) && createdAt >= timelineBuildStartedAt - 10_000;
        });
        if (createdTimeline) {
          return workProducts;
        }
      }
      return null;
    };

    const attachChatEventsAfterCreate =
      !requestedTargetTimelineId && !!pendingTimelineCreation?.creatingEventFromChat;

    const resolveCreatedTimelineId = (
      workProducts: TimelineDropdownItem[],
      persistenceTimelineId?: unknown,
    ): string | undefined => {
      if (typeof persistenceTimelineId === 'string' && persistenceTimelineId.trim() !== '') {
        return persistenceTimelineId.trim();
      }
      return workProducts.find((item) => {
        if (timelineIdsBeforeBuild.has(item.id) || item.title !== requestedTimelineTitle) {
          return false;
        }
        const createdAt = Date.parse(item.createdAt);
        return Number.isFinite(createdAt) && createdAt >= timelineBuildStartedAt - 10_000;
      })?.id;
    };

    const reopenTimelineSelection = (preferredTimelineId?: string) => {
      setModalSelectedTimeline(preferredTimelineId);
      setPleaseSelectTimeline(true);
    };

    const attachPendingChatEventsToCreatedTimeline = async (
      workProducts: TimelineDropdownItem[],
      persistenceTimelineId?: unknown,
    ) => {
      if (!attachChatEventsAfterCreate) return;
      setIsTimelineNameModalOpen(false);
      const createdId = resolveCreatedTimelineId(workProducts, persistenceTimelineId);
      if (!createdId) {
        reopenTimelineSelection();
        return;
      }
      const attached = await fetchNewTimeline(createdId, buildForCase, false, true);
      if (attached) {
        setPendingTimelineCreation(null);
        return;
      }
      reopenTimelineSelection(createdId);
    };

    setGeneratingTimelineCaseIds((prev) =>
      prev.includes(buildForCase) ? prev : [...prev, buildForCase],
    );
    notifyTimelineGenerating();

    try {
      if (requestedTargetTimelineId) {
        const targetSnapshot = await fetchTimelineSnapshot(requestedTargetTimelineId);
        targetEventCountBeforeBuild = targetSnapshot?.eventCount ?? null;
      }

      const postBody: any = {
        timelineBuild: true,
        deepDive: false,
        threatReport: false,
        query: requestedTimelinePrompt,
        selectedCase: buildForCase,
        ...(await caseFolderNameFields(buildForCase)),
        ...timelineScopeBodyFields,
        userId,
        ...(requestedTimelineTitle && { timelineTitle: requestedTimelineTitle }),
        ...(requestedTargetTimelineId && { targetTimelineId: requestedTargetTimelineId }),
        sessionId: null,
        requestType: 'timeline_build',
      };
      const timelineResponseLanguage = responseLanguageForLocale(readStoredLocale());
      if (timelineResponseLanguage) postBody.responseLanguage = timelineResponseLanguage;

      let result = await postAssistantChat({
        cellId,
        accessToken: realAccessToken,
        idToken: authState?.idToken?.idToken,
        body: postBody,
      });

      const pollBody = {
        timelineBuild: true,
        sessionId: result?.sessionId,
        epochTimeSecs: result?.epoch_time_secs,
        chatKey: result?.chat_key,
        requestType: result?.request_type ?? 'timeline_build',
      };

      const startTime = Date.now();
      while (isEnrichmentHistoryPending(result?.status) && Date.now() - startTime < TIMELINE_BUILD_POLL_TIMEOUT_MS) {
        await new Promise((resolve) => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
        result = await fetchChatHistory({
          cellId,
          accessToken: realAccessToken,
          idToken: authState?.idToken?.idToken,
          body: pollBody,
        }) as Record<string, unknown>;
      }

      // The user may have switched cases while the build ran; only touch the UI
      // for the case the build was started for.
      const stillOnCase = caseIdRef.current === buildForCase;

      if (result?.status === 'error') {
        const reconciledWorkProducts = await reconcileTimelineAfterAssistantError();
        const stillOnCaseAfterReconciliation = caseIdRef.current === buildForCase;
        if (reconciledWorkProducts) {
          if (stillOnCaseAfterReconciliation) {
            if (!requestedTargetTimelineId || reconciledWorkProducts.length > 0) {
              setMultipleTimelinesAvailable(reconciledWorkProducts);
            }
            if (
              requestedTargetTimelineId &&
              currentTimelineIdRef.current === requestedTargetTimelineId
            ) {
              await fetchNewTimeline(
                requestedTargetTimelineId,
                buildForCase,
                true,
                false,
                {
                  preserveTimelineList: true,
                  preserveCurrentTimelineOnError: true,
                  onlyUpdateIfCurrentTimelineId: requestedTargetTimelineId,
                },
              );
            }
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 4000,
              title: requestedTargetTimelineId ? 'Timeline Events Ready' : 'Timeline Ready',
              description: requestedTargetTimelineId
                ? 'Generated events have been added to the selected timeline.'
                : 'Your generated timeline is now available in Work Products.',
              level: ToastLevel.SUCCESS,
            });
            await attachPendingChatEventsToCreatedTimeline(reconciledWorkProducts);
          }
          return true;
        }

        if (stillOnCaseAfterReconciliation) {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 5000,
            title: t('main.timelineGenerationFailed'),
            description: t('main.timelineBuildIncomplete'),
            level: ToastLevel.ERROR,
          });
        }
        return false;
      }

      if (isEnrichmentHistoryPending(result?.status)) {
        if (stillOnCase) {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 5000,
            title: t('main.timelineStillGenerating'),
            description: t('main.timelineStillGeneratingDescription'),
            level: ToastLevel.INFO,
          });
        }
        return 'pending';
      }

      let timelinePersistence: {
        persisted?: unknown;
        reason?: unknown;
        timeline_id?: unknown;
        target_timeline_id?: unknown;
      } | undefined;
      try {
        const completedAnswer =
          typeof result?.answer === 'string'
            ? JSON.parse(result.answer)
            : result?.answer;
        if (
          completedAnswer &&
          typeof completedAnswer === 'object' &&
          completedAnswer.timeline_persistence &&
          typeof completedAnswer.timeline_persistence === 'object'
        ) {
          timelinePersistence = completedAnswer.timeline_persistence;
        }
      } catch {
        // Older assistant-service responses may not contain a JSON answer blob.
      }

      const reportedPersistenceTimelineIds = [
        timelinePersistence?.timeline_id,
        timelinePersistence?.target_timeline_id,
      ].filter(
        (timelineId): timelineId is string =>
          typeof timelineId === 'string' && timelineId.trim() !== '',
      );
      const persistedToWrongTimeline =
        !!requestedTargetTimelineId &&
        timelinePersistence?.persisted === true &&
        reportedPersistenceTimelineIds.length > 0 &&
        reportedPersistenceTimelineIds.some(
          (timelineId) => timelineId !== requestedTargetTimelineId,
        );
      if (timelinePersistence?.persisted === false || persistedToWrongTimeline) {
        if (stillOnCase) {
          const rawReason =
            typeof timelinePersistence?.reason === 'string'
              ? timelinePersistence.reason.trim()
              : '';
          const reason = rawReason.length > 240
            ? `${rawReason.slice(0, 237)}…`
            : rawReason;
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 6000,
            title: t('main.timelineEventsNotSaved'),
            description: persistedToWrongTimeline
              ? t('main.timelineEventsWrongTimeline')
              : reason || t('main.timelineEventsNotSavedDescription'),
            level: ToastLevel.ERROR,
          });
        }
        return false;
      }

      // Refresh the case's work products so the freshly-built timeline appears.
      const workProducts = await fetchWorkProductsForCase(buildForCase);
      if (caseIdRef.current === buildForCase) {
        if (!requestedTargetTimelineId || workProducts.length > 0) {
          setMultipleTimelinesAvailable(workProducts);
        }
        const shouldReloadTargetTimeline =
          !!requestedTargetTimelineId &&
          currentTimelineIdRef.current === requestedTargetTimelineId;
        if (shouldReloadTargetTimeline) {
          const didReloadTimeline = await fetchNewTimeline(
            requestedTargetTimelineId,
            buildForCase,
            true,
            false,
            {
              preserveTimelineList: true,
              preserveCurrentTimelineOnError: true,
              onlyUpdateIfCurrentTimelineId: requestedTargetTimelineId,
            },
          );
          if (!didReloadTimeline) {
            setToast({
              setOpen: setToastOpen,
              open: true,
              location: 'bottom-right',
              time: Date.now(),
              duration: 5000,
              title: t('main.timelineEventsGenerated'),
              description: t('main.timelineEventsGeneratedDescription'),
              level: ToastLevel.WARNING,
            });
            return false;
          }
        }
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 4000,
          title: requestedTargetTimelineId ? 'Timeline Events Ready' : 'Timeline Ready',
          description: requestedTargetTimelineId
            ? 'Generated events have been added to the selected timeline.'
            : 'Your generated timeline is now available in Work Products.',
          level: ToastLevel.SUCCESS,
        });
        await attachPendingChatEventsToCreatedTimeline(
          workProducts,
          timelinePersistence?.timeline_id,
        );
      }
      return true;
    } catch (error) {
      let description = 'Could not generate a timeline. Please try again.';
      if (error instanceof AssistantApiError) {
        const raw = (error.detail ?? '').trim();
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { error?: unknown; message?: unknown };
            const extracted =
              typeof parsed.error === 'string'
                ? parsed.error
                : typeof parsed.message === 'string'
                  ? parsed.message
                  : raw;
            description = extracted.length > 280 ? `${extracted.slice(0, 277)}…` : extracted;
          } catch {
            description = raw.length > 280 ? `${raw.slice(0, 277)}…` : raw;
          }
        } else if (error.message) {
          description = error.message;
        }
      }
      if (caseIdRef.current === buildForCase) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.timelineGenerationFailed'),
          description,
          level: ToastLevel.ERROR,
        });
      }
      return false;
    } finally {
      setGeneratingTimelineCaseIds((prev) => prev.filter((id) => id !== buildForCase));
    }
  };

  const sourceFileScopePath = (source: Source): string | null => {
    const candidates = [
      source.sourceFilePath,
      source.sourceUrl,
      source.truncatedFilePath,
      source.dispatchedFilePath,
      source.analysisFilePath,
    ];
    for (const candidate of candidates) {
      const trimmed = (candidate ?? '').trim();
      if (!trimmed) {
        continue;
      }
      return toProvenanceLookupPath(trimmed);
    }
    return null;
  };

  const buildTimelinePromptFromAnswer = ({ question, answerText }: GenerateTimelineFromAnswerArgs) => {
    const trimmedQuestion = question.trim();
    const trimmedAnswer = answerText.trim();
    return [
      'Generate timeline events based on the assistant answer below.',
      'Only use evidence from the source files scoped with this request. Do not add events that are unsupported by those sources.',
      trimmedQuestion ? `Original user question:\n${trimmedQuestion}` : null,
      `Assistant answer:\n${trimmedAnswer}`,
    ].filter(Boolean).join('\n\n');
  };

  const prepareTimelineBuildFromAnswer = (args: GenerateTimelineFromAnswerArgs): PendingAnswerTimelineBuild | null => {
    if (!args.answerText.trim()) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.timelineGenerationFailedToStart'),
        description: t('main.timelineNotEnoughText'),
        level: ToastLevel.ERROR,
      });
      return null;
    }

    const files = Array.from(
      new Set(
        args.sources
          .map(sourceFileScopePath)
          .filter((path): path is string => !!path),
      ),
    );
    if (files.length === 0) {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 5000,
        title: t('main.timelineGenerationFailedToStart'),
        description: t('main.timelineNoSources'),
        level: ToastLevel.ERROR,
      });
      return null;
    }

    return {
      prompt: buildTimelinePromptFromAnswer(args),
      epochTimeSecs: args.epochTimeSecs,
      scopesAndFiles: {
        scopes: [],
        files,
        excludedScopes: [],
        excludedFiles: [],
        contentCategories: [],
        excludedContentCategories: [],
        evidenceCategoryChips: [],
        evidenceCategoryScopes: [],
        excludedEvidenceCategoryScopes: [],
        evidenceAllFileTypes: false,
      },
    };
  };

  const getDefaultAnswerTimelineTarget = (): {
    choice: AnswerTimelineTargetChoice;
    selectedTimelineId: string | undefined;
  } => {
    const firstExistingTimeline = multipleTimelinesAvailable[0];
    if (currentTimelineId) {
      return { choice: 'existing', selectedTimelineId: currentTimelineId };
    }
    if (firstExistingTimeline?.id) {
      return { choice: 'existing', selectedTimelineId: firstExistingTimeline.id };
    }
    return { choice: 'new', selectedTimelineId: undefined };
  };

  const resetAnswerTimelineTargetModal = () => {
    const defaultTarget = getDefaultAnswerTimelineTarget();
    setAnswerTimelineTargetModalOpen(false);
    setPendingAnswerTimelineBuild(null);
    pendingAnswerTimelineCaseIdRef.current = undefined;
    setAnswerTimelineSelectedTimeline(defaultTarget.selectedTimelineId);
    setAnswerTimelineNewTitle('');
    setAnswerTimelineTargetChoice(defaultTarget.choice);
    setAnswerTimelineTargetSubmitting(false);
  };

  const generateTimelineFromAnswer = (args: GenerateTimelineFromAnswerArgs) => {
    if (
      !featureFlags?.betterWand ||
      !featureFlags?.chatAutogenTimeline ||
      !caseId ||
      caseId.trim() === '' ||
      caseId === '*' ||
      caseId === 'All Cases'
    ) {
      return;
    }

    const prepared = prepareTimelineBuildFromAnswer(args);
    if (!prepared) {
      return;
    }
    const defaultTarget = getDefaultAnswerTimelineTarget();
    setPendingAnswerTimelineBuild(prepared);
    pendingAnswerTimelineCaseIdRef.current = caseId;
    setAnswerTimelineTargetChoice(defaultTarget.choice);
    setAnswerTimelineSelectedTimeline(defaultTarget.selectedTimelineId);
    setAnswerTimelineNewTitle(getDefaultTimelineTitleForCase(caseId, t));
    setAnswerTimelineTargetModalOpen(true);
  };

  useEffect(() => {
    if (answerTimelineTargetModalOpen && pendingAnswerTimelineCaseIdRef.current !== caseId) {
      resetAnswerTimelineTargetModal();
    }
  }, [answerTimelineTargetModalOpen, caseId]);

  const trimmedAnswerTimelineNewTitle = answerTimelineNewTitle.trim();
  const answerTimelineNewTitleLength = trimmedAnswerTimelineNewTitle.length;
  const isAnswerTimelineNewTitleTooLong = answerTimelineNewTitleLength > TIMELINE_TITLE_MAX_LENGTH;
  const answerTimelineExistingOptions = multipleTimelinesAvailable
    .map((timeline) => ({
      value: timeline.id,
      label: timeline.title?.trim() || timeline.id,
    }));
  if (
    currentTimelineId &&
    !answerTimelineExistingOptions.some((option) => option.value === currentTimelineId)
  ) {
    answerTimelineExistingOptions.unshift({
      value: currentTimelineId,
      label: currentTimelineId,
    });
  }
  const selectedAnswerTimelineStillAvailable =
    !!answerTimelineSelectedTimeline &&
    answerTimelineExistingOptions.some((option) => option.value === answerTimelineSelectedTimeline);
  const canSubmitAnswerTimelineTarget = (() => {
    const citedSourcesRemain = (pendingAnswerTimelineBuild?.scopesAndFiles.files.length ?? 0) > 0;
    if (
      !pendingAnswerTimelineBuild ||
      !citedSourcesRemain ||
      answerTimelineTargetSubmitting ||
      generatingTimelineCaseIds.includes(caseId ?? '')
    ) {
      return false;
    }
    if (answerTimelineTargetChoice === 'existing') {
      return selectedAnswerTimelineStillAvailable;
    }
    return !!trimmedAnswerTimelineNewTitle && !isAnswerTimelineNewTitleTooLong;
  })();

  const handleConfirmAnswerTimelineTarget = async () => {
    if (!pendingAnswerTimelineBuild || !canSubmitAnswerTimelineTarget) {
      return;
    }

    const { scopesAndFiles, prompt, epochTimeSecs } = pendingAnswerTimelineBuild;
    const buildForCase = pendingAnswerTimelineCaseIdRef.current ?? caseId;
    const answerBuildKey = `${buildForCase ?? ''}:${epochTimeSecs}`;
    const targetTimelineId =
      answerTimelineTargetChoice === 'existing'
        ? answerTimelineSelectedTimeline
        : undefined;
    const timelineTitle =
      answerTimelineTargetChoice === 'new'
        ? trimmedAnswerTimelineNewTitle
        : undefined;

    setAnswerTimelineTargetSubmitting(true);
    if (targetTimelineId && targetTimelineId !== currentTimelineId) {
      const didLoadTimeline = await fetchNewTimeline(
        targetTimelineId,
        buildForCase,
        true,
        false,
        {
          preserveTimelineList: true,
          preserveCurrentTimelineOnError: true,
          onlyUpdateIfCurrentCaseId: buildForCase,
        },
      );
      if (!didLoadTimeline) {
        setAnswerTimelineTargetSubmitting(false);
        if (caseIdRef.current !== buildForCase) {
          return;
        }
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 5000,
          title: t('main.timelineNotSelected'),
          description: t('main.timelineNotSelectedDescription'),
          level: ToastLevel.ERROR,
        });
        return;
      }
      sessionStorage.setItem('presentation', targetTimelineId);
      sessionStorage.setItem('timeline', targetTimelineId);
    }

    if (!buildForCase || caseIdRef.current !== buildForCase) {
      setAnswerTimelineTargetSubmitting(false);
      return;
    }

    setAnswerTimelineGenerationStatuses((prev) => ({
      ...prev,
      [answerBuildKey]: 'pending',
    }));
    resetAnswerTimelineTargetModal();
    const generated = await handleGenerateTimeline(
      scopesAndFiles,
      timelineTitle,
      prompt,
      targetTimelineId,
      buildForCase,
    );
    if (generated === 'pending') {
      window.setTimeout(() => {
        setAnswerTimelineGenerationStatuses((latest) => {
          if (latest[answerBuildKey] !== 'pending') return latest;
          const next = { ...latest };
          delete next[answerBuildKey];
          return next;
        });
      }, TIMELINE_PENDING_STATUS_EXPIRY_MS);
      return;
    }
    setAnswerTimelineGenerationStatuses((prev) => {
      if (generated === true) {
        return {
          ...prev,
          [answerBuildKey]: 'completed',
        };
      }
      const next = { ...prev };
      delete next[answerBuildKey];
      return next;
    });
  };

  const answerTimelineTargetModal = (
    <AnswerTimelineTargetModal
      isOpen={answerTimelineTargetModalOpen}
      submitting={answerTimelineTargetSubmitting}
      choice={answerTimelineTargetChoice}
      onChoiceChange={setAnswerTimelineTargetChoice}
      newTitle={answerTimelineNewTitle}
      onNewTitleChange={setAnswerTimelineNewTitle}
      existingOptions={answerTimelineExistingOptions}
      selectedTimelineId={answerTimelineSelectedTimeline}
      onSelectedTimelineChange={setAnswerTimelineSelectedTimeline}
      scopes={pendingAnswerTimelineBuild?.scopesAndFiles ?? emptyScopesNew()}
      onScopesChange={(next) => {
        setPendingAnswerTimelineBuild((prev) => (
          prev ? { ...prev, scopesAndFiles: next } : prev
        ));
      }}
      canSubmit={canSubmitAnswerTimelineTarget}
      onCancel={resetAnswerTimelineTargetModal}
      onConfirm={() => void handleConfirmAnswerTimelineTarget()}
    />
  );


  const onFeedbackClick = () => {
    safeShowIntercom();
  };

  // Handle global keyboard shortcut for feedback button
  useEffect(() => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        safeShowIntercom();
      } else if (e.key === 'Escape') {
        setIsFullscreen(false);
      } else if (featureFlags?.sandbox && e.ctrlKey && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
        setSelectedView('sandbox');
      }
    };
    (window as any).addEventListener('keydown', handleKeyDown);
    return () => {
      (window as any).removeEventListener('keydown', handleKeyDown);
    };
  }, [featureFlags?.sandbox, isFullscreen]);

  const { width } = useWindowSize();

  useEffect(() => {
    if (!flagsReady) return;
    if (featureFlags?.supportFallbackForm || !allowsNonEssentialCookies) {
      safeHideIntercom();
      safeShutdownIntercom();
    } else if (width && width < TINY_VIEWPORT_WIDTH) {
      safeShutdownIntercom();
    } else {
      const intercomUserReady =
        !!authState?.idToken?.claims.email &&
        authState?.idToken?.claims.email !== '' &&
        !!authState?.idToken?.claims.name &&
        authState?.idToken?.claims.name !== '';
      if (
        intercomJwt &&
        selectedView !== 'presentation' &&
        selectedView !== 'courtroom' &&
        selectedView !== 'horizontal' &&
        intercomUserReady
      ) {
        const intercomInit = {
          intercom_user_jwt: intercomJwt,
          app_id: 'bab3jdz3',
          user_id: authState?.idToken?.claims.email,
          name: authState?.idToken?.claims.name,
          email: authState?.idToken?.claims.email,
        };
        Intercom(intercomInit);
      } else if (!intercomUserReady || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal') {
        safeHideIntercom();
        safeShutdownIntercom();
      } else {
        safeHideIntercom();
      }
    }
    if (selectedView !== 'chat') {
      const svgElements = document.querySelectorAll('.svg-highlight-answer-item');
      svgElements.forEach((svg) => {
        svg.remove();
      });
    }
  }, [flagsReady, featureFlags?.supportFallbackForm, allowsNonEssentialCookies, intercomJwt, width, selectedView, authState?.idToken?.claims.email, authState?.idToken?.claims.name]);

  useEffect(() => {
    setIsFullscreen(false);
  }, [selectedView]);

  const handleOptionChange = (option: string | undefined, viewOverride?: View) => {
    // Nav bar clears selection when the case is missing from the loaded list.
    // During Back/Forward apply that can race and wipe the case history just restored.
    if (
      option === undefined &&
      caseIdRef.current &&
      (suppressNavSideEffectsRef.current || skipForceChatOnCaseChangeRef.current)
    ) {
      return;
    }
    if (option !== caseId) {
      setNewTimelineIsAvailable(false);
      setSelectedSecondOption(undefined);
      handleSetCaseId(option);
      setCreatingTimeline(false);
      setIsEditableTimeline(false);
      setTimelineCreated(false);

      // Clear work product context when switching cases (not during URL/session restore)
      if (!skipForceChatOnCaseChangeRef.current) {
        sessionStorage.removeItem('lastWorkProductContext');
        sessionStorage.removeItem('timeline');
        sessionStorage.removeItem('presentation');
        setLastWorkProductView(null);
        setLastWorkProductTimelineId(undefined);
        setLastWorkProductCaseId(undefined);
        if (isTimelineNavView(selectedView)) {
          setIsEventModalOpen(false);
          clearPresentationViewState();
        }
      }

      // Fetch work products when a case is selected
      if (option) {
        setMultipleTimelinesAvailable([]);
        setLoadingWorkProducts(true);
        fetchWorkProductsForCase(option).then((workProducts) => {
          setMultipleTimelinesAvailable(workProducts);
          setLoadingWorkProducts(false);
        }).catch(() => {
          setLoadingWorkProducts(false);
        });
      } else {
        setMultipleTimelinesAvailable([]);
        setLoadingWorkProducts(false);
      }

      // Timeline views are case-scoped — the previous timeline will not exist
      // on the new case, so land on the work products index. Restore path
      // re-applies the saved view instead.
      if (!skipForceChatOnCaseChangeRef.current) {
        const nextView =
          viewOverride ??
          viewAfterCaseChange(selectedView, {
            caseFileManagementEnabled,
          });
        setSelectedView(nextView);
        sessionStorage.setItem('view', nextView);
      }
      sessionStorage.setItem('case', option || '');
    }
  };

  const handleSelectTimelineFromWorkProducts = async (timelineId: string) => {
    // Save context before navigating (default to table view)
    const context = {
      view: 'table' as View,
      timelineId: timelineId,
      caseId: caseId,
    };
    setLastWorkProductView('table');
    setLastWorkProductTimelineId(timelineId);
    setLastWorkProductCaseId(caseId);
    sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));

    setSelectedSecondOption(timelineId);
    setCurrentTimelineId(timelineId);
    await fetchNewTimeline(timelineId, caseId, false);
    // Default to table view when opening from work products
    setSelectedView('table');
    sessionStorage.setItem('view', 'table');
    sessionStorage.setItem('timeline', timelineId);
  };

  // Work products back button must always go directly to the index; do not step through Details/Presentation.
  const handleBackToWorkProducts = () => {
    setSelectedView('work-product-index');
    sessionStorage.setItem('view', 'work-product-index');
  };

  const handleWorkProductViewModeChange = (mode: 'grid' | 'list') => {
    setWorkProductViewMode(mode);
    sessionStorage.setItem('workProductViewMode', mode);
  };

  const handleWorkProductsClick = () => {
    // If already viewing a work product, do nothing
    if (isWorkProductView(selectedView)) {
      return;
    }

    // Check if there's saved work product context
    let contextToRestore = null;

    if (lastWorkProductView && lastWorkProductCaseId) {
      contextToRestore = {
        view: lastWorkProductView,
        timelineId: lastWorkProductTimelineId,
        caseId: lastWorkProductCaseId,
      };
    } else {
      const savedContext = sessionStorage.getItem('lastWorkProductContext');
      if (savedContext) {
        try {
          contextToRestore = JSON.parse(savedContext);
        } catch (e) {
          console.error('Failed to parse saved work product context:', e);
        }
      }
    }

    if (contextToRestore) {
      const { view } = contextToRestore;
      // Immediately update the view state for instant visual feedback
      setSelectedView(view);
      sessionStorage.setItem('view', view);
      // Then restore the full context asynchronously
      handleRestoreWorkProduct();
    } else {
      // No saved context, go to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };

  const handleSelectThreatReport = (key?: ThreatReportKey) => {
    if (key) {
      setThreatReportKey(key);
    }
    // Save context before navigating
    const context = {
      view: 'threat-report' as View,
      timelineId: undefined,
      caseId: caseId,
    };
    setLastWorkProductView('threat-report');
    setLastWorkProductTimelineId(undefined);
    setLastWorkProductCaseId(caseId);
    sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));

    setIsThreatReportProcessing(false); // just load the last one
    setSelectedView('threat-report');
    sessionStorage.setItem('view', 'threat-report');
  };

  const handleRestoreWorkProduct = async () => {
    // Try to restore from state first, then from sessionStorage
    let contextToRestore = null;

    if (lastWorkProductView && lastWorkProductCaseId) {
      contextToRestore = {
        view: lastWorkProductView,
        timelineId: lastWorkProductTimelineId,
        caseId: lastWorkProductCaseId,
      };
    } else {
      const savedContext = sessionStorage.getItem('lastWorkProductContext');
      if (savedContext) {
        try {
          contextToRestore = JSON.parse(savedContext);
        } catch (e) {
          console.error('Failed to parse saved work product context:', e);
        }
      }
    }

    if (contextToRestore) {
      const { view, timelineId, caseId: savedCaseId } = contextToRestore;

      // Ensure we have the correct case selected
      if (savedCaseId && savedCaseId !== caseId) {
        handleSetCaseId(savedCaseId);
        // Wait a bit for the case to be set before restoring the timeline
        // The case change will trigger effects that set up the case context
        setTimeout(async () => {
          await restoreWorkProductView(view, timelineId, savedCaseId);
        }, 100);
      } else {
        await restoreWorkProductView(view, timelineId, savedCaseId || caseId);
      }
    } else {
      // No saved context, go to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };

  const restoreWorkProductView = async (view: View, timelineId: string | undefined, caseIdToUse: string | undefined) => {
    // Restore the view
    if (view === 'threat-report') {
      setSelectedView('threat-report');
      sessionStorage.setItem('view', 'threat-report');
    } else if (view === 'table' || view === 'presentation' || view === 'courtroom' || view === 'horizontal') {
      // For timeline views, we need to restore the timeline
      if (timelineId && caseIdToUse) {
        const safeView: View =
          view === 'courtroom' && !featureFlags?.courtroomMode
            ? 'table'
            : view === 'horizontal' && !featureFlags?.horizontalTimeline
              ? 'table'
              : view;
        // Set the view first so the loading message shows in the correct view
        setSelectedView(safeView);
        sessionStorage.setItem('view', safeView);
        setSelectedSecondOption(timelineId);
        setCurrentTimelineId(timelineId);
        // fetchNewTimeline will set loadingTimeline to true
        await fetchNewTimeline(timelineId, caseIdToUse, false);
        sessionStorage.setItem('timeline', timelineId);
      } else {
        // No timeline ID or case ID, go to work product index
        setSelectedView('work-product-index');
        sessionStorage.setItem('view', 'work-product-index');
      }
    } else {
      // Fallback to work product index
      setSelectedView('work-product-index');
      sessionStorage.setItem('view', 'work-product-index');
    }
  };

  const handleSecondOptionChange = async (optionIn: string | undefined, creatingEventFromChat?: boolean) => {
    if (optionIn) {
      if (optionIn === CREATE_NEW_TIMELINE_ID) {
        setEntityServicePollAllJobsCount(0);
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        sessionStorage.removeItem('timeline');
        setSelectedSecondOption(undefined);
        setCurrentTimelineId(undefined);
        setNewTimelineIsAvailable(false);
        setMultipleTimelinesAvailable([]);
        await onCreateTimeline(creatingEventFromChat);
      } else {
        sessionStorage.setItem('presentation', optionIn);
        sessionStorage.setItem('timeline', optionIn);
        setSelectedSecondOption(optionIn || undefined);
        setCurrentTimelineId(optionIn);
        if (creatingEventFromChat && (chatEventToAdd || parsedEventsFromChat)) {
          setPleaseSelectTimeline(false);
          if (chatEventToAdd || parsedEventsFromChat) {
            fetchNewTimeline(optionIn, caseId, false, true);
          }
        } else {
          await fetchNewTimeline(optionIn, caseId, false);
        }
      }
    } else {
      sessionStorage.removeItem('presentation');
      sessionStorage.removeItem('timeline');
    }
  };

  const csamAccess = useMemo(
    () =>
      resolveCsamAccess({
        csamLimitedUser,
        csamVerifier,
        tenantAccess: !!featureFlags?.csamTenantAccess,
        logAccess: !!featureFlags?.csamLogAccess,
        ready: csamCredentialsReady && ldFlagsSet,
      }),
    [
      csamLimitedUser,
      csamVerifier,
      featureFlags?.csamTenantAccess,
      featureFlags?.csamLogAccess,
      csamCredentialsReady,
      ldFlagsSet,
    ],
  );
  const csamAccessReadyRef = useRef(csamAccess.ready);
  csamAccessReadyRef.current = csamAccess.ready;

  const buildSanitizeOptions = useCallback(() => ({
    courtroomMode: !!featureFlags?.courtroomMode,
    horizontalTimeline: !!featureFlags?.horizontalTimeline,
    canOpenDashboard: csamAccess.canOpenDashboard,
    csamAccessReady: csamAccess.ready,
    sandbox: !!featureFlags?.sandbox,
    caseManagementDashboard: !!featureFlags?.caseManagementDashboard,
    caseFileManagement: !!featureFlags?.caseFileManagement,
    // Omit until permission-service settles — `isUserAdmin` is false on first
    // paint, and an 8s restore timeout must not bounce a bookmarked Account.
    accountAllowed: csamCredentialsReady ? isUserAdmin : undefined,
    caseId: caseIdRef.current,
    personalFilesCaseId,
  }), [
    featureFlags?.courtroomMode,
    featureFlags?.horizontalTimeline,
    csamAccess.canOpenDashboard,
    csamAccess.ready,
    featureFlags?.sandbox,
    featureFlags?.caseManagementDashboard,
    featureFlags?.caseFileManagement,
    csamCredentialsReady,
    isUserAdmin,
    personalFilesCaseId,
  ]);

  const buildCurrentAppNavState = useCallback((): AppNavState => {
    const onTimelineView = isTimelineNavView(selectedView);
    return normalizeAppNavState({
      caseId,
      view: selectedView,
      timelineId: currentTimelineId,
      sessionId: chatSessionId ?? undefined,
      eventIndex: onTimelineView ? eventIndex : undefined,
      eventModal: onTimelineView && isEventModalOpen ? true : undefined,
      wpMode: workProductViewMode,
      threatReportKey:
        selectedView === 'threat-report' ? threatReportKey : undefined,
    });
  }, [
    caseId,
    selectedView,
    currentTimelineId,
    chatSessionId,
    eventIndex,
    isEventModalOpen,
    workProductViewMode,
    threatReportKey,
  ]);

  /**
   * Read-only navigation apply for refresh / Back / Forward / DevTools.
   * Loads remote state via GET-style queries only; toasts are suppressed.
   * Returns `null` when auth isn't ready yet so the caller can retry.
   *
   * Always switches `caseId` to match `pending` (including clearing it) so
   * Back/Forward into an entry for another case restores that case — not the
   * one the user had selected before navigating history.
   *
   * Caller (`runNavApply`) owns suppress/skipForceChat flags so they stay set
   * until after the caseId effect from this apply has run.
   */
  const applyAppNavState = useCallback(
    async (pending: AppNavState): Promise<AppNavState | null> => {
      setToastRaw(undefined);

      const view = sanitizeRestoredView(pending.view, {
        ...buildSanitizeOptions(),
        caseId: pending.caseId || caseIdRef.current,
      });

      // Back/Forward must land on the case encoded in the history URL — even when
      // the user had since switched cases in the UI.
      if (pending.caseId) {
        const caseChanged = pending.caseId !== caseIdRef.current;
        if (caseChanged) {
          if (!realAccessTokenRef.current) {
            return null;
          }
          setNewTimelineIsAvailable(false);
          setSelectedSecondOption(undefined);
          handleSetCaseId(pending.caseId);
          setCreatingTimeline(false);
          setIsEditableTimeline(false);
          setTimelineCreated(false);
          sessionStorage.setItem('case', pending.caseId);
        }

        // Case already selected (refresh) or just switched — load that case's work products.
        if (!realAccessTokenRef.current) {
          return null;
        }
        setLoadingWorkProducts(true);
        try {
          const workProducts = await fetchWorkProductsForCaseRef.current(pending.caseId);
          setMultipleTimelinesAvailable(workProducts);
        } catch {
          // ignore — restore continues with empty work-product list
        } finally {
          setLoadingWorkProducts(false);
        }
      } else if (caseIdRef.current) {
        // History entry has no case — clear the current selection.
        handleSetCaseId(undefined);
        setMultipleTimelinesAvailable([]);
        sessionStorage.removeItem('case');
      }

      if (pending.sessionId) {
        setChatSessionId(pending.sessionId);
        setSeedChatSessionId(pending.sessionId);
      } else {
        setChatSessionId(null);
        setSeedChatSessionId(null);
      }

      // grid is omitted from the URL; missing wpMode must restore grid (not keep list).
      const wpMode = pending.wpMode ?? 'grid';
      setWorkProductViewMode(wpMode);

      if (pending.threatReportKey && view === 'threat-report') {
        setThreatReportKey(pending.threatReportKey);
      } else if (view !== 'threat-report') {
        setThreatReportKey(undefined);
      }

      const caseForTimeline = pending.caseId || caseIdRef.current;
      const needsTimelineLoad =
        !!pending.timelineId &&
        !!caseForTimeline &&
        isTimelineNavView(view);

      let appliedTimelineId: string | undefined;
      if (needsTimelineLoad && pending.timelineId) {
        if (!realAccessTokenRef.current) {
          // Auth not ready — keep pending restore for a later retry.
          return null;
        }
        setSelectedSecondOption(pending.timelineId);
        setCurrentTimelineId(pending.timelineId);
        sessionStorage.setItem('timeline', pending.timelineId);
        const didLoad = await fetchNewTimelineRef.current(
          pending.timelineId,
          caseForTimeline,
          false,
        );
        if (!didLoad) {
          if (!realAccessTokenRef.current) {
            return null;
          }
          // Auth was ready but timeline load failed (missing/deleted) — continue
          // without a selected timeline rather than looping forever.
          setSelectedSecondOption(undefined);
          setCurrentTimelineId(undefined);
          setIsEventModalOpen(false);
          appliedTimelineId = undefined;
        } else {
          appliedTimelineId = pending.timelineId;
          lastTimelineRefetchKeyRef.current = `${caseForTimeline}::${pending.timelineId}`;
          if (pending.eventIndex != null) {
            setEventIndex(pending.eventIndex);
          }
          if (pending.eventModal) {
            setIsEventModalOpen(true);
          } else {
            setIsEventModalOpen(false);
          }
        }
      } else if (pending.timelineId) {
        // Non-timeline view (or missing case): keep timeline id in nav state
        // without loading content. Clear the seed-time loading flag so table /
        // presentation / courtroom cannot stick on "Loading Timeline…".
        setLoadingTimeline(false);
        setSelectedSecondOption(pending.timelineId);
        setCurrentTimelineId(pending.timelineId);
        sessionStorage.setItem('timeline', pending.timelineId);
        setIsEventModalOpen(false);
        appliedTimelineId = pending.timelineId;
      } else {
        // No timeline in pending (or view sanitized off a timeline view).
        setLoadingTimeline(false);
        setSelectedSecondOption(undefined);
        setCurrentTimelineId(undefined);
        setIsEventModalOpen(false);
        appliedTimelineId = undefined;
      }

      setSelectedView(view);
      sessionStorage.setItem('view', view);

      if (isWorkProductView(view)) {
        const context = {
          view,
          timelineId: appliedTimelineId,
          caseId: pending.caseId || caseIdRef.current,
        };
        setLastWorkProductView(view);
        setLastWorkProductTimelineId(context.timelineId);
        setLastWorkProductCaseId(context.caseId);
        sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
      }

      return {
        caseId: pending.caseId || caseIdRef.current,
        view,
        timelineId: appliedTimelineId,
        sessionId: pending.sessionId,
        eventIndex:
          isTimelineNavView(view) && appliedTimelineId
            ? pending.eventIndex
            : undefined,
        eventModal:
          isTimelineNavView(view) && appliedTimelineId && pending.eventModal
            ? true
            : undefined,
        wpMode,
        threatReportKey: view === 'threat-report' ? pending.threatReportKey : undefined,
      };
    },
    // fetchNewTimeline / fetchWorkProductsForCase / handleSetCaseId via refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildSanitizeOptions],
  );

  const runNavApply = useCallback(
    async (pending: AppNavState, onSuccess: (applied: AppNavState) => void) => {
      const generation = ++navApplyGenerationRef.current;
      isApplyingHistoryNavRef.current = true;
      historyApplyLockRef.current = true;
      // Hold through the caseId effect that this apply will schedule. Clearing in
      // applyAppNavState's finally raced ahead of that effect and let the normal
      // case-switch reset wipe the timeline/view we just restored (including when
      // Back/Forward switches back to a previous case).
      suppressNavSideEffectsDepthRef.current += 1;
      suppressNavSideEffectsRef.current = true;
      skipForceChatOnCaseChangeRef.current = true;
      try {
        const applied = await applyAppNavState(pending);
        if (generation !== navApplyGenerationRef.current) return;
        if (applied == null) return;
        onSuccess(applied);
      } finally {
        if (generation === navApplyGenerationRef.current) {
          // Unlock persist immediately so the post-apply URL sync can run.
          isApplyingHistoryNavRef.current = false;
          historyApplyLockRef.current = false;
          // Wake persist: unlock is a ref write, so without this the effect can
          // miss Back/Forward when apply setStates were no-ops / already flushed.
          setNavPersistWake((n) => n + 1);
          // Defer suppress/skip clear until after React flushes setState + caseId effects.
          window.setTimeout(() => {
            if (generation !== navApplyGenerationRef.current) return;
            skipForceChatOnCaseChangeRef.current = false;
            suppressNavSideEffectsDepthRef.current = Math.max(
              0,
              suppressNavSideEffectsDepthRef.current - 1,
            );
            suppressNavSideEffectsRef.current =
              suppressNavSideEffectsDepthRef.current > 0;
          }, 0);
        } else {
          // Superseded by a newer apply/cleanup — drop the depth we added.
          suppressNavSideEffectsDepthRef.current = Math.max(
            0,
            suppressNavSideEffectsDepthRef.current - 1,
          );
          suppressNavSideEffectsRef.current =
            suppressNavSideEffectsDepthRef.current > 0;
          if (suppressNavSideEffectsDepthRef.current === 0) {
            skipForceChatOnCaseChangeRef.current = false;
          }
        }
      }
    },
    [applyAppNavState],
  );

  const handleNavChatSessionApplied = useCallback(() => {
    setSeedChatSessionId(undefined);
  }, []);

  // App-nav session: reuse across refresh (sessionStorage); create on fresh login
  // after logout cleared the previous id. Back/Forward entries are tagged with it.
  useEffect(() => {
    if (!authState?.isAuthenticated) return;
    if (appNavSessionIdRef.current) return;
    appNavSessionIdRef.current = getOrCreateAppNavSessionId();
  }, [authState?.isAuthenticated]);

  // Finish one-time mount bookkeeping. Nav fields are already seeded into React
  // state from URL/sessionStorage so we do not setCaseId/setSelectedView here
  // (that was the post-paint "full app" flash).
  useLayoutEffect(() => {
    if (navRestoreStartedRef.current) return;
    navRestoreStartedRef.current = true;

    const restored = initialAppNavRef.current ?? {};
    pendingNavRestoreRef.current = restored;

    const savedContext = sessionStorage.getItem('lastWorkProductContext');
    if (savedContext) {
      try {
        const context = JSON.parse(savedContext);
        setLastWorkProductView(context.view);
        setLastWorkProductTimelineId(context.timelineId);
        setLastWorkProductCaseId(context.caseId);
      } catch (e) {
        console.error('Failed to parse saved work product context on mount:', e);
      }
    }

    if (!needsAppNavRestore(restored)) {
      // Empty / default-chat restore: reveal immediately. replaceState once so
      // the first user sync does not pushState a phantom Back/Forward step.
      forceReplaceNavUrlRef.current = true;
      lastPersistedNavRef.current = restored;
      lastHistoryCommitRef.current = restored;
      setNavRestoreComplete(true);
      return;
    }

    if (restored.caseId) {
      skipForceChatOnCaseChangeRef.current = true;
      sessionStorage.setItem('case', restored.caseId);
      sessionStorage.removeItem('caseIdFromUrl');
    }
    // Skew-reload used to own restore; general restore covers it now.
    sessionStorage.removeItem('TimePilotSkewReload');
  }, []);

  // Apply view / timeline / chat / threat-report after case (if any) is selected.
  useEffect(() => {
    if (navRestoreComplete) return;
    if (!navRestoreStartedRef.current) return;
    if (!ldFlagsSet || !flagsReady || !csamCredentialsReady) return;

    const pending = pendingNavRestoreRef.current;
    if (!pending || !needsAppNavRestore(pending)) {
      skipForceChatOnCaseChangeRef.current = false;
      setLoadingTimeline(false);
      setLoadingWorkProducts(false);
      setNavRestoreComplete(true);
      return;
    }

    // Timeline restore needs a gateway token; wait rather than clearing selection.
    const pendingView = sanitizeRestoredView(pending.view, {
      ...buildSanitizeOptions(),
      caseId: pending.caseId || caseId,
    });
    if (
      pending.timelineId &&
      isTimelineNavView(pendingView) &&
      !realAccessToken
    ) {
      return;
    }

    void runNavApply(pending, (applied) => {
      lastPersistedNavRef.current = applied;
      lastHistoryCommitRef.current = applied;
      forceReplaceNavUrlRef.current = true;
      writeAppNavToSessionStorage(applied);
      pendingNavRestoreRef.current = null;
      setNavRestoreComplete(true);
    });

    return () => {
      navApplyGenerationRef.current += 1;
      isApplyingHistoryNavRef.current = false;
      historyApplyLockRef.current = false;
    };
  }, [
    navRestoreComplete,
    ldFlagsSet,
    flagsReady,
    csamCredentialsReady,
    caseId,
    realAccessToken,
    runNavApply,
    buildSanitizeOptions,
  ]);

  // Belt-and-suspenders: never leave returning users on "Loading TimePilot…"
  // if identify never fires (e.g. missing claims) or restore stalls.
  useEffect(() => {
    if (navRestoreComplete) return;
    const timer = window.setTimeout(() => {
      if (navRestoreCompleteRef.current) return;
      console.warn('Nav restore timed out; revealing shell with seeded state');

      const pending = pendingNavRestoreRef.current;
      const applyInFlight = isApplyingHistoryNavRef.current;

      // Unstick seed-time loading so controls do not spin forever. Do NOT bump
      // navApplyGeneration or clear pending while an apply is running — that
      // would drop the async timeline/work-product load with no retry.
      if (!applyInFlight) {
        skipForceChatOnCaseChangeRef.current = false;
        suppressNavSideEffectsDepthRef.current = 0;
        suppressNavSideEffectsRef.current = false;
        historyApplyLockRef.current = false;
      }
      setLoadingTimeline(false);
      setLoadingWorkProducts(false);
      setLdFlagsSet(true);
      setNavRestoreComplete(true);
      setNavPersistWake((n) => n + 1);

      if (applyInFlight) {
        // In-flight onSuccess still writes applied state + clears pending.
        return;
      }

      // Do not sanitize ``csam-report`` or Account until clearance has settled —
      // otherwise the timeout would bounce a bookmarked dashboard / Account
      // while the user fetch is still in flight. The credentials-ready effect
      // applies leftover pending.
      const pendingView = pending?.view;
      const waitsOnClearance =
        pendingView === 'csam-report' ||
        pendingView === 'account' ||
        pendingView === 'cases';
      if (waitsOnClearance && !csamAccessReadyRef.current) {
        return;
      }

      if (pending && needsAppNavRestore(pending)) {
        void runNavApply(pending, (applied) => {
          lastPersistedNavRef.current = applied;
          lastHistoryCommitRef.current = applied;
          forceReplaceNavUrlRef.current = true;
          writeAppNavToSessionStorage(applied);
          pendingNavRestoreRef.current = null;
        });
      } else {
        pendingNavRestoreRef.current = null;
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [navRestoreComplete, runNavApply]);

  // Timeout may have revealed the shell while leaving a ``csam-report`` or
  // Account restore pending. Apply it once clearance is known so the bookmark
  // is not lost — but only if nothing else is applying and the user is still
  // on that view.
  useEffect(() => {
    if (!csamAccess.ready || !navRestoreComplete) return;
    if (isApplyingHistoryNavRef.current) return;
    const pending = pendingNavRestoreRef.current;
    const pendingView = pending?.view;
    const pendingIsCsam = pendingView === 'csam-report';
    const pendingIsAccount = pendingView === 'account' || pendingView === 'cases';
    if (!pending || (!pendingIsCsam && !pendingIsAccount)) return;
    const leftBookmarkedView = pendingIsCsam
      ? selectedView !== 'csam-report'
      : selectedView !== 'account' && selectedView !== 'cases';
    const leftBookmarkedCase =
      !!pending.caseId && !!caseId && pending.caseId !== caseId;
    if (leftBookmarkedView || leftBookmarkedCase) {
      pendingNavRestoreRef.current = null;
      return;
    }
    void runNavApply(pending, (applied) => {
      lastPersistedNavRef.current = applied;
      lastHistoryCommitRef.current = applied;
      forceReplaceNavUrlRef.current = true;
      writeAppNavToSessionStorage(applied);
      pendingNavRestoreRef.current = null;
    });
  }, [csamAccess.ready, navRestoreComplete, runNavApply, selectedView, caseId]);

  // LaunchDarkly flag value changes and delayed sanitize options (e.g. My Files
  // case id resolving from claims): keep case/timeline/session; only relocate
  // out of views the new flags or credentials no longer allow.
  useEffect(() => {
    if (!navRestoreComplete || !csamAccess.ready) return;

    const sanitized = sanitizeRestoredView(selectedView, buildSanitizeOptions());
    if (sanitized === selectedView) return;

    forceReplaceNavUrlRef.current = true;
    suppressNavSideEffectsDepthRef.current += 1;
    suppressNavSideEffectsRef.current = true;
    try {
      setSelectedView(sanitized);
      sessionStorage.setItem('view', sanitized);
      if (sanitized !== 'threat-report') {
        setThreatReportKey(undefined);
      }
    } finally {
      suppressNavSideEffectsDepthRef.current = Math.max(
        0,
        suppressNavSideEffectsDepthRef.current - 1,
      );
      suppressNavSideEffectsRef.current =
        suppressNavSideEffectsDepthRef.current > 0;
    }
  }, [
    featureFlags,
    navRestoreComplete,
    csamAccess.ready,
    selectedView,
    buildSanitizeOptions,
  ]);

  const resetHistoryCoalesce = useCallback(() => {
    if (historyCoalesceTimerRef.current != null) {
      clearTimeout(historyCoalesceTimerRef.current);
      historyCoalesceTimerRef.current = null;
    }
    historyCoalesceActiveRef.current = false;
  }, []);

  // CSM / employee tenant switch in the same browser tab: cases and work products
  // belong to the new tenant, so drop prior case/view/timeline/history nav state.
  useEffect(() => {
    const next =
      tenantId && tenantId.trim() !== '' && tenantId !== 'null'
        ? tenantId.trim()
        : null;
    const prev = prevTenantIdRef.current;

    if (prev && next && prev !== next) {
      resetAppNavSession(pathname || '/');
      appNavSessionIdRef.current = startAppNavSession();
      navApplyGenerationRef.current += 1;
      isApplyingHistoryNavRef.current = false;
      historyApplyLockRef.current = false;
      resetHistoryCoalesce();
      lastPersistedNavRef.current = {};
      lastHistoryCommitRef.current = {};
      forceReplaceNavUrlRef.current = true;
      pendingNavRestoreRef.current = null;

      skipForceChatOnCaseChangeRef.current = true;
      suppressNavSideEffectsDepthRef.current += 1;
      suppressNavSideEffectsRef.current = true;
      try {
        handleSetCaseId(undefined);
        setSelectedView('chat');
        sessionStorage.setItem('view', 'chat');
        setCurrentTimelineId(undefined);
        setSelectedSecondOption(undefined);
        setChatSessionId(null);
        setSeedChatSessionId(undefined);
        setAllSlides(undefined);
        setTimeline(undefined);
        setTitle(undefined);
        setDescription(undefined);
        setMedia(undefined);
        setThreatReportKey(undefined);
        setIsEventModalOpen(false);
        setIsTitleSlideModalOpen(false);
        setMultipleTimelinesAvailable([]);
        setNewTimelineIsAvailable(false);
        setLoadingTimeline(false);
        setLoadingWorkProducts(false);
        setLastWorkProductView(null);
        setLastWorkProductTimelineId(undefined);
        setLastWorkProductCaseId(undefined);
        setChatHistorySessions(null);
        setEntityExtractionJobs([]);
        setEntityExtractionJobsWithQuestions([]);
        setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
        setEntityServicePollAllJobsCount(0);
      } finally {
        window.setTimeout(() => {
          skipForceChatOnCaseChangeRef.current = false;
          suppressNavSideEffectsDepthRef.current = Math.max(
            0,
            suppressNavSideEffectsDepthRef.current - 1,
          );
          suppressNavSideEffectsRef.current =
            suppressNavSideEffectsDepthRef.current > 0;
        }, 0);
      }
    }

    if (next) {
      prevTenantIdRef.current = next;
    }
  }, [tenantId, pathname, resetHistoryCoalesce]);

  // Browser Back / Forward — restore the URL's nav state without mutating remotes.
  useEffect(() => {
    if (!navRestoreComplete || !csamCredentialsReady) return;

    const onPopState = (event: PopStateEvent) => {
      resetHistoryCoalesce();

      const sessionId =
        appNavSessionIdRef.current || getOrCreateAppNavSessionId();
      appNavSessionIdRef.current = sessionId;

      if (
        event.state != null &&
        !isCurrentAppNavHistoryEntry(event.state, sessionId)
      ) {
        // Entry from a prior login session — stay put by re-syncing current URL.
        const current = lastPersistedNavRef.current || buildCurrentAppNavState();
        persistAppNavState(pathname || '/', current, {
          mode: 'replace',
          sessionId,
        });
        return;
      }

      const fromUrl = parseAppNavFromSearchParams(
        new URLSearchParams(window.location.search),
      );

      void runNavApply(fromUrl, (applied) => {
        lastPersistedNavRef.current = applied;
        lastHistoryCommitRef.current = applied;
        forceReplaceNavUrlRef.current = true;
        writeAppNavToSessionStorage(applied);
      });
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [
    navRestoreComplete,
    csamCredentialsReady,
    pathname,
    runNavApply,
    buildCurrentAppNavState,
    resetHistoryCoalesce,
  ]);

  // Keep the address bar + sessionStorage in sync.
  // Significant nav pushes once, then rapid follow-up updates (timeline clear→reload,
  // etc.) replace that same entry so Back/Forward is one step per user action.
  useEffect(() => {
    if (!navRestoreComplete || historyApplyLockRef.current) return;
    if (isApplyingHistoryNavRef.current) return;

    const next = buildCurrentAppNavState();
    const prev = lastPersistedNavRef.current;
    if (prev && appNavStatesEqual(prev, next)) {
      writeAppNavToSessionStorage(next);
      // Still consume a pending force-replace so a missed persist wake cannot
      // leave forceReplaceNavUrlRef stuck true across later navigations.
      if (forceReplaceNavUrlRef.current) {
        forceReplaceNavUrlRef.current = false;
        const sessionId =
          appNavSessionIdRef.current || getOrCreateAppNavSessionId();
        appNavSessionIdRef.current = sessionId;
        lastHistoryCommitRef.current = next;
        resetHistoryCoalesce();
        persistAppNavState(pathname || '/', next, { mode: 'replace', sessionId });
      }
      return;
    }

    const sessionId =
      appNavSessionIdRef.current || getOrCreateAppNavSessionId();
    appNavSessionIdRef.current = sessionId;

    const forceReplace = forceReplaceNavUrlRef.current;
    forceReplaceNavUrlRef.current = false;

    const commitBaseline = lastHistoryCommitRef.current ?? prev;
    const significant =
      !!commitBaseline && isSignificantNavChange(commitBaseline, next);

    let mode: 'push' | 'replace' = 'replace';
    if (!forceReplace && significant) {
      if (historyCoalesceActiveRef.current) {
        // Still inside the quiet window for the entry we just pushed — update it.
        mode = 'replace';
      } else {
        mode = 'push';
        historyCoalesceActiveRef.current = true;
      }

      if (historyCoalesceTimerRef.current != null) {
        clearTimeout(historyCoalesceTimerRef.current);
      }
      historyCoalesceTimerRef.current = setTimeout(() => {
        historyCoalesceActiveRef.current = false;
        historyCoalesceTimerRef.current = null;
      }, APP_NAV_HISTORY_COALESCE_MS);

      lastHistoryCommitRef.current = next;
    } else if (forceReplace) {
      lastHistoryCommitRef.current = next;
      resetHistoryCoalesce();
    }

    persistAppNavState(pathname || '/', next, { mode, sessionId });
    lastPersistedNavRef.current = next;
  }, [
    navRestoreComplete,
    navPersistWake,
    pathname,
    buildCurrentAppNavState,
    resetHistoryCoalesce,
  ]);

  useEffect(() => {
    return () => {
      if (historyCoalesceTimerRef.current != null) {
        clearTimeout(historyCoalesceTimerRef.current);
      }
    };
  }, []);

  const handleViewChange = (view: View, options?: { fromOverlayBack?: boolean }) => {
    setIsEventModalOpen(false);
    setIsTitleSlideModalOpen(false);
    view = sanitizeRestoredView(view, buildSanitizeOptions());
    const previousView = selectedView;
    if (view !== previousView) {
      clearPresentationViewState();
    }
    if (
      !options?.fromOverlayBack &&
      isOverlayNavView(view) &&
      previousView !== view
    ) {
      rememberViewBeforeOverlay(previousView);
    }
    sessionStorage.setItem('view', view);
    setSelectedView(view);

    // Save work product context when switching from work product view to chat
    if (view === 'chat' && isWorkProductView(previousView)) {
      const context = {
        view: previousView,
        timelineId: currentTimelineId,
        caseId: caseId,
      };
      setLastWorkProductView(previousView);
      setLastWorkProductTimelineId(currentTimelineId);
      setLastWorkProductCaseId(caseId);
      sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    }

    // Save context when navigating TO a work product view (update current context)
    if (isWorkProductView(view)) {
      const context = {
        view: view,
        timelineId: currentTimelineId,
        caseId: caseId,
      };
      setLastWorkProductView(view);
      setLastWorkProductTimelineId(currentTimelineId);
      setLastWorkProductCaseId(caseId);
      sessionStorage.setItem('lastWorkProductContext', JSON.stringify(context));
    }

    if (view === 'chat') {
      setEntityServicePollAllJobsCount(0);
      setEntityServiceJob({ jobId: undefined, caseId: undefined, count: 0 });
    }

    // Restore may keep timelineId in nav state without loading slides (chat /
    // work-product-index). Fetch when the user opens a timeline view.
    if (
      isTimelineNavView(view) &&
      !isTimelineNavView(previousView) &&
      currentTimelineId &&
      caseId?.trim() &&
      !newTimelineIsAvailable
    ) {
      void fetchNewTimeline(currentTimelineId, caseId, false);
    }

    CheckBuildVersionViaApi();
  }

  // Selecting the current case is a no-op inside handleOptionChange, so a
  // same-case open (including "case already exists") still has to change view.
  const openCaseInView = (name: string, view: View) => {
    if (name === caseId) {
      handleViewChange(view);
      return;
    }
    handleOptionChange(name, view);
  };

  const handleBackFromOverlay = () => {
    handleViewChange(consumeViewBeforeOverlay(), { fromOverlayBack: true });
  };

  const updateNewAnswer = (answer: Answer) => {
    setNewAnswer(answer);
  }

  const handleLogin = async () => {
    deleteCookie('PdfPageCounts');
    if (isAuthInitialized && !!features && (features?.tokens as any)?.decodedIdToken && (features?.tokens as any)?.decodedAccessToken) {
      window.location.reload();
    } else {
      window.location.href = '/api/auth/sign-in';
    }
  };

  const handleLogout = async () => {
    // Keep token cookies for /logout-confirmation; that route performs Cognito cleanup.
    sessionStorage.removeItem('walkthrus');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    // Clear bookmark/history nav so a later login (other user/tenant) cannot
    // restore this session via refresh or Back/Forward.
    resetAppNavSession(pathname || '/');
    appNavSessionIdRef.current = null;
    lastPersistedNavRef.current = null;
    lastHistoryCommitRef.current = null;
    setSeedChatSessionId(undefined);
    resetHistoryCoalesce();
    deleteCookie('PdfPageCounts');
    bannerModalAcceptedRef.current = false;

    if (isAuthInitialized) {
      try {
        setAuthState(null);
        setAccessToken(null);
        setRealAccessToken(null);
        setIsCognitoUser(false);
        // replace so the dirty app URL is not left in the history stack.
        window.location.replace('/logout-confirmation');
      } catch (error) {
        console.error('Logout error:', error);
        sessionStorage.removeItem('isLoggingOut');
      }
    } else {
      console.error('Auth not initialized');
    }
  };

  // set up all walkthrus and allow setting of a specific walkthru
  // to enable walkthrus, set the availableWalkthrus object in the WalkthruContext below like { intro1, intro2 }
  const setWalkthru = async(name: string, walkthru: WalkthruState) => {
    const newWalkthrus: Record<string, WalkthruState> = { ...allWalkthrus };
    newWalkthrus[name] = walkthru;
    const walkthrusString: string = JSON.stringify(newWalkthrus);
    localStorage.setItem('walkthrus', walkthrusString.toString());
    setAllWalkthrus(newWalkthrus);
  }

  const cognitoIdFromFeatures = (features?.tokens as { decodedIdToken?: Record<string, unknown> })
    ?.decodedIdToken;
  const effectiveTenantId =
    resolveTenantId(
      cognitoIdFromFeatures?.['custom:tenant_id'] as string | undefined,
      cognitoIdFromFeatures?.['custom:tenant'] as string | undefined,
      (features as { tenantId?: string })?.tenantId,
      features?.orgId as string | undefined,
    ) ?? '';
  const presentationStateScope = useMemo<PresentationViewStateScope | undefined>(() => {
    if (!effectiveTenantId || !caseId || !currentTimelineId) return undefined;
    const appSessionId =
      appNavSessionIdRef.current || getOrCreateAppNavSessionId();
    appNavSessionIdRef.current = appSessionId;
    return {
      appSessionId,
      tenantId: effectiveTenantId,
      caseId,
      timelineId: currentTimelineId,
    };
  }, [effectiveTenantId, caseId, currentTimelineId]);
  const previousPresentationContextRef = useRef({
    selectedView,
    tenantId: effectiveTenantId,
    caseId,
  });

  useEffect(() => {
    const previous = previousPresentationContextRef.current;
    const contextChanged =
      previous.selectedView !== selectedView ||
      previous.tenantId !== effectiveTenantId ||
      previous.caseId !== caseId;

    if (contextChanged) {
      clearPresentationViewState();
    }
    previousPresentationContextRef.current = {
      selectedView,
      tenantId: effectiveTenantId,
      caseId,
    };
  }, [selectedView, effectiveTenantId, caseId]);
  const effectiveUsername =
    resolveCognitoUsername(
      cognitoIdFromFeatures?.['cognito:username'] as string | undefined,
      getUsername(cognitoIdFromFeatures),
      legacyCognitoLoginFromClaimsSub(
        cognitoIdFromFeatures?.sub as string | undefined,
      ),
      authState?.idToken?.claims?.['cognito:username'] as string | undefined,
      getUsername(authState?.idToken?.claims),
      legacyCognitoLoginFromClaimsSub(
        authState?.idToken?.claims?.sub as string | undefined,
      ),
    ) ??
    resolveCognitoUsername(
      cognitoIdFromFeatures?.email as string | undefined,
      authState?.idToken?.claims?.email as string | undefined,
    ) ??
    '';

  // Stable-identity wrappers for the handlers passed to the memoized `Chat`
  // (and other memoized children). Each always invokes the latest closure, so
  // there's no stale-state risk, but the prop identity never changes — which is
  // what lets `React.memo(Chat)` skip unrelated `Main` re-renders.
  const stableCreateEventFromChat = useStableCallback(createEventFromChat);
  const stableGenerateTimelineFromAnswer = useStableCallback(generateTimelineFromAnswer);
  const stableCreateEventsFromParsedEvents = useStableCallback(createEventsFromParsedEvents);
  const stableApplyAutogeneratedTimelineFromAnswer = useStableCallback(applyAutogeneratedTimelineFromAnswer);
  const stableUpdateNewAnswer = useStableCallback(updateNewAnswer);
  const stableSwapSessionId = useStableCallback(swapSessionId);
  const stableSetMainChatSessionId = useStableCallback(setMainChatSessionId);
  const stableHandleNavChatSessionApplied = useStableCallback(handleNavChatSessionApplied);
  const stableCreateEventsFromEntityEvents = useStableCallback(createEventsFromEntityEvents);
  const stableGetEventsFromEntityService = useStableCallback(getEventsFromEntityService);
  const stableRefreshChatHistorySessions = useStableCallback(refreshChatHistorySessions);
  const stableHandleCloseEventModal = useStableCallback(handleCloseEventModal);
  const stableHandleAddEvent = useStableCallback(handleAddEvent);
  const stableHandleEditEvent = useStableCallback(handleEditEvent);
  const stableOnUpdateTimeline = useStableCallback(onUpdateTimeline);
  const stableOpenModalFromTable = useStableCallback(openModalFromTable);
  const stableOpenModalFromPresentation = useStableCallback(openModalFromPresentation);
  const stableHandleDeleteEvent = useStableCallback(handleDeleteEvent);
  const stableHandleDeleteTimeline = useStableCallback(handleDeleteTimeline);
  const stableHandleViewChange = useStableCallback(handleViewChange);
  const stableHandleBackToWorkProducts = useStableCallback(handleBackToWorkProducts);
  const stableOpenTitleSlideModal = useStableCallback(openTitleSlideModal);
  const stableToggleTimelineVisibility = useStableCallback(toggleTimelineVisibility);
  const stableUpdateHorizontalTimelineSlide = useStableCallback((next: Slide) => {
    applySlideToLocalTimeline(next);
    void updateEvent(next, false);
  });

  const timelineViewFeatureFlags = useMemo(
    () => ({
      enableMediaAndSourceShare: featureFlags?.enableMediaAndSourceShare,
      exportTimelinePptx: featureFlags?.exportTimelinePptx,
      jsonMediaDisplay: isJsonMediaEnabled(featureFlags),
    }),
    [
      featureFlags?.enableMediaAndSourceShare,
      featureFlags?.exportTimelinePptx,
      featureFlags?.jsonMediaDisplay,
      featureFlags?.mediaPlayerTransition,
      featureFlags?.['media-player-transition'],
    ],
  );

  const timelineCurationEnabled = isTimelineCurationEnabled(featureFlags);

  // Signature of every slide's `show` flag. Table visibility toggles mutate
  // `slide.show` in place (see `handleVisibilityClick` in `tp-table.tsx`) without
  // replacing the `allSlides` reference, so a memo keyed only on `allSlides`
  // would not recompute and the presentation could keep showing hidden events
  // (or omit newly shown ones) until a full timeline refetch swapped the array.
  // Folding the visibility state into the dependency restores correctness while
  // still keeping the filtered array referentially stable across unrelated
  // renders (the point of memoizing it for the `React.memo`'d presentation).
  const slidesVisibilitySignature = allSlides
    ? allSlides.map((slide) => (slide.show ? '1' : '0')).join('')
    : '';

  const visibleSlidesForPresentation = useMemo(
    () => allSlides?.filter((slide) => slide.show) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSlides, slidesVisibilitySignature],
  );

  // Horizontal timeline shows only events that are both visible (`show`) and,
  // when curation is enabled, not individually hidden (`hideFromTimeline`).
  // With the flag off there is no control to restore a hidden event, so every
  // visible event is shown. The signature mirrors `slidesVisibilitySignature`
  // so in-place toggles recompute.
  const slidesTimelineVisibilitySignature = allSlides
    ? allSlides.map((slide) => (slide.hideFromTimeline ? '1' : '0')).join('')
    : '';

  const visibleSlidesForHorizontalTimeline = useMemo(
    () => (timelineCurationEnabled
      ? visibleSlidesForPresentation.filter((slide) => !slide.hideFromTimeline)
      : visibleSlidesForPresentation),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleSlidesForPresentation, slidesTimelineVisibilitySignature, timelineCurationEnabled],
  );

  // Stable array identity for the memoized EventModal. Recomputed when the
  // pending chat event changes or the modal toggles open (the only times the
  // modal reads it), so it never receives a brand-new array on unrelated renders.
  const eventsFromChatForModal = useMemo(() => {
    const evt = pendingChatEventRef.current ?? chatEventToAdd;
    return evt ? [evt] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatEventToAdd, isEventModalOpen]);

  const csamCredentialsValue = useMemo(
    () => ({ csamLimitedUser, csamVerifier }),
    [csamLimitedUser, csamVerifier],
  );

  return (
    <StatsContext.Provider value={statsContextValue}>
      <AuthContext.Provider value={authContextValue}>
      <TimePilotI18nProviders>
      <SyncLocale onLocale={setUiLocale} />
      <WalkthruContext.Provider value={{ walkthrus: allWalkthrus, setWalkthru: setWalkthru, availableWalkthrus: (featureFlags?.walkthrus || {}) as Record<string, WalkthruItem[]> }}>
      <div className="z-[30000] sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden sm:full-viewport-height">
          <MainSkipLinks />
          {pdfCaptureHost}
          <ToastProvider swipeDirection="right" duration={toast?.duration || 3000}>
          <ToastContext.Provider value={toastContextValue}>
            <FeaturesContext.Provider value={featuresContextValue}>
            <EmailArchivePreviewProvider>
            <CsamCredentialsContext.Provider value={csamCredentialsValue}>
              <FullScreenContext.Provider value={fullScreenContextValue}>
                {!isAuthInitialized || !navRestoreComplete || !flagsReady ? (
                  <TimePilotLoadingScreen />
                  ) : isAuthInitialized && authState?.isAuthenticated && authState?.accessToken?.accessToken && authState?.accessToken?.accessToken.trim() !== '' && authState?.idToken?.idToken && authState?.idToken?.idToken.trim() !== '' ? (
                  <SessionHistoryProvider>
                  <TimelineEventsProvider>
                  <OverlayVariantProvider variant="inverted">
                  {featureFlags?.enableMediaAndSourceShare ? (
                  <ToolsDrawerProvider caseId={caseId} onAddToTimeline={createEventFromChat}>
                    <ToolsDrawerChrome>
                    <div className="box-border overflow-x-hidden relative sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden full-viewport-height">
                    {isEventModalOpen && (
                      <DismissibleBlurOverlay
                        onClose={handleCloseEventModal}
                        closeLabel="Close event editor"
                        showCloseButton={false}
                      >
                        <EventModal
                          isOpen={isEventModalOpen}
                          onClose={stableHandleCloseEventModal}
                          onAddEvent={stableHandleAddEvent}
                          onEditEvent={stableHandleEditEvent}
                          events={allSlides}
                          eventIndex={eventIndex}
                          column={currentColumn}
                          editingEnabled={isEditableTimeline}
                          sourcesFromChat={allSourcesFromChat?.allSources}
                          mediaSourcesFromChat={allSourcesFromChat?.mediaSources}
                          eventsFromChat={eventsFromChatForModal}
                          caseId={caseId}
                          isLegacy={false}
                        />
                      </DismissibleBlurOverlay>
                    )}
                    {isTitleSlideModalOpen && !!timelinePatch && (
                      <DismissibleBlurOverlay
                        onClose={closeTitleSlideModal}
                        closeLabel="Close title slide editor"
                        showCloseButton={false}
                      >
                        <TitleSlideModal
                          isOpen={isTitleSlideModalOpen}
                          tags={timelineTags}
                          onClose={closeTitleSlideModal}
                          onPatchTimeline={handlePatchTimeline}
                          timelinePatch={timelinePatch}
                          editingEnabled={isEditableTimeline}
                          mediaSourcesFromChat={allSourcesFromChat?.mediaSources}
                          caseId={caseId}
                        />
                      </DismissibleBlurOverlay>
                    )}
                    <NavigationBarHorizontal
                      currentTimelineId={currentTimelineId}
                      selectedOption={caseId || null}
                      onOptionChange={handleOptionChange}
                      selectedView={selectedView}
                      onViewChange={handleViewChange}
                      userId={authState?.idToken?.claims.email ?? ''}
                      newTimelineIsAvailable={newTimelineIsAvailable}
                      multipleTimelinesAvailable={multipleTimelinesAvailable}
                      loadingTimeline={loadingTimeline}
                      selectedSecondOption={selectedSecondOption || null}
                      onSecondOptionChange={handleSecondOptionChange}
                      clientWidth={width}
                      onFeedbackClick={onFeedbackClick}
                      onCreateTimeline={onCreateTimeline}
                      timelineCreated={timelineCreated}
                      creatingTimeline={creatingTimeline}
                      timelineServiceAppearsOperational={timelineServiceAppearsOperational}
                      setTenantName={setTenantName}
                      setRemoteStorageLink={setRemoteStorageLink}
                      setOrgName={setOrgName}
                      onWorkProductsClick={handleWorkProductsClick}
                      setBanners={setBanners}
                      tenantId={effectiveTenantId}
                      username={effectiveUsername}
                      adHocFileUpload={
                        showAdHocCaseFeatures ? adHocCaseFileUpload : undefined
                      }
                      className={isEventModalOpen ? 'blur-sm' : ''}
                      handleLogout={handleLogout}
                      isUserAdmin={isUserAdmin}
                      csamAccessReady={csamAccess.ready}
                    />
                    {selectedView === 'work-product-index' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        caseId={caseId}
                        isIndexView={true}
                        onCreateTimeline={onCreateTimeline}
                        onCreateThreatReport={onCreateThreatReport}
                        viewMode={workProductViewMode}
                        onViewModeChange={handleWorkProductViewModeChange}
                      />
                    )}
                    {(selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal') && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onBackToWorkProducts={handleBackToWorkProducts}
                        onViewChange={handleViewChange}
                        selectedView={selectedView}
                        caseId={caseId}
                        workProductType="timeline"
                        timelineTitle={title}
                        isEditableTimeline={isEditableTimeline}
                        onEditTimelineTitle={openTitleSlideModal}
                      />
                    )}
                    {selectedView === 'threat-report' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onBackToWorkProducts={handleBackToWorkProducts}
                        caseId={caseId}
                        workProductType="threat-report"
                        isThreatReportReady={isThreatReportReady}
                        threatReportKey={threatReportKey}
                        threatReportSourceModifiedAt={threatReportSourceModifiedAt}
                        onSelectThreatReportVersion={pickThreatReportVersion}
                      />
                    )}
                    {selectedView === 'chat' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onViewChange={handleViewChange}
                        selectedView={selectedView}
                        caseId={caseId}
                      />
                    )}
                    {selectedView === 'chat' && !isFullscreen && showAdHocCaseFeatures && (
                      <AdHocFileDropLayer
                        isDragActive={adHocCaseFileUpload.isDragActive}
                        isUploading={adHocCaseFileUpload.isUploading}
                      />
                    )}
                    {!isFullscreen && selectedView !== 'presentation' && selectedView !== 'courtroom' && selectedView !== 'horizontal' && (
                      <StatusBar
                        userId={authState?.idToken?.claims.email ?? ''}
                        status={timeSaved ? `Time saved: ${timeSaved} minutes` : 'Time saved: 0 minutes'}
                        timeSaved={timeSaved || 0}
                        loadingTimeSaved={loadingTimeSaved}
                        caseId={caseId || undefined}
                        remoteStorageLink={remoteStorageLinkLocal}
                        onOpenCaseFileManagement={
                          caseFileManagementEnabled
                            ? () => handleViewChange('case-file-management')
                            : undefined
                        }
                        isCaseFileManagementView={selectedView === 'case-file-management'}
                        showEmployeeSwitcher={showCaseManagementNav}
                      />
                    )}
                    {isFullscreen && fullscreenMediaItem && (
                      <FullscreenTransformProvider>
                        <MediaDisplay
                          mediaItem={fullscreenMediaItem}
                          isAppFullscreenViewer
                          isPresentation={selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal' || selectedView === 'chat'}
                          confirmCaptureRegion={maximizedViewerConfirmsCapture({
                            selectedView,
                            isEventModalOpen,
                          })}
                          isEventModal={isEventModalOpen}
                          contentModerationType={fullscreenObfuscation?.contentModerationType}
                          showObfuscatedPreview={fullscreenObfuscation?.showObfuscatedPreview}
                          onToggleObfuscatedPreview={fullscreenObfuscation?.onToggleObfuscatedPreview}
                        />
                      </FullscreenTransformProvider>
                    )}
                    {linkedMediaItem && (
                      <MediaDisplay
                        mediaItem={linkedMediaItem}
                        isLinkedMedia={true}
                      />
                    )}
                    <main
                      id="main-content"
                      role="main"
                      // Programmatically focusable so "Skip to main content" moves
                      // keyboard focus, not just the hash (WCAG 2.1 SC 2.4.1).
                      tabIndex={-1}
                      className={
                        `ag-theme-quartz bg-gray-900 fixed w-screen pb-[400px] sm:pb-0 ${selectedView === 'chat' ?
                          (`${isFullscreen ? 'opacity-[0.01] overflow-y-hidden' :
                            caseId ?
                            'top-[150px] sm:top-[147px] h-[calc(100vh-150px)] sm:h-[calc(100vh-186px)]' :
                            'top-[56px] sm:top-[67px] h-[calc(100vh-60px)] sm:h-[calc(100vh-74px)]'}`) :
                          (selectedView === 'work-product-index' || selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal' || selectedView === 'threat-report') || selectedView === 'account' || (csamAccess.canOpenDashboard && selectedView === 'csam-report' && !isAdHocCase(caseId, personalFilesCaseId)) ?
                          `tp-secondary-nav-offset ${selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal' ? 'tp-secondary-nav-offset--full' : ''}` :
                          'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]'}`
                      }>
                      {selectedView === 'table' && (
                        <div className={`ag-theme-quartz w-screen sm:p-[30px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <>
                            {loadingTimeline && (
                              <div
                                className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'
                                aria-live="polite"
                                aria-busy="true"
                                role="status"
                              >
                                <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                                <SpinnerGapIcon size={42} className="animate-spin-only" aria-hidden="true" />
                              </div>
                            )}
                            {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'table' && (
                              <TPTable
                                title={title}
                                caseId={caseId || ''}
                                timelineId={currentTimelineId || ''}
                                description={description}
                                media={media}
                                allSlides={allSlides}
                                updateTimeline={stableOnUpdateTimeline}
                                onEditEvent={stableHandleEditEvent}
                                onOpenModal={stableOpenModalFromTable}
                                onSetEventIndex={setEventIndex}
                                onDeleteEvent={stableHandleDeleteEvent}
                                onAddEvent={stableHandleAddEvent}
                                isEditableTimeline={isEditableTimeline}
                                onDeleteTimeline={stableHandleDeleteTimeline}
                                sortByDateTime={sortByDateTime}
                                enrichEvents={stableCreateEventsFromParsedEvents}
                                createEventsFromParsedEvents={stableCreateEventsFromParsedEvents}
                                entityExtractionJobs={entityExtractionJobsWithQuestions}
                                getEventsFromEntityService={stableGetEventsFromEntityService}
                                createEventsFromEntityEvents={stableCreateEventsFromEntityEvents}
                                featureFlags={timelineViewFeatureFlags}
                                onViewChange={stableHandleViewChange}
                                onBackToWorkProducts={stableHandleBackToWorkProducts}
                                selectedView={selectedView}
                                onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              />
                            )}
                            {!currentTimelineId && (
                              <div className="flex flex-col items-center justify-center h-screen">
                                <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                                <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                              </div>
                            )}
                          </>
                        </div>
                        )}
                      {selectedView === 'presentation' && (
                        <div className={`ag-theme-quartz w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'presentation' && (
                            <TPPresentation
                              key={`${presentationStateScope?.appSessionId ?? 'session'}:${presentationStateScope?.tenantId ?? 'tenant'}:${presentationStateScope?.caseId ?? 'case'}:${presentationStateScope?.timelineId ?? 'timeline'}`}
                              title={title}
                              description={description}
                              media={media}
                              allSlides={visibleSlidesForPresentation}
                              onOpenModal={stableOpenModalFromPresentation}
                              onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              onToggleTimelineVisibility={
                                timelineCurationEnabled
                                  ? stableToggleTimelineVisibility
                                  : undefined
                              }
                              isEventModalOpen={isEventModalOpen}
                              featureFlags={timelineViewFeatureFlags}
                              stateScope={presentationStateScope}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                              <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-lg sm:text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'courtroom' && featureFlags?.courtroomMode && (
                        <div className={`ag-theme-quartz h-full w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'courtroom' && (
                            <TPCourtroom
                              title={title}
                              description={description}
                              courtroomTitle={courtroomTitle}
                              courtroomDescription={courtroomDescription}
                              media={media}
                              allSlides={allSlides?.filter(slide => slide.show) || []}
                              onExitFullscreen={() => stableHandleViewChange('presentation')}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'horizontal' && featureFlags?.horizontalTimeline && (
                        <div className={`ag-theme-quartz h-full w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'horizontal' && (
                            <TPHorizontalTimeline
                              title={title}
                              description={description}
                              media={media}
                              allSlides={visibleSlidesForHorizontalTimeline}
                              onOpenModal={stableOpenModalFromPresentation}
                              onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              onUpdateSlide={stableUpdateHorizontalTimelineSlide}
                              isEventModalOpen={isEventModalOpen}
                              featureFlags={timelineViewFeatureFlags}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                              <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-lg sm:text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'work-product-index' && (
                        <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                          <WorkProductIndex
                            caseId={caseId}
                            timelines={multipleTimelinesAvailable}
                            onCreateTimeline={onCreateTimeline}
                            onSelectTimeline={handleSelectTimelineFromWorkProducts}
                            onSelectThreatReport={handleSelectThreatReport}
                            featureFlags={featureFlags}
                            isLoading={loadingWorkProducts}
                            viewMode={workProductViewMode}
                            isThreatReportProcessing={isThreatReportProcessing}
                            processingThreatReportId={newThreatReportId}
                            clearThreatReportKey={() => setThreatReportKey(undefined)}
                            sourceModifiedAt={threatReportSourceModifiedAt}
                          />
                        </div>
                      )}
                      {selectedView === 'threat-report' && (
                        <div className="threat-report w-full h-full">
                          <ThreatReport
                            threatReportKey={threatReportKey}
                            userId={authState?.idToken?.claims.email ?? ''}
                            tenant={tenantId ?? null}
                            caseId={caseId}
                            setIsThreatReportProcessing={setIsThreatReportProcessing}
                            onThreatReportReadyChange={setIsThreatReportReady}
                            clearThreatReportKey={() => setThreatReportKey(undefined)}
                            onSelectVersion={setThreatReportKey}
                            pickedVersion={pickedThreatReportVersion}
                            onGenerateThreatReport={onCreateThreatReport}
                            sourceModifiedAt={threatReportSourceModifiedAt}
                          />
                        </div>
                      )}
                      {selectedView === 'csam-report' && csamAccess.canOpenDashboard && !isAdHocCase(caseId, personalFilesCaseId) && (
                        <div className="w-full h-full overflow-y-auto">
                          <CsamReport
                            caseId={caseId ?? undefined}
                            reviewer={csamReportCreator}
                          />
                        </div>
                      )}
                      <div className={`${selectedView === 'chat' && !isFullscreen ? '' : 'h-[1px] w-[1px] max-h-[1px] max-w-[1px] opacity-0 z-0 overflow-hidden'} ag-theme-quartz ${isEventModalOpen ? 'opacity-50' : ''}`}>
                        <div
                          className={`relative overflow-y-hidden ${(selectedView !== 'chat' || isFullscreen) ? 'opacity-[0.01] top-0 left-0 h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-hidden' : ''}`}
                        >
                          <Chat
                            assistantViewVisible={selectedView === 'chat' && !isFullscreen}
                            assistantViewSelected={selectedView === 'chat'}
                            featureFlags={featureFlags}
                            selectedOption={caseId ?? ''}
                            userName={authState?.idToken?.claims.name ?? ''}
                            userId={authState?.idToken?.claims.email ?? ''}
                            addToTimeline={stableCreateEventFromChat}
                            generateTimelineFromAnswer={stableGenerateTimelineFromAnswer}
                            timelineGenerationStatuses={answerTimelineGenerationStatuses}
                            addMultipleToTimeline={stableCreateEventsFromParsedEvents}
                            applyAutogeneratedTimelineFromAnswer={stableApplyAutogeneratedTimelineFromAnswer}
                            isAutogeneratedTimelineAppliedForAnswer={isAutogeneratedTimelineAppliedForAnswer}
                            isAutogeneratedTimelineApplyingForAnswer={isAutogeneratedTimelineApplyingForAnswer}
                            isEditableTimeline={isEditableTimeline}
                            updateNewAnswer={stableUpdateNewAnswer}
                            setSourcesFromChat={setSourcesFromChat}
                            swapSessionId={stableSwapSessionId}
                            setMainChatSessionId={stableSetMainChatSessionId}
                            initialChatSessionId={seedChatSessionId}
                            onNavChatSessionApplied={stableHandleNavChatSessionApplied}
                            entityExtractionJobs={entityExtractionJobs}
                            chatHistorySessions={chatHistorySessions}
                            refreshChatHistorySessions={stableRefreshChatHistorySessions}
                            createEventsFromEntityEvents={stableCreateEventsFromEntityEvents}
                            getEventsFromEntityService={stableGetEventsFromEntityService}
                          />
                        </div>
                      </div>
                      {selectedView === 'account' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Account
                            userId={authState?.idToken?.claims.email ?? ''}
                            isUserAdmin={isUserAdmin}
                            isPermissionServiceAvailable={isPermissionServiceAvailable}
                            onOpenCase={(name, destination) =>
                              openCaseInView(
                                name,
                                destination === 'case-file-management' && caseFileManagementEnabled
                                  ? 'case-file-management'
                                  : 'chat',
                              )
                            }
                          />
                        </div>
                      )}
                      {selectedView === 'settings' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <AdminPanel onBackToDashboard={() => handleViewChange('account', { fromOverlayBack: true })} />
                        </div>
                      )}
                      {caseFileManagementEnabled && selectedView === 'case-file-management' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <CaseFileManagement
                            caseId={caseId}
                            uploadApi={caseFileManagementUpload}
                            onBack={handleBackFromOverlay}
                            onCaseCreated={(created) =>
                              handleOptionChange(created.name, 'case-file-management')
                            }
                          />
                        </div>
                      )}
                      {featureFlags?.sandbox && selectedView === 'sandbox' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Sandbox onClose={() => {setSelectedView('chat')}} />
                        </div>
                      )}
                    </main>
                  </div>
                    </ToolsDrawerChrome>
                  </ToolsDrawerProvider>
                  ) : (
                  <div className="box-border overflow-x-hidden relative sm:h-screen sm:overflow-x-hidden sm:overflow-y-hidden full-viewport-height">
                    {isEventModalOpen && (
                      <DismissibleBlurOverlay
                        onClose={handleCloseEventModal}
                        closeLabel="Close event editor"
                        showCloseButton={false}
                      >
                        <EventModal
                          isOpen={isEventModalOpen}
                          onClose={stableHandleCloseEventModal}
                          onAddEvent={stableHandleAddEvent}
                          onEditEvent={stableHandleEditEvent}
                          events={allSlides}
                          eventIndex={eventIndex}
                          column={currentColumn}
                          editingEnabled={isEditableTimeline}
                          sourcesFromChat={allSourcesFromChat?.allSources}
                          mediaSourcesFromChat={allSourcesFromChat?.mediaSources}
                          eventsFromChat={eventsFromChatForModal}
                          caseId={caseId}
                          isLegacy={false}
                        />
                      </DismissibleBlurOverlay>
                    )}
                    {isTitleSlideModalOpen && !!timelinePatch && (
                      <DismissibleBlurOverlay
                        onClose={closeTitleSlideModal}
                        closeLabel="Close title slide editor"
                        showCloseButton={false}
                      >
                        <TitleSlideModal
                          isOpen={isTitleSlideModalOpen}
                          tags={timelineTags}
                          onClose={closeTitleSlideModal}
                          onPatchTimeline={handlePatchTimeline}
                          timelinePatch={timelinePatch}
                          editingEnabled={isEditableTimeline}
                          mediaSourcesFromChat={allSourcesFromChat?.mediaSources}
                          caseId={caseId}
                        />
                      </DismissibleBlurOverlay>
                    )}
                    <NavigationBarHorizontal
                      currentTimelineId={currentTimelineId}
                      selectedOption={caseId || null}
                      onOptionChange={handleOptionChange}
                      selectedView={selectedView}
                      onViewChange={handleViewChange}
                      userId={authState?.idToken?.claims.email ?? ''}
                      newTimelineIsAvailable={newTimelineIsAvailable}
                      multipleTimelinesAvailable={multipleTimelinesAvailable}
                      loadingTimeline={loadingTimeline}
                      selectedSecondOption={selectedSecondOption || null}
                      onSecondOptionChange={handleSecondOptionChange}
                      clientWidth={width}
                      onFeedbackClick={onFeedbackClick}
                      onCreateTimeline={onCreateTimeline}
                      timelineCreated={timelineCreated}
                      creatingTimeline={creatingTimeline}
                      timelineServiceAppearsOperational={timelineServiceAppearsOperational}
                      setTenantName={setTenantName}
                      setRemoteStorageLink={setRemoteStorageLink}
                      setOrgName={setOrgName}
                      onWorkProductsClick={handleWorkProductsClick}
                      setBanners={setBanners}
                      tenantId={effectiveTenantId}
                      username={effectiveUsername}
                      adHocFileUpload={
                        showAdHocCaseFeatures ? adHocCaseFileUpload : undefined
                      }
                      className={isEventModalOpen ? 'blur-sm' : ''}
                      handleLogout={handleLogout}
                      isUserAdmin={isUserAdmin}
                      csamAccessReady={csamAccess.ready}
                    />
                    {selectedView === 'work-product-index' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        caseId={caseId}
                        isIndexView={true}
                        onCreateTimeline={onCreateTimeline}
                        onCreateThreatReport={onCreateThreatReport}
                        viewMode={workProductViewMode}
                        onViewModeChange={handleWorkProductViewModeChange}
                      />
                    )}
                    {(selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal') && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onBackToWorkProducts={handleBackToWorkProducts}
                        onViewChange={handleViewChange}
                        selectedView={selectedView}
                        caseId={caseId}
                        workProductType="timeline"
                        timelineTitle={title}
                        isEditableTimeline={isEditableTimeline}
                        onEditTimelineTitle={openTitleSlideModal}
                      />
                    )}
                    {selectedView === 'threat-report' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onBackToWorkProducts={handleBackToWorkProducts}
                        caseId={caseId}
                        workProductType="threat-report"
                        isThreatReportReady={isThreatReportReady}
                        threatReportKey={threatReportKey}
                        threatReportSourceModifiedAt={threatReportSourceModifiedAt}
                        onSelectThreatReportVersion={pickThreatReportVersion}
                      />
                    )}
                    {selectedView === 'chat' && !isFullscreen && (
                      <WorkProductNavBar
                        featureFlags={featureFlags}
                        onViewChange={handleViewChange}
                        selectedView={selectedView}
                        caseId={caseId}
                      />
                    )}
                    {selectedView === 'chat' && !isFullscreen && showAdHocCaseFeatures && (
                      <AdHocFileDropLayer
                        isDragActive={adHocCaseFileUpload.isDragActive}
                        isUploading={adHocCaseFileUpload.isUploading}
                      />
                    )}
                    {!isFullscreen && selectedView !== 'presentation' && selectedView !== 'courtroom' && selectedView !== 'horizontal' && (
                      <StatusBar
                        userId={authState?.idToken?.claims.email ?? ''}
                        status={timeSaved ? `Time saved: ${timeSaved} minutes` : 'Time saved: 0 minutes'}
                        timeSaved={timeSaved || 0}
                        loadingTimeSaved={loadingTimeSaved}
                        caseId={caseId || undefined}
                        remoteStorageLink={remoteStorageLinkLocal}
                        onOpenCaseFileManagement={
                          caseFileManagementEnabled
                            ? () => handleViewChange('case-file-management')
                            : undefined
                        }
                        isCaseFileManagementView={selectedView === 'case-file-management'}
                        showEmployeeSwitcher={showCaseManagementNav}
                      />
                    )}
                    {isFullscreen && fullscreenMediaItem && (
                      <FullscreenTransformProvider>
                        <MediaDisplay
                          mediaItem={fullscreenMediaItem}
                          isAppFullscreenViewer
                          isPresentation={selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal' || selectedView === 'chat'}
                          confirmCaptureRegion={maximizedViewerConfirmsCapture({
                            selectedView,
                            isEventModalOpen,
                          })}
                          isEventModal={isEventModalOpen}
                          contentModerationType={fullscreenObfuscation?.contentModerationType}
                          showObfuscatedPreview={fullscreenObfuscation?.showObfuscatedPreview}
                          onToggleObfuscatedPreview={fullscreenObfuscation?.onToggleObfuscatedPreview}
                        />
                      </FullscreenTransformProvider>
                    )}
                    {linkedMediaItem && (
                      <MediaDisplay
                        mediaItem={linkedMediaItem}
                        isLinkedMedia={true}
                      />
                    )}
                    <main
                      id="main-content"
                      role="main"
                      // Programmatically focusable so "Skip to main content" moves
                      // keyboard focus, not just the hash (WCAG 2.1 SC 2.4.1).
                      tabIndex={-1}
                      className={
                        `ag-theme-quartz bg-gray-900 fixed w-screen pb-[400px] sm:pb-0 ${selectedView === 'chat' ?
                          (`${isFullscreen ? 'opacity-[0.01] overflow-y-hidden' : 'sm:top-[111px] top-[56px] h-[calc(100vh-60px)] sm:h-[calc(100vh-114px)]'}`) :
                          (selectedView === 'work-product-index' || selectedView === 'table' || selectedView === 'presentation' || selectedView === 'courtroom' || selectedView === 'horizontal' || selectedView === 'threat-report') || selectedView === 'account' || (csamAccess.canOpenDashboard && selectedView === 'csam-report' && !isAdHocCase(caseId, personalFilesCaseId)) ?
                          'tp-secondary-nav-offset' :
                          'top-[55px] sm:top-[67px] h-[calc(100vh-50px)] sm:h-[calc(100vh-74px)]'}`
                      }>
                      {selectedView === 'table' && (
                        <div className={`ag-theme-quartz w-screen sm:p-[30px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <>
                            {loadingTimeline && (
                              <div
                                className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'
                                aria-live="polite"
                                aria-busy="true"
                                role="status"
                              >
                                <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                                <SpinnerGapIcon size={42} className="animate-spin-only" aria-hidden="true" />
                              </div>
                            )}
                            {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'table' && (
                              <TPTable
                                title={title}
                                caseId={caseId || ''}
                                timelineId={currentTimelineId || ''}
                                description={description}
                                media={media}
                                allSlides={allSlides}
                                updateTimeline={stableOnUpdateTimeline}
                                onEditEvent={stableHandleEditEvent}
                                onOpenModal={stableOpenModalFromTable}
                                onSetEventIndex={setEventIndex}
                                onDeleteEvent={stableHandleDeleteEvent}
                                onAddEvent={stableHandleAddEvent}
                                isEditableTimeline={isEditableTimeline}
                                onDeleteTimeline={stableHandleDeleteTimeline}
                                sortByDateTime={sortByDateTime}
                                enrichEvents={stableCreateEventsFromParsedEvents}
                                createEventsFromParsedEvents={stableCreateEventsFromParsedEvents}
                                entityExtractionJobs={entityExtractionJobsWithQuestions}
                                getEventsFromEntityService={stableGetEventsFromEntityService}
                                createEventsFromEntityEvents={stableCreateEventsFromEntityEvents}
                                featureFlags={timelineViewFeatureFlags}
                                onViewChange={stableHandleViewChange}
                                onBackToWorkProducts={stableHandleBackToWorkProducts}
                                selectedView={selectedView}
                                onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              />
                            )}
                            {!currentTimelineId && (
                              <div className="flex flex-col items-center justify-center h-screen">
                                <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                                <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                              </div>
                            )}
                          </>
                        </div>
                      )}
                      {selectedView === 'presentation' && (
                        <div className={`ag-theme-quartz w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'presentation' && (
                            <TPPresentation
                              key={`${presentationStateScope?.appSessionId ?? 'session'}:${presentationStateScope?.tenantId ?? 'tenant'}:${presentationStateScope?.caseId ?? 'case'}:${presentationStateScope?.timelineId ?? 'timeline'}`}
                              title={title}
                              description={description}
                              media={media}
                              allSlides={visibleSlidesForPresentation}
                              onOpenModal={stableOpenModalFromPresentation}
                              onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              onToggleTimelineVisibility={
                                timelineCurationEnabled
                                  ? stableToggleTimelineVisibility
                                  : undefined
                              }
                              isEventModalOpen={isEventModalOpen}
                              featureFlags={timelineViewFeatureFlags}
                              stateScope={presentationStateScope}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                              <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-lg sm:text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'courtroom' && featureFlags?.courtroomMode && (
                        <div className={`ag-theme-quartz h-full w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'courtroom' && (
                            <TPCourtroom
                              title={title}
                              description={description}
                              courtroomTitle={courtroomTitle}
                              courtroomDescription={courtroomDescription}
                              media={media}
                              allSlides={allSlides?.filter(slide => slide.show) || []}
                              onExitFullscreen={() => stableHandleViewChange('presentation')}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'horizontal' && featureFlags?.horizontalTimeline && (
                        <div className={`ag-theme-quartz h-full w-screen ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          {loadingTimeline && (
                            <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
                              <div>{t('main.loadingTimeline')}<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
                              <SpinnerGapIcon size={42} className="animate-spin-only" />
                            </div>
                          )}
                          {!!currentTimelineId && !!allSlides && !loadingTimeline && selectedView === 'horizontal' && (
                            <TPHorizontalTimeline
                              title={title}
                              description={description}
                              media={media}
                              allSlides={visibleSlidesForHorizontalTimeline}
                              onOpenModal={stableOpenModalFromPresentation}
                              onOpenTitleSlideModal={stableOpenTitleSlideModal}
                              onUpdateSlide={stableUpdateHorizontalTimelineSlide}
                              isEventModalOpen={isEventModalOpen}
                              featureFlags={timelineViewFeatureFlags}
                            />
                          )}
                          {!currentTimelineId && (
                            <div className="flex flex-col items-center justify-center h-screen">
                              <div className="text-2xl sm:text-4xl font-bold mb-8">{t('timeline.select.pleaseSelect')}</div>
                              <Button {...glowCtaButtonProps} onClick={() => setIsModalOpen(true)} className={`text-lg sm:text-2xl py-6 px-8 ${GLOW_CTA_BUTTON_CLASS}`}>{t('main.timelinesTutorial')}</Button>
                            </div>
                          )}
                        </div>
                      )}
                      {selectedView === 'work-product-index' && (
                        <div className="w-full h-full overflow-y-auto overflow-x-hidden">
                          <WorkProductIndex
                            caseId={caseId}
                            timelines={multipleTimelinesAvailable}
                            onCreateTimeline={onCreateTimeline}
                            onSelectTimeline={handleSelectTimelineFromWorkProducts}
                            onSelectThreatReport={handleSelectThreatReport}
                            featureFlags={featureFlags}
                            isLoading={loadingWorkProducts}
                            viewMode={workProductViewMode}
                            isThreatReportProcessing={isThreatReportProcessing}
                            processingThreatReportId={newThreatReportId}
                            clearThreatReportKey={() => setThreatReportKey(undefined)}
                            sourceModifiedAt={threatReportSourceModifiedAt}
                          />
                        </div>
                      )}
                      {selectedView === 'threat-report' && (
                        <div className="threat-report w-full h-full">
                          <ThreatReport
                            threatReportKey={threatReportKey}
                            userId={authState?.idToken?.claims.email ?? ''}
                            tenant={tenantId ?? null}
                            caseId={caseId}
                            setIsThreatReportProcessing={setIsThreatReportProcessing}
                            onThreatReportReadyChange={setIsThreatReportReady}
                            clearThreatReportKey={() => setThreatReportKey(undefined)}
                            onSelectVersion={setThreatReportKey}
                            pickedVersion={pickedThreatReportVersion}
                            onGenerateThreatReport={onCreateThreatReport}
                            sourceModifiedAt={threatReportSourceModifiedAt}
                          />
                        </div>
                      )}
                      {selectedView === 'csam-report' && csamAccess.canOpenDashboard && !isAdHocCase(caseId, personalFilesCaseId) && (
                        <div className="w-full h-full overflow-y-auto">
                          <CsamReport
                            caseId={caseId ?? undefined}
                            reviewer={csamReportCreator}
                          />
                        </div>
                      )}
                      <div className={`${selectedView === 'chat' && !isFullscreen ? '' : 'h-[1px] w-[1px] max-h-[1px] max-w-[1px] opacity-0 z-0 overflow-hidden'} ag-theme-quartz ${isEventModalOpen ? 'opacity-50' : ''}`}>
                        <div
                          className={`relative overflow-y-hidden ${(selectedView !== 'chat' || isFullscreen) ? 'opacity-[0.01] top-0 left-0 h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-hidden' : ''}`}
                        >
                          <Chat
                            assistantViewVisible={selectedView === 'chat' && !isFullscreen}
                            assistantViewSelected={selectedView === 'chat'}
                            selectedOption={caseId ?? ''}
                            userName={authState?.idToken?.claims.name ?? ''}
                            userId={authState?.idToken?.claims.email ?? ''}
                            addToTimeline={stableCreateEventFromChat}
                            generateTimelineFromAnswer={stableGenerateTimelineFromAnswer}
                            timelineGenerationStatuses={answerTimelineGenerationStatuses}
                            addMultipleToTimeline={stableCreateEventsFromParsedEvents}
                            applyAutogeneratedTimelineFromAnswer={stableApplyAutogeneratedTimelineFromAnswer}
                            isAutogeneratedTimelineAppliedForAnswer={isAutogeneratedTimelineAppliedForAnswer}
                            isAutogeneratedTimelineApplyingForAnswer={isAutogeneratedTimelineApplyingForAnswer}
                            isEditableTimeline={isEditableTimeline}
                            featureFlags={featureFlags}
                            updateNewAnswer={stableUpdateNewAnswer}
                            setSourcesFromChat={setSourcesFromChat}
                            swapSessionId={stableSwapSessionId}
                            setMainChatSessionId={stableSetMainChatSessionId}
                            initialChatSessionId={seedChatSessionId}
                            onNavChatSessionApplied={stableHandleNavChatSessionApplied}
                            entityExtractionJobs={entityExtractionJobs}
                            chatHistorySessions={chatHistorySessions}
                            refreshChatHistorySessions={stableRefreshChatHistorySessions}
                            createEventsFromEntityEvents={stableCreateEventsFromEntityEvents}
                            getEventsFromEntityService={stableGetEventsFromEntityService}
                          />
                        </div>
                      </div>
                      {selectedView === 'account' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Account
                            userId={authState?.idToken?.claims.email ?? ''}
                            isUserAdmin={isUserAdmin}
                            isPermissionServiceAvailable={isPermissionServiceAvailable}
                            onOpenCase={(name, destination) =>
                              openCaseInView(
                                name,
                                destination === 'case-file-management' && caseFileManagementEnabled
                                  ? 'case-file-management'
                                  : 'chat',
                              )
                            }
                          />
                        </div>
                      )}
                      {selectedView === 'settings' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <AdminPanel onBackToDashboard={() => handleViewChange('account', { fromOverlayBack: true })} />
                        </div>
                      )}
                      {caseFileManagementEnabled && selectedView === 'case-file-management' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <CaseFileManagement
                            caseId={caseId}
                            uploadApi={caseFileManagementUpload}
                            onBack={handleBackFromOverlay}
                            onCaseCreated={(created) =>
                              handleOptionChange(created.name, 'case-file-management')
                            }
                          />
                        </div>
                      )}
                      {featureFlags?.sandbox && selectedView === 'sandbox' && (
                        <div className={`ag-theme-quartz w-screen h-full min-h-0 overflow-y-auto p-[20px] ${isEventModalOpen ? 'opacity-50' : ''}`}>
                          <Sandbox onClose={() => {setSelectedView('chat')}} />
                        </div>
                      )}
                    </main>
                  </div>
                  )}
                  </OverlayVariantProvider>
                  </TimelineEventsProvider>
                  </SessionHistoryProvider>
                ) : (
                  <main className="flex flex-col items-center justify-center h-screen w-screen">
                    <div id="logo" className="flex flex-row items-center justify-center gap-2 sm:gap-4 leading-normal tracking-normal mq480:flex-wrap mb-10 sm:mb-20">
                      <Image
                        className="relative"
                        loading="lazy"
                        alt={t('login.logoAlt')}
                        src="/tp-logotype.png"
                        height={logoHeight}
                        width={logoWidth}
                      />
                    </div>
                    {!!timeSavedLocal && timeSavedLocal > 0 && (
                      <div className="flex flex-row gap-2 items-center justify-center text-sm opacity-70 mb-14">
                        <div className="flex flex-row items-center justify-center gap-1 text-lg p-4 sm:p-0 sm:text-3xl font-extralight font-inter">
                          {t('main.timeSaved', { time: formatSavedDuration(timeSavedLocal) })}
                        </div>
                      </div>
                    )}
                    {isAuthInitialized && (
                      <Button
                        type="button"
                        style="solid"
                        color="accent"
                        background="inverse"
                        onClick={handleLogin}
                      >
                        {t('login.logIn')}
                      </Button>
                    )}
                    <div className="mt-8 px-6 text-center text-xs sm:text-sm text-white/70 max-w-[700px]">
                      {(() => {
                        const termsUrl = 'https://tranquility.ai/terms/enterprise';
                        const notice = t('main.accessTerms', { termsUrl });
                        const [before, after] = notice.split(termsUrl);
                        if (after === undefined) return notice;
                        return (
                          <>
                            {before}
                            <a
                              href={termsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-white underline hover:text-white/90"
                            >
                              {termsUrl}
                            </a>
                            {after}
                          </>
                        );
                      })()}
                    </div>
                  </main>
                )}
                {pleaseSelectTimelineModal}
                {answerTimelineTargetModal}
                <TimelineCreateModal
                  isOpen={isTimelineNameModalOpen}
                  defaultName={defaultTimelineName}
                  caseId={caseId ?? ''}
                  autoGenerateEnabled={isTimelineCreateAutoGenerateEnabled(featureFlags, caseId)}
                  isGenerating={generatingTimelineCaseIds.includes(caseId ?? '')}
                  onClose={() => {
                    setIsTimelineNameModalOpen(false);
                    setPendingTimelineCreation(null);
                    if (caseId && caseId.trim() !== '') {
                      fetchWorkProductsForCase(caseId).then((workProducts) => {
                        setMultipleTimelinesAvailable(workProducts);
                      });
                    }
                  }}
                  onCreateBlank={(name) => {
                    void handleCreateTimelineWithName(name);
                  }}
                  onGenerate={(scopes, title) => {
                    void handleGenerateTimeline(scopes, title);
                  }}
                />
                <MainAppModals
                  isThreatReportNameModalOpen={isThreatReportNameModalOpen}
                  threatReportCaseId={threatReportApiCaseId ?? getCaseInternalName(caseId) ?? caseId}
                  threatReportTitleCaseId={caseId}
                  threatReportSources={threatReportSources}
                  threatReportSourcesLoading={threatReportSourcesLoading}
                  threatReportSourcesError={threatReportSourcesError}
                  onRetryThreatReportSources={onCreateThreatReport}
                  threatReportFivecastServerConfigured={
                    !!(featureFlags?.threatReportConfig as any)?.fivecastServer
                  }
                  threatReportCreatorSelectionEnabled={threatReportCreatorsEnabled}
                  selectedFivecastSource={selectedFivecastSource}
                  setSelectedFivecastSource={setSelectedFivecastSource}
                  threatReportSourceModifiedAt={threatReportSourceModifiedAt}
                  selectedMagnetSource={selectedMagnetSource}
                  setSelectedMagnetSource={setSelectedMagnetSource}
                  onCreateThreatReportWithName={handleCreateThreatReportWithName}
                  onCancelThreatReportName={closeThreatReportNameModal}
                  showBannerModal={!!featureFlags?.bannerModal}
                  isBannerModalOpen={isBannerModalOpen}
                  setIsBannerModalOpen={setIsBannerModalOpen}
                  bannerModalAccepted={bannerModalAcceptedRef.current}
                  onAcceptBanner={() => {
                    setIsBannerModalOpen(false);
                    bannerModalAcceptedRef.current = true;
                  }}
                  onLogout={handleLogout}
                  tenantName={tenantName}
                  tenantId={tenantId}
                  orgName={orgName}
                />
                <TimelinesTutorialModal isOpen={isModalOpen} onOpenChange={setIsModalOpen} />
                {authState?.isAuthenticated &&
                  selectedView !== 'presentation' &&
                  selectedView !== 'courtroom' &&
                  !!featureFlags?.supportFallbackForm && (
                  <SupportFallbackModal
                    caseId={caseId ?? ''}
                    selectedView={selectedView ?? ''}
                    chatSessionId={chatSessionId ?? ''}
                    identity={userIdentity}
                    onResult={({ ok }) => {
                      setToast({
                        setOpen: setToastOpen,
                        open: true,
                        location: 'bottom-right',
                        time: Date.now(),
                        duration: 4000,
                        title: ok ? 'Feedback Sent' : 'Submission Failed',
                        description: ok
                          ? 'Thanks — our team will follow up.'
                          : 'We could not submit your feedback. Please try again.',
                        level: ok ? ToastLevel.SUCCESS : ToastLevel.ERROR,
                      });
                    }}
                  />
                )}
              </FullScreenContext.Provider>
              <TpToastHost toast={toast} setToastOpen={setToastOpen} />
              <CookieDisclaimer />
            </CsamCredentialsContext.Provider>
            </EmailArchivePreviewProvider>
            </FeaturesContext.Provider>
          </ToastContext.Provider>
          </ToastProvider>
        </div>
      </WalkthruContext.Provider>
      </TimePilotI18nProviders>
      </AuthContext.Provider>
    </StatsContext.Provider>
  );
};

export default Main;
